import { useState, useEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useAuth } from '@/contexts/AuthContext';
import { useLanguage } from '@/contexts/LanguageContext';
import { useTheme } from '@/contexts/ThemeContext';
import { api } from '@/services/api';
import { useAutoRefresh } from '@/hooks/useAutoRefresh';
import useImageCropper from '@/hooks/useImageCropper';
import { compressImage } from '@/utils/imageCompress';
import { formatDateAR, formatDateTimeAR, parseBackendDate } from '@/utils/dateFormat';
import HouseRoomsAvailability from '@/components/HouseRoomsAvailability';
import ClaimChatModal from '@/components/ClaimChatModal';
import DirectChatModal from '@/components/DirectChatModal';
import LoadingIndicator from '@/components/LoadingIndicator';
import { pushService } from '@/services/pushService';
import { PROGRESS_TEMPLATES, RESOLVE_TEMPLATES } from '@/utils/claimTemplates';
import { linkify } from '@/utils/linkify';
import SurveysSection from '@/components/SurveysSection';
import PopupsSection from '@/components/PopupsSection';
import BenefitQRModal from '@/components/BenefitQRModal';
import './AdminDashboard.css';
import { 
  Home, Bell, Users, Settings, LogOut, Plus, Search,
  Building2, Clock, Clock3, CheckCircle, CircleCheck, CircleX, XCircle, ChevronRight, ChevronLeft, 
  Wrench, Sparkles, Volume2, HelpCircle, Send,
  UserX, UserCheck, UserRoundCheck, UserRoundX, Gift, Images, Image as ImageIcon, RotateCcw,
  Trash2, Eye, MapPin, Calendar, FileText, IdCard, Edit2, BarChart3, KeyRound, Copy, Check, Sun, Moon, ChevronUp, ChevronDown, X, Mail, MailOpen,
  LayoutDashboard, ClipboardList, UserRoundPlus,
  ArrowLeftRight, CalendarSync, MessageCircle, Megaphone, House,
  DoorOpen, CalendarDays, UsersRound, Languages, MoreHorizontal,
      Info, CircleAlert, Siren, FileWarning, Dumbbell, Laptop, Flame, WashingMachine, BookOpen, Tv, Sofa, QrCode, Download, Menu, Lock, PanelLeftClose, PanelLeftOpen
} from 'lucide-react';
import {
  ResponsiveContainer, BarChart, Bar, XAxis, YAxis, Tooltip,
  LineChart, Line, PieChart, Pie, Cell, Legend, CartesianGrid,
} from 'recharts';

const claimTypeIcons = {
  repair: <Wrench className="w-4 h-4" />,
  cleaning: <Sparkles className="w-4 h-4" />,
  noise: <Volume2 className="w-4 h-4" />,
  other: <HelpCircle className="w-4 h-4" />
};

const claimTypeLabels = {
  repair: 'Reparación',
  cleaning: 'Limpieza',
  noise: 'Convivencia',
  other: 'Otro'
};

const statusLabels = {
  pending: 'Pendiente',
  in_progress: 'En progreso',
  resolved: 'Resuelto',
  closed: 'Cerrado'
};

const csvEscape = (value) => {
  const text = value == null ? '' : String(value);
  return /[",\r\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
};

const formatCsvDate = (value) => {
  if (!value) return '';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? String(value) : date.toLocaleString('es-AR');
};

const spaceTypeVisualMap = {
  gym: { icon: Dumbbell, semantic: 'amber' },
  coworking: { icon: Laptop, semantic: 'blue' },
  bbq: { icon: Flame, semantic: 'rose' },
  laundry: { icon: WashingMachine, semantic: 'cyan' },
  living: { icon: Sofa, semantic: 'violet' },
  study: { icon: BookOpen, semantic: 'indigo' },
  tv: { icon: Tv, semantic: 'blue' },
  other: { icon: House, semantic: 'green' },
};

const adminDashboardSemanticStyles = {
  mint: {
    light: { borderColor: 'rgba(21, 128, 61, 0.22)', backgroundColor: 'var(--pill-green-bg)', color: 'var(--pill-green-fg)' },
    dark: { borderColor: 'rgba(89, 210, 137, 0.25)', backgroundColor: 'rgba(52, 158, 94, 0.32)', color: '#75d49a' },
  },
  lavender: {
    light: { borderColor: 'rgba(109, 40, 217, 0.32)', backgroundColor: 'var(--pill-violet-bg)', color: 'var(--pill-violet-fg)' },
    dark: { borderColor: 'rgba(167, 139, 250, 0.42)', backgroundColor: 'rgba(109, 40, 217, 0.36)', color: '#C4B5FD' },
  },
  yellow: {
    light: { borderColor: 'rgba(161, 98, 7, 0.22)', backgroundColor: 'var(--pill-yellow-bg)', color: 'var(--pill-yellow-fg)' },
    dark: { borderColor: 'rgba(237, 190, 88, 0.26)', backgroundColor: 'rgba(186, 133, 31, 0.32)', color: '#edc86f' },
  },
  blue: {
    light: { borderColor: 'rgba(37, 99, 235, 0.3)', backgroundColor: 'var(--pill-blue-bg)', color: 'var(--pill-blue-fg)' },
    dark: { borderColor: 'rgba(104, 169, 241, 0.42)', backgroundColor: 'rgba(54, 116, 185, 0.32)', color: '#9CC7F7' },
  },
  teal: {
    light: { borderColor: 'rgba(13, 148, 136, 0.32)', backgroundColor: '#CCFBF1', color: '#0D9488' },
    dark: { borderColor: 'rgba(45, 212, 191, 0.46)', backgroundColor: 'rgba(13, 148, 136, 0.34)', color: '#5EEAD4' },
  },
  rose: {
    light: { borderColor: 'rgba(225, 29, 72, 0.22)', backgroundColor: 'var(--pill-rose-bg)', color: 'var(--pill-rose-fg)' },
    dark: { borderColor: 'rgba(225, 29, 72, 0.36)', backgroundColor: 'rgba(190, 24, 93, 0.24)', color: '#FDA4AF' },
  },
};

const getAdminDashboardSemanticStyle = (theme, semantic) => (
  adminDashboardSemanticStyles[semantic]?.[theme] || undefined
);

const getAdminDashboardSemanticClass = (semantic) => ({
  mint: 'icon-pill-green',
  lavender: 'icon-pill-violet',
  yellow: 'icon-pill-yellow',
  blue: 'icon-pill-blue',
  teal: 'icon-pill-green',
  rose: 'icon-pill-rose',
}[semantic] || 'icon-pill-green');

const getAdminDashboardMetricSemantic = (color) => ({
  'bg-accent-peach': 'mint',
  'bg-accent-lavender': 'lavender',
  'bg-accent-yellow': 'yellow',
  'bg-accent-mint': 'mint',
  'icon-pill-blue': 'blue',
}[color] || 'mint');

const getAdminDashboardQuickActionStyle = (theme) => (
  theme === 'light'
    ? { boxShadow: '0 4px 12px -10px rgba(24, 32, 43, 0.16), 0 1px 2px rgba(24, 32, 43, 0.025)' }
    : undefined
);

function AdminRestrictedSection({ language, onBack }) {
  const es = language === 'es';
  return (
    <div className="flex min-h-48 flex-col items-center justify-center px-4 text-center">
      <Lock className="mb-3 h-8 w-8 text-muted-foreground" aria-hidden="true" />
      <h2 className="text-lg font-semibold text-foreground">
        {es ? 'No tenés acceso a esta sección' : 'You do not have access to this section'}
      </h2>
      <p className="mt-1 text-sm text-muted-foreground">
        {es ? 'Esta sección está disponible solo para administradores.' : 'This section is available to administrators only.'}
      </p>
      <button type="button" onClick={onBack} className="mt-4 text-sm font-medium text-primary hover:underline">
        {es ? 'Volver al Dashboard' : 'Back to Dashboard'}
      </button>
    </div>
  );
}

export function AdminDashboard() {
  const { user: currentUser, logout } = useAuth();
  const user = currentUser;
  const { t, language, toggleLanguage } = useLanguage();
  const { theme, toggleTheme } = useTheme();
  const [activeSection, setActiveSection] = useState('dashboard');
  const [stats, setStats] = useState(null);
  const [claims, setClaims] = useState([]);
  const [announcements, setAnnouncements] = useState([]);
  const [houses, setHouses] = useState([]);
  const [users, setUsers] = useState([]);
  const [visits, setVisits] = useState([]);
  const [transfers, setTransfers] = useState([]);
  const [benefits, setBenefits] = useState([]);
  const [renewals, setRenewals] = useState([]);
  const [directThreads, setDirectThreads] = useState([]);
  
  
  const directThreadsUnread = directThreads.reduce((acc, t) => acc + (t.unread_admin || 0), 0);
  const [loading, setLoading] = useState(true);
  const [showNewAnnouncement, setShowNewAnnouncement] = useState(false);
  const [showNewHouse, setShowNewHouse] = useState(false);
  const [showNewBenefit, setShowNewBenefit] = useState(false);
  const [editingHouse, setEditingHouse] = useState(null);
  const [editingAnnouncement, setEditingAnnouncement] = useState(null);
  const [editingUser, setEditingUser] = useState(null);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  
  
  
  
  const popHandlerRef = useRef(null);
  useEffect(() => {
    window.history.pushState({ wannaAdmin: 'sentinel' }, '');
    const onPopState = () => {
      const handled = popHandlerRef.current && popHandlerRef.current();
      if (handled) window.history.pushState({ wannaAdmin: 'sentinel' }, '');
    };
    window.addEventListener('popstate', onPopState);
    return () => window.removeEventListener('popstate', onPopState);
  }, []);

  popHandlerRef.current = () => {
    if (editingUser) { setEditingUser(null); return true; }
    if (editingAnnouncement) { setEditingAnnouncement(null); return true; }
    if (editingHouse) { setEditingHouse(null); return true; }
    if (showNewBenefit) { setShowNewBenefit(false); return true; }
    if (showNewHouse) { setShowNewHouse(false); return true; }
    if (showNewAnnouncement) { setShowNewAnnouncement(false); return true; }
    if (activeSection !== 'dashboard') { setActiveSection('dashboard'); return true; }
    return false;
  };

  useEffect(() => {
    (async () => {
      if (!pushService.isSupported()) return;
      const sub = await pushService.getSubscription();
      setPushEnabled(!!sub);
      
      
      if (sub) {
        pushService.syncSubscription().catch(() => {});
      }
    })();
  }, []);

  const handleTogglePush = async () => {
    setPushBusy(true);
    try {
      if (pushEnabled) {
        await pushService.unsubscribe();
        setPushEnabled(false);
      } else {
        const perm = await pushService.requestPermission();
        if (perm !== 'granted') {
          alert(language === 'es' ? 'Permiso denegado.' : 'Permission denied.');
          return;
        }
        await pushService.subscribe();
        setPushEnabled(true);
      }
    } catch (e) {
      alert(e.message || 'Error');
    } finally {
      setPushBusy(false);
    }
  };

  const [editingBenefit, setEditingBenefit] = useState(null);
  const [qrBenefit, setQrBenefit] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [mobileSettingsOpen, setMobileSettingsOpen] = useState(false);
  const [accountPanelOpen, setAccountPanelOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [railAccountOpen, setRailAccountOpen] = useState(false);
  const [houseFilter, setHouseFilter] = useState('all'); 
  const railAccountRef = useRef(null);

  useEffect(() => {
    if (!mobileMenuOpen && !mobileSettingsOpen) return undefined;

    const restoreBodyOverflow = document.body.style.overflow;
    if (window.matchMedia?.('(max-width: 1024px)').matches) {
      document.body.style.overflow = 'hidden';
    }

    const handleEscape = (event) => {
      if (event.key === 'Escape') {
        setMobileMenuOpen(false);
        setMobileSettingsOpen(false);
      }
    };
    window.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = restoreBodyOverflow;
      window.removeEventListener('keydown', handleEscape);
    };
  }, [mobileMenuOpen, mobileSettingsOpen]);

  useEffect(() => {
    if (!railAccountOpen || !sidebarCollapsed) return undefined;

    const handleRailAccountOutsideClick = (event) => {
      if (!railAccountRef.current?.contains(event.target)) {
        setRailAccountOpen(false);
      }
    };
    const handleRailAccountEscape = (event) => {
      if (event.key === 'Escape') setRailAccountOpen(false);
    };

    document.addEventListener('mousedown', handleRailAccountOutsideClick);
    window.addEventListener('keydown', handleRailAccountEscape);
    return () => {
      document.removeEventListener('mousedown', handleRailAccountOutsideClick);
      window.removeEventListener('keydown', handleRailAccountEscape);
    };
  }, [railAccountOpen, sidebarCollapsed]);

  useEffect(() => {
    if (!sidebarCollapsed) setRailAccountOpen(false);
  }, [sidebarCollapsed]);

  
  const accessibleHouses = (() => {
    const ids = currentUser?.assigned_house_ids || [];
    if (!ids.length) return houses; 
    return houses.filter(h => ids.includes(h.id));
  })();
  const showHouseFilter = accessibleHouses.length > 1;
  const showMobileHouseFilter = accessibleHouses.length > 0;

  
  const filterByCurrentHouse = (items, key = 'house_id') => {
    if (houseFilter === 'all') return items;
    return items.filter(it => it?.[key] === houseFilter);
  };
  const filteredClaims = filterByCurrentHouse(claims);
  const filteredVisits = filterByCurrentHouse(visits);
  const filteredAnnouncements = houseFilter === 'all'
    ? announcements
    : announcements.filter(a => !a.target_houses?.length || a.target_houses.includes(houseFilter));
  const filteredBenefits = houseFilter === 'all'
    ? benefits
    : benefits.filter(b => !b.target_houses?.length || b.target_houses.includes(houseFilter));
  const filteredTransfers = houseFilter === 'all'
    ? transfers
    : transfers.filter(t => t.current_house_id === houseFilter || t.target_house_id === houseFilter);
  const filteredUsers = houseFilter === 'all'
    ? users
    : users.filter(u => u.role !== 'guest' || u.house_id === houseFilter);
  const filteredHousesList = houseFilter === 'all' ? houses : houses.filter(h => h.id === houseFilter);

  
  const computedStats = (() => {
    if (houseFilter === 'all' || !stats) return stats;
    const guests = filteredUsers.filter(u => u.role === 'guest' && u.is_active !== false);
    const totalClaims = filteredClaims.length;
    const pending = filteredClaims.filter(c => c.status === 'pending').length;
    const inProg = filteredClaims.filter(c => c.status === 'in_progress').length;
    const resolved = filteredClaims.filter(c => c.status === 'resolved').length;
    const pendingVisits = filteredVisits.filter(v => v.status === 'pending').length;
    const pendingTransfers = filteredTransfers.filter(t => t.status === 'pending').length;
    return {
      ...stats,
      total_users: guests.length,
      total_houses: filteredHousesList.length,
      total_claims: totalClaims,
      pending_claims: pending,
      in_progress_claims: inProg,
      resolved_claims: resolved,
      total_announcements: filteredAnnouncements.length,
      pending_visits: pendingVisits,
      pending_transfers: pendingTransfers,
      total_benefits: filteredBenefits.filter(b => b.is_active !== false).length,
    };
  })();

  useEffect(() => {
    loadData();
    
  }, []);

  
  useAutoRefresh(() => loadData({ silent: true }), 120000);

  const loadData = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [statsData, claimsData, announcementsData, housesData, usersData, visitsData, transfersData, benefitsData, renewalsData, threadsData] = await Promise.all([
        api.getStats(),
        api.getClaims(),
        api.getAnnouncements(),
        api.getHouses(),
        api.getUsers(),
        api.getVisits(),
        api.getTransfers(),
        api.getBenefits(),
        api.getContractRenewals().catch(() => []),
        api.listDirectThreads().catch(() => []),
      ]);
      setStats(statsData);
      setClaims(claimsData);
      setAnnouncements(announcementsData);
      setHouses(housesData);
      setUsers(usersData);
      setVisits(visitsData);
      setTransfers(transfersData);
      setBenefits(benefitsData);
      setRenewals(renewalsData);
      setDirectThreads(threadsData || []);
    } catch (error) {
      console.error('Error loading data:', error);
    }
    if (!silent) setLoading(false);
  };

  const navigate = async (section) => {
        setRailAccountOpen(false);
    setActiveSection(section);
    if (section === 'visits' || section === 'agenda') {
      try {
        const now = new Date().toISOString();
        await api.markSeen(section === 'visits' ? { visits: now } : { reservations: now });
        const statsData = await api.getStats();
        setStats(statsData);
      } catch (e) {
        console.error('Error marking section as seen:', e);
      }
    }
  };

  const handleUpdateClaim = async (claimId, updateData) => {
    try {
      const updated = await api.updateClaim(claimId, updateData);
      setClaims(claims.map(c => c.id === claimId ? updated : c));
      loadData(); 
    } catch (error) {
      console.error('Error updating claim:', error);
    }
  };

  const handleClaimRead = (claimId) => {
    setClaims(prev => (prev || []).map(c => (c.id === claimId ? { ...c, unread_count: 0 } : c)));
  };

  const handleCreateAnnouncement = async (data) => {
    try {
      const newAnnouncement = await api.createAnnouncement(data);
      setAnnouncements([newAnnouncement, ...announcements]);
      setShowNewAnnouncement(false);
      loadData();
    } catch (error) {
      console.error('Error creating announcement:', error);
    }
  };

  const handleCreateUser = async (userData) => {
    try {
      const newUser = await api.createUser(userData);
      setUsers([...users, newUser]);
      loadData();
    } catch (error) {
      console.error('Error creating user:', error);
      throw error;
    }
  };

  const handleUpdateUser = async (userId, updateData) => {
    try {
      const updated = await api.updateUser(userId, updateData);
      setUsers(users.map(u => u.id === userId ? updated : u));
      loadData();
    } catch (error) {
      console.error('Error updating user:', error);
    }
  };

  const handleCreateHouse = async (houseData) => {
    try {
      const newHouse = await api.createHouse(houseData);
      setHouses([...houses, newHouse]);
      setShowNewHouse(false);
      loadData();
    } catch (error) {
      console.error('Error creating house:', error);
      throw error;
    }
  };

  const handleRespondVisit = async (visitId, status, rejectionReason = null) => {
    try {
      await api.respondToVisit(visitId, { status, rejection_reason: rejectionReason });
      loadData();
    } catch (error) {
      console.error('Error responding to visit:', error);
    }
  };

  const handleUpdateVisit = async (visitId, updateData) => {
    try {
      const updated = await api.respondToVisit(visitId, updateData);
      setVisits(prev => prev.map(v => v.id === visitId ? { ...v, ...updated } : v));
    } catch (error) {
      console.error('Error updating visit:', error);
      alert(error.response?.data?.detail || 'Error al actualizar la visita');
    }
  };

  const handleRespondTransfer = async (transferId, status, rejectionReason = null) => {
    try {
      await api.respondToTransfer(transferId, { status, rejection_reason: rejectionReason });
      loadData();
    } catch (error) {
      console.error('Error responding to transfer:', error);
    }
  };

  const handleCreateBenefit = async (benefitData) => {
    try {
      await api.createBenefit(benefitData);
      setShowNewBenefit(false);
      loadData();
    } catch (error) {
      console.error('Error creating benefit:', error);
      throw error;
    }
  };

  const handleDeleteBenefit = async (benefitId) => {
    if (!window.confirm('¿Estás seguro de que querés eliminar este beneficio?')) return;
    try {
      await api.deleteBenefit(benefitId);
      loadData();
    } catch (error) {
      console.error('Error deleting benefit:', error);
    }
  };

  const handleReorderBenefits = async (benefitId, direction) => {
    
    const sorted = [...benefits].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
    const idx = sorted.findIndex(b => b.id === benefitId);
    if (idx < 0) return;
    const target = direction === 'up' ? idx - 1 : idx + 1;
    if (target < 0 || target >= sorted.length) return;
    const reordered = [...sorted];
    [reordered[idx], reordered[target]] = [reordered[target], reordered[idx]];
    
    const optimistic = reordered.map((b, i) => ({ ...b, display_order: i }));
    setBenefits(optimistic);
    try {
      await api.reorderBenefits(reordered.map(b => b.id));
    } catch (error) {
      console.error('Error reordering benefits:', error);
      loadData();  
    }
  };

  const handleUpdateBenefit = async (benefitId, data) => {
    try {
      await api.updateBenefit(benefitId, data);
      setEditingBenefit(null);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar beneficio');
    }
  };

  const handleUpdateHouse = async (houseId, data) => {
    try {
      await api.updateHouse(houseId, data);
      setEditingHouse(null);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar casa');
    }
  };

  const handleDeleteHouse = async (houseId) => {
    if (!window.confirm('¿Estás seguro de que querés eliminar esta casa?')) return;
    try {
      await api.deleteHouse(houseId);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar casa');
    }
  };

  const handleUpdateAnnouncement = async (id, data) => {
    try {
      await api.updateAnnouncement(id, data);
      setEditingAnnouncement(null);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al actualizar comunicado');
    }
  };

  const handleDeleteAnnouncement = async (id) => {
    if (!window.confirm('¿Estás seguro de que querés eliminar este comunicado?')) return;
    try {
      await api.deleteAnnouncement(id);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar comunicado');
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('¿Eliminar permanentemente este huésped? Preferí "Dar de baja" para mantener el historial.')) return;
    try {
      await api.deleteUser(id);
      loadData();
    } catch (error) {
      alert(error.response?.data?.detail || 'Error al eliminar usuario');
    }
  };

  const menuItems = [
    { id: 'dashboard', icon: LayoutDashboard, label: t('dashboard') },
    { id: 'analytics', icon: BarChart3, label: t('analytics') },
    { id: 'claims', icon: ClipboardList, label: t('claims'), badge: stats?.pending_claims },
    { id: 'visits', icon: UserRoundPlus, label: t('visits'), badge: stats?.new_visits },
    { id: 'transfers', icon: ArrowLeftRight, label: language === 'es' ? 'Cambios' : 'Transfers', badge: stats?.pending_transfers },
    { id: 'renewals', icon: CalendarSync, label: language === 'es' ? 'Renovaciones' : 'Renewals', badge: renewals.filter(r => r.status === 'pending').length || 0 },
    { id: 'messages', icon: MessageCircle, label: language === 'es' ? 'Mensajes' : 'Messages', badge: directThreadsUnread },
    { id: 'announcements', icon: Megaphone, label: t('announcements') },
    { id: 'benefits', icon: Gift, label: t('benefits') },
    { id: 'surveys', icon: FileText, label: language === 'es' ? 'Encuestas' : 'Surveys' },
    { id: 'popups', icon: Images, label: language === 'es' ? 'Pop-ups' : 'Pop-ups' },
    { id: 'houses', icon: Building2, label: t('houses') },
    { id: 'occupancy', icon: House, label: language === 'es' ? 'Ocupación' : 'Occupancy' },
    { id: 'spaces', icon: DoorOpen, label: language === 'es' ? 'Espacios' : 'Spaces' },
    { id: 'agenda', icon: CalendarDays, label: language === 'es' ? 'Agenda' : 'Agenda', badge: stats?.new_reservations },
    { id: 'users', icon: UsersRound, label: t('users') },
  ];
  const visibleMenuItems = user?.role === 'house_manager'
    ? menuItems.filter(item => item.id !== 'surveys' && item.id !== 'popups')
    : menuItems;

  const menuGroups = [
    {
      id: 'general',
      label: language === 'es' ? 'General' : 'General',
      itemIds: ['dashboard', 'analytics'],
    },
    {
      id: 'operation',
      label: language === 'es' ? 'Operación' : 'Operations',
      itemIds: ['claims', 'visits', 'transfers', 'renewals', 'messages'],
    },
    {
      id: 'community',
      label: language === 'es' ? 'Comunidad' : 'Community',
      itemIds: ['announcements', 'benefits', 'surveys', 'popups'],
    },
    {
      id: 'management',
      label: language === 'es' ? 'Gestión' : 'Management',
      itemIds: ['houses', 'occupancy', 'spaces', 'agenda', 'users'],
    },
  ].map(group => ({
    ...group,
    items: group.itemIds.map(id => visibleMenuItems.find(item => item.id === id)).filter(Boolean),
  }));

  const activeNavigationStyle = theme === 'dark'
    ? { background: '#ffffff', color: '#111111', boxShadow: 'none' }
    : { background: '#111111', color: '#ffffff', boxShadow: 'none' };

  return (
    <div className="admin-dashboard-shell h-screen bg-background flex overflow-hidden">
      {}
          <aside className={`admin-desktop-sidebar relative hidden lg:flex ${sidebarCollapsed ? 'w-[72px]' : 'w-64'} bg-surface border-r border-border flex-col flex-shrink-0 overflow-visible h-screen transition-[width] duration-200 ease-in-out`}>
        <div className={`relative flex-shrink-0 border-b border-border ${sidebarCollapsed ? 'flex flex-col items-center gap-2 p-2' : 'p-6'}`}>
          <div className={`flex items-center ${sidebarCollapsed ? 'justify-center' : 'gap-3'}`}>
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center overflow-hidden rounded-lg border-2 border-foreground bg-surface">
              <img src="/logo192.png" alt="WannaColive" className="w-full h-full object-cover brand-logo-adaptive" />
            </div>
            <div className={sidebarCollapsed ? 'hidden' : ''}>
              <h1 className="font-heading font-bold text-foreground">WannaColive</h1>
              <p className="text-xs text-muted-foreground">Panel Admin</p>
            </div>
          </div>
          <button
            type="button"
            onClick={() => setSidebarCollapsed((collapsed) => !collapsed)}
            className={`inline-flex items-center justify-center text-muted-foreground transition-colors hover:bg-muted hover:text-foreground ${sidebarCollapsed ? 'absolute right-[-12px] top-3 z-20 h-8 w-8 rounded-full border border-border bg-surface' : 'absolute right-3 top-1/2 h-9 w-9 -translate-y-1/2 rounded-lg'}`}
            aria-label={sidebarCollapsed
              ? (language === 'es' ? 'Expandir menú' : 'Expand menu')
              : (language === 'es' ? 'Contraer menú' : 'Collapse menu')}
            title={sidebarCollapsed
              ? (language === 'es' ? 'Expandir menú' : 'Expand menu')
              : (language === 'es' ? 'Contraer menú' : 'Collapse menu')}
            data-testid="admin-sidebar-collapse-toggle"
          >
            {sidebarCollapsed ? <PanelLeftOpen className="h-4 w-4" /> : <PanelLeftClose className="h-4 w-4" />}
          </button>
        </div>
        
        <nav className={`admin-sidebar-nav flex-1 overflow-y-auto py-4 ${sidebarCollapsed ? 'px-2' : 'px-3'}`}>
          <div className={sidebarCollapsed ? 'space-y-2' : 'space-y-5'}>
            {menuGroups.map((group) => (
              <section key={group.id} aria-labelledby={`admin-menu-${group.id}`}>
                <h2
                  id={`admin-menu-${group.id}`}
                  className={`mb-2 px-3 text-[10px] font-bold uppercase tracking-[0.16em] text-muted-foreground ${sidebarCollapsed ? 'sr-only' : ''}`}
                >
                  {group.label}
                </h2>
                <ul className="space-y-1">
                  {group.items.map((item) => (
                    <li key={item.id}>
                      <button
                        onClick={() => navigate(item.id)}
                        style={activeSection === item.id ? activeNavigationStyle : undefined}
                        className={`admin-sidebar-item relative flex h-10 w-full items-center rounded-xl text-sm font-medium transition-all ${sidebarCollapsed ? 'justify-center px-2' : 'gap-3.5 px-3'} ${
                          activeSection === item.id
                            ? 'admin-sidebar-item-active font-semibold'
                            : 'text-foreground hover:bg-muted'
                        }`}
                        title={sidebarCollapsed ? item.label : undefined}
                        data-testid={`admin-nav-${item.id}`}
                        aria-current={activeSection === item.id ? 'page' : undefined}
                      >
                        <item.icon
                          className="h-5 w-5 flex-shrink-0"
                          style={activeSection === item.id ? { stroke: theme === 'dark' ? '#111111' : '#ffffff' } : undefined}
                        />
                        <span className={`flex-1 text-left truncate ${sidebarCollapsed ? 'hidden' : ''}`}>{item.label}</span>
                        {item.badge > 0 && (
                          <span className={`inline-flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold leading-none text-white shadow-sm ${sidebarCollapsed ? 'absolute right-0.5 top-0.5' : ''}`}>
                            {item.badge}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </nav>

        <div ref={railAccountRef} className="relative flex-shrink-0 border-t border-border px-3 py-3">
          <button
            type="button"
            onClick={() => {
              if (sidebarCollapsed) {
                setRailAccountOpen((open) => !open);
              } else {
                setAccountPanelOpen((open) => !open);
              }
            }}
            className={`flex min-h-11 w-full items-center rounded-lg py-2 text-left transition-colors hover:bg-muted ${sidebarCollapsed ? 'justify-center px-0' : 'gap-3 px-2'}`}
            aria-expanded={sidebarCollapsed ? railAccountOpen : accountPanelOpen}
            aria-haspopup={sidebarCollapsed ? 'dialog' : undefined}
            aria-controls={sidebarCollapsed ? 'admin-rail-account-popover' : 'admin-account-panel'}
            title={sidebarCollapsed ? (language === 'es' ? 'Cuenta y configuración' : 'Account and settings') : undefined}
            data-testid="admin-account-trigger"
          >
            <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-accent-mint shadow-sm ring-1 ring-emerald-200/60">
              <span
                className="font-heading font-bold"
                style={{ color: theme === 'dark' ? '#ffffff' : '#166534' }}
              >
                {user?.name?.[0] || 'A'}
              </span>
            </div>
            <div className={`flex-1 min-w-0 ${sidebarCollapsed ? 'hidden' : ''}`}>
              <p className="truncate text-sm font-semibold text-foreground">{user?.name || 'Admin'}</p>
              <p className="text-xs text-muted-foreground">
                {user?.role === 'admin'
                  ? (language === 'es' ? 'Administrador' : 'Administrator')
                  : user?.role}
              </p>
            </div>
            <ChevronDown
              className={`h-4 w-4 flex-shrink-0 text-muted-foreground transition-transform ${sidebarCollapsed ? 'hidden' : ''} ${accountPanelOpen ? 'rotate-180' : ''}`}
              data-testid="admin-account-chevron"
              aria-hidden="true"
            />
          </button>

          {accountPanelOpen && !sidebarCollapsed && <div id="admin-account-panel" data-testid="admin-account-panel">
          {showHouseFilter && (
            <div className="admin-sidebar-house-filter mb-2 px-2 pb-2" data-testid="admin-house-filter-account">
              <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                {language === 'es' ? 'Casa actual' : 'Current house'}
              </label>
              <div className="relative">
                <select
                  value={houseFilter}
                  onChange={(e) => setHouseFilter(e.target.value)}
                  className="h-9 w-full appearance-none rounded-xl border border-border bg-surface py-1.5 pl-3 pr-8 text-[13px] text-foreground outline-none transition"
                  data-testid="admin-house-filter-select"
                >
                  <option value="all">{language === 'es' ? 'Todas las casas' : 'All houses'}</option>
                  {accessibleHouses.map(h => (
                    <option key={h.id} value={h.id}>{h.name}</option>
                  ))}
                  <option value="unassigned">{language === 'es' ? 'Sin asignar a una casa' : 'Unassigned'}</option>
                </select>
                <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
              </div>
            </div>
          )}

          <div className="space-y-0.5 border-t border-border pt-2">
            <button
              onClick={handleTogglePush}
              disabled={pushBusy}
              className="admin-sidebar-preference flex h-9 w-full items-center gap-3 rounded-lg px-2 text-sm text-foreground transition-colors hover:bg-muted disabled:opacity-60"
              data-testid="admin-push-toggle"
            >
              <Bell className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-left">
                {language === 'es' ? 'Notificaciones' : 'Notifications'}
              </span>
              {pushBusy ? (
                <span className="text-xs text-muted-foreground">…</span>
              ) : (
                <span className="admin-sidebar-toggle" data-on={pushEnabled} aria-hidden="true">
                  <span />
                </span>
              )}
            </button>

            {pushEnabled && (
              <button
                onClick={async () => {
                  try {
                    await pushService.syncSubscription();
                    await api.testPushNotification();
                    alert(language === 'es'
                      ? 'Se envió una notificación de prueba. Debería llegarte en unos segundos.'
                      : 'Test notification sent. It should arrive in a few seconds.');
                  } catch (err) {
                    alert(language === 'es'
                      ? 'No se pudo enviar la notificación de prueba. Chequeá que las notificaciones estén habilitadas en el navegador.'
                      : 'Could not send test notification. Check browser notification permissions.');
                  }
                }}
                className="ml-9 px-2 py-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                data-testid="admin-push-test"
              >
                {language === 'es' ? 'Enviar notificación de prueba' : 'Send test notification'}
              </button>
            )}

            <button
              onClick={toggleTheme}
              className="admin-sidebar-preference flex h-9 w-full items-center gap-3 rounded-lg px-2 text-sm text-foreground transition-colors hover:bg-muted"
              data-testid="admin-theme-toggle"
            >
              {theme === 'light' ? <Moon className="w-4 h-4 flex-shrink-0" /> : <Sun className="w-4 h-4 flex-shrink-0" />}
              <span className="flex-1 text-left">
                {theme === 'light'
                  ? (language === 'es' ? 'Modo oscuro' : 'Dark mode')
                  : (language === 'es' ? 'Modo claro' : 'Light mode')}
              </span>
            </button>

            <button
              onClick={toggleLanguage}
              className="admin-sidebar-preference flex h-9 w-full items-center gap-3 rounded-lg px-2 text-sm text-foreground transition-colors hover:bg-muted"
              data-testid="admin-lang-toggle"
            >
              <Languages className="w-4 h-4 flex-shrink-0" />
              <span className="flex-1 text-left">{language === 'es' ? 'Idioma' : 'Language'}</span>
              <span className="text-xs font-semibold text-muted-foreground">
                {language === 'es' ? 'EN' : 'ES'}
              </span>
            </button>
          </div>

          <button
            onClick={logout}
            className="mt-2 pt-3 pb-1 px-2 border-t border-border w-full flex items-center gap-3 text-sm font-medium text-destructive hover:opacity-75 transition-opacity"
            data-testid="admin-logout-button"
          >
            <LogOut className="w-4 h-4" />
            <span>{t('logout')}</span>
          </button>
          </div>}

          {railAccountOpen && sidebarCollapsed && (
            <div
              id="admin-rail-account-popover"
              data-testid="admin-rail-account-popover"
              role="dialog"
              aria-label={language === 'es' ? 'Cuenta y configuración' : 'Account and settings'}
              className="absolute bottom-3 left-full z-30 ml-2 w-72 max-w-[calc(100vw-96px)] rounded-xl border border-border bg-surface p-3 text-foreground shadow-md"
            >
              <div className="mb-3 flex items-center gap-3 border-b border-border pb-3">
                <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-accent-mint shadow-sm ring-1 ring-emerald-200/60">
                  <span className="font-heading font-bold" style={{ color: theme === 'dark' ? '#ffffff' : '#166534' }}>
                    {user?.name?.[0] || 'A'}
                  </span>
                </div>
                <div className="min-w-0">
                  <p className="truncate text-sm font-semibold">{user?.name || 'Admin WannaColive'}</p>
                  <p className="text-xs text-muted-foreground">
                    {user?.role === 'admin'
                      ? (language === 'es' ? 'Administrador' : 'Administrator')
                      : user?.role}
                  </p>
                </div>
              </div>

              {showHouseFilter && (
                <div className="mb-3 border-b border-border pb-3" data-testid="admin-rail-house-filter">
                  <label className="mb-1 block text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    {language === 'es' ? 'Casa actual' : 'Current house'}
                  </label>
                  <div className="relative">
                    <select
                      value={houseFilter}
                      onChange={(e) => setHouseFilter(e.target.value)}
                      className="h-9 w-full appearance-none rounded-lg border border-border bg-surface py-1.5 pl-3 pr-8 text-[13px] text-foreground outline-none transition"
                      data-testid="admin-rail-house-filter-select"
                    >
                      <option value="all">{language === 'es' ? 'Todas las casas' : 'All houses'}</option>
                      {accessibleHouses.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                      <option value="unassigned">{language === 'es' ? 'Sin asignar a una casa' : 'Unassigned'}</option>
                    </select>
                    <ChevronDown className="pointer-events-none absolute right-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                  </div>
                </div>
              )}

              <div className="space-y-0.5">
                <button
                  onClick={handleTogglePush}
                  disabled={pushBusy}
                  className="flex h-9 w-full items-center gap-3 rounded-lg px-2 text-sm transition-colors hover:bg-muted disabled:opacity-60"
                >
                  <Bell className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1 text-left">{language === 'es' ? 'Notificaciones' : 'Notifications'}</span>
                  {pushBusy ? <span className="text-xs text-muted-foreground">…</span> : <span className="admin-sidebar-toggle" data-on={pushEnabled} aria-hidden="true"><span /></span>}
                </button>
                <button
                  onClick={toggleTheme}
                  className="flex h-9 w-full items-center gap-3 rounded-lg px-2 text-sm transition-colors hover:bg-muted"
                >
                  {theme === 'light' ? <Moon className="h-4 w-4 flex-shrink-0" /> : <Sun className="h-4 w-4 flex-shrink-0" />}
                  <span className="flex-1 text-left">
                    {theme === 'light'
                      ? (language === 'es' ? 'Modo oscuro' : 'Dark mode')
                      : (language === 'es' ? 'Modo claro' : 'Light mode')}
                  </span>
                </button>
                <button
                  onClick={toggleLanguage}
                  className="flex h-9 w-full items-center gap-3 rounded-lg px-2 text-sm transition-colors hover:bg-muted"
                >
                  <Languages className="h-4 w-4 flex-shrink-0" />
                  <span className="flex-1 text-left">{language === 'es' ? 'Idioma' : 'Language'}</span>
                  <span className="text-xs font-semibold text-muted-foreground">{language === 'es' ? 'EN' : 'ES'}</span>
                </button>
              </div>

              <button
                onClick={logout}
                className="mt-2 flex h-9 w-full items-center gap-3 border-t border-border px-2 pt-2 text-sm font-medium text-destructive transition-opacity hover:opacity-75"
              >
                <LogOut className="h-4 w-4" />
                <span>{t('logout')}</span>
              </button>
            </div>
          )}
        </div>
      </aside>

      {}
      <div className="admin-mobile-shell lg:hidden fixed top-0 left-0 right-0 bg-surface border-b border-border z-50" style={{ paddingTop: 'env(safe-area-inset-top)' }}>
        <div className="admin-mobile-topbar flex items-center justify-between p-4">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg overflow-hidden border-2 border-foreground bg-surface flex items-center justify-center">
              <img src="/logo192.png" alt="WannaColive" className="w-full h-full object-cover brand-logo-adaptive" />
            </div>
            <div className="min-w-0">
              <p className="font-heading font-semibold leading-tight">WannaColive</p>
              <p className="text-xs leading-tight text-muted-foreground">Panel Admin</p>
            </div>
          </div>
          <div className="admin-mobile-header-actions flex items-center gap-2 relative">
            <div className="admin-mobile-inline-controls">
              <button
                type="button"
                onClick={() => { setMobileSettingsOpen(false); setMobileMenuOpen(v => !v); }}
                className="admin-mobile-inline-menu"
                aria-label={language === 'es' ? 'Abrir menú' : 'Open menu'}
                aria-expanded={mobileMenuOpen}
                data-testid="admin-mobile-inline-menu"
              >
                <Menu className="h-4 w-4" />
                <span className="admin-mobile-inline-menu-label">{language === 'es' ? 'Menú' : 'Menu'}</span>
              </button>
            </div>
            <button
              onClick={toggleTheme}
              className="admin-mobile-theme-button admin-mobile-icon-button p-2 border-2 border-foreground rounded-lg text-foreground"
              data-testid="admin-mobile-theme-toggle"
              aria-label="Toggle theme"
            >
              {theme === 'light' ? <Moon className="w-5 h-5" /> : <Sun className="w-5 h-5" />}
            </button>
            <button
              onClick={() => { setMobileMenuOpen(false); setMobileSettingsOpen(v => !v); }}
              className="admin-mobile-icon-button p-2 border-2 border-foreground rounded-lg relative"
              data-testid="admin-mobile-settings-toggle"
              aria-label={language === 'es' ? 'Configuración' : 'Settings'}
              aria-expanded={mobileSettingsOpen}
            >
              <Settings className="w-5 h-5" />
            </button>
            {mobileMenuOpen && (
              <>
                {}
                <button
                  type="button"
                  className="fixed inset-0 z-40 bg-transparent"
                  onClick={() => setMobileMenuOpen(false)}
                  aria-label="Cerrar menú"
                  data-testid="admin-mobile-menu-overlay"
                />
                <div
                  className="absolute right-4 top-[calc(100%+4px)] z-50 w-56 bg-surface border-2 border-foreground rounded-xl shadow-lg overflow-hidden"
                  data-testid="admin-mobile-menu"
                >
                  <div className="admin-mobile-menu-header">
                    <h2 className="font-heading font-semibold text-lg">{language === 'es' ? 'Menú' : 'Menu'}</h2>
                    <button
                      type="button"
                      onClick={() => setMobileMenuOpen(false)}
                      className="admin-mobile-menu-close"
                      aria-label={language === 'es' ? 'Cerrar menú' : 'Close menu'}
                      data-testid="admin-mobile-menu-close"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <nav className="admin-mobile-menu-nav" aria-label={language === 'es' ? 'Navegación Admin' : 'Admin navigation'}>
                    {menuGroups.map((group) => (
                      <section key={group.id} className="admin-mobile-menu-group" aria-labelledby={`admin-mobile-menu-${group.id}`}>
                        <h3 id={`admin-mobile-menu-${group.id}`}>{group.label}</h3>
                        <div className="admin-mobile-menu-items">
                          {group.items.map((item) => {
                            const isActive = activeSection === item.id;
                            return (
                              <button
                                key={item.id}
                                type="button"
                                onClick={() => { setMobileMenuOpen(false); navigate(item.id); }}
                                className={`admin-mobile-menu-item${isActive ? ' is-active' : ''}`}
                                aria-current={isActive ? 'page' : undefined}
                                data-testid={`admin-mobile-nav-${item.id}`}
                              >
                                <item.icon className="h-5 w-5" />
                                <span>{item.label}</span>
                                {item.badge > 0 && <span className="admin-mobile-menu-badge">{item.badge}</span>}
                              </button>
                            );
                          })}
                        </div>
                      </section>
                    ))}
                  </nav>
                </div>
              </>
            )}
            {mobileSettingsOpen && (
              <>
                <button
                  type="button"
                  className="admin-mobile-settings-overlay fixed inset-0 z-40 bg-transparent"
                  onClick={() => setMobileSettingsOpen(false)}
                  aria-label={language === 'es' ? 'Cerrar ajustes' : 'Close settings'}
                  data-testid="admin-mobile-settings-overlay"
                />
                <div className="admin-mobile-settings" data-testid="admin-mobile-settings">
                  <div className="admin-mobile-menu-header">
                    <h2 className="font-heading font-semibold text-lg">{language === 'es' ? 'Ajustes' : 'Settings'}</h2>
                    <button
                      type="button"
                      onClick={() => setMobileSettingsOpen(false)}
                      className="admin-mobile-menu-close"
                      aria-label={language === 'es' ? 'Cerrar ajustes' : 'Close settings'}
                      data-testid="admin-mobile-settings-close"
                    >
                      <X className="h-5 w-5" />
                    </button>
                  </div>
                  <div className="admin-mobile-settings-body">
                    <p className="text-xs text-muted-foreground">{language === 'es' ? 'Sesión iniciada como' : 'Signed in as'}</p>
                    <p className="text-sm font-semibold text-foreground truncate">{user?.name || user?.email}</p>
                    {showMobileHouseFilter && (
                      <div className="admin-mobile-settings-house">
                        <label htmlFor="admin-house-filter-select-settings" className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                          {language === 'es' ? 'Casa' : 'House'}
                        </label>
                        <div className="relative">
                          <select
                            id="admin-house-filter-select-settings"
                            value={houseFilter}
                            onChange={(e) => setHouseFilter(e.target.value)}
                            className="admin-mobile-settings-house-select h-11 w-full appearance-none rounded-xl border border-border bg-surface px-3 pr-9 text-sm text-foreground outline-none transition"
                            data-testid="admin-house-filter-select-settings"
                          >
                            <option value="all">{language === 'es' ? 'Todas las casas' : 'All houses'}</option>
                            {accessibleHouses.map(h => (
                              <option key={h.id} value={h.id}>{h.name}</option>
                            ))}
                          </select>
                          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        </div>
                      </div>
                    )}
                    <button
                      type="button"
                      onClick={toggleTheme}
                      className="admin-mobile-settings-action"
                      data-testid="admin-mobile-theme-setting"
                    >
                      <span className="flex items-center gap-2">
                        {theme === 'light' ? <Moon className="h-4 w-4 text-primary" /> : <Sun className="h-4 w-4 text-primary" />}
                        {theme === 'light'
                          ? (language === 'es' ? 'Modo oscuro' : 'Dark mode')
                          : (language === 'es' ? 'Modo claro' : 'Light mode')}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={handleTogglePush}
                      disabled={pushBusy}
                      className="admin-mobile-settings-action"
                      data-testid="admin-mobile-push-toggle"
                    >
                      <span className="flex items-center gap-2">
                        <Bell className="h-4 w-4 text-primary" />
                        {pushEnabled ? 'Notificaciones activas' : 'Activar notificaciones'}
                      </span>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] ${pushEnabled ? 'bg-accent-mint text-foreground' : 'bg-muted text-muted-foreground'}`}>
                        {pushBusy ? '…' : pushEnabled ? 'ON' : 'OFF'}
                      </span>
                    </button>
                    <button
                      type="button"
                      onClick={() => { setMobileSettingsOpen(false); logout(); }}
                      className="admin-mobile-settings-action admin-mobile-settings-logout"
                      data-testid="admin-mobile-logout"
                    >
                      <LogOut className="h-4 w-4" />
                      <span className="font-medium">{t('logout') || (language === 'es' ? 'Cerrar sesión' : 'Logout')}</span>
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
        
        {}
        <div className="admin-mobile-nav-strip flex overflow-x-auto border-t-2 border-foreground">
          {visibleMenuItems.map((item) => (
            <button
              key={item.id}
              onClick={() => navigate(item.id)}
              style={activeSection === item.id ? activeNavigationStyle : undefined}
              className={`flex-shrink-0 flex items-center gap-2 px-4 py-3 ${
                activeSection === item.id ? 'bg-accent-yellow font-semibold' : ''
              }`}
            >
              <item.icon
                className="w-4 h-4"
                style={activeSection === item.id ? { stroke: theme === 'dark' ? '#111111' : '#ffffff' } : undefined}
              />
              <span className="text-sm">{item.label}</span>
            </button>
          ))}
        </div>

        <div className="admin-mobile-controls px-4 py-2 border-t border-border bg-muted/30">
          <button
            type="button"
            onClick={() => { setMobileSettingsOpen(false); setMobileMenuOpen(v => !v); }}
            className="admin-mobile-controls-menu"
            aria-label={language === 'es' ? 'Abrir menú' : 'Open menu'}
            aria-expanded={mobileMenuOpen}
            data-testid="admin-mobile-controls-menu"
          >
            <Menu className="h-4 w-4" />
            <span>{language === 'es' ? 'Menú' : 'Menu'}</span>
          </button>
          {showHouseFilter && (
          <div className="admin-mobile-house-filter" data-testid="admin-house-filter-mobile">
            <div className="relative inline-block max-w-full">
              <select
                value={houseFilter}
                onChange={(e) => setHouseFilter(e.target.value)}
                className="h-10 min-w-[190px] max-w-full appearance-none rounded-xl border border-border bg-surface py-2 pl-3 pr-9 text-sm text-foreground outline-none transition"
                data-testid="admin-house-filter-select-mobile"
              >
                <option value="all">{language === 'es' ? 'Todas las casas' : 'All houses'}</option>
                {accessibleHouses.map(h => (
                  <option key={h.id} value={h.id}>{h.name}</option>
                ))}
              </select>
              <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            </div>
          </div>
          )}
        </div>
      </div>

      {}
      <main
        className={`flex-1 lg:p-8 p-4 lg:pt-8 overflow-y-auto ${showHouseFilter ? 'admin-main-mobile-pt-extra' : 'admin-main-mobile-pt'}`}
        data-admin-main="true"
      >
        <div className="max-w-6xl mx-auto">
          {activeSection === 'dashboard' && (
            <DashboardSection stats={computedStats} claims={filteredClaims} users={filteredUsers} houses={filteredHousesList} loading={loading} onNavigate={setActiveSection} />
          )}
          {activeSection === 'analytics' && (
            <AnalyticsSection loading={loading} />
          )}
          {activeSection === 'claims' && (
            <ClaimsSection claims={filteredClaims} allClaims={claims} houses={houses} onUpdateClaim={handleUpdateClaim} onClaimRead={handleClaimRead} loading={loading} currentUserId={user?.id} />
          )}
          {activeSection === 'visits' && (
            <VisitsSection visits={filteredVisits} houses={houses} loading={loading} onUpdateVisit={handleUpdateVisit} />
          )}
          {activeSection === 'transfers' && (
            <TransfersSection transfers={filteredTransfers} houses={houses} onRespond={handleRespondTransfer} loading={loading} />
          )}
          {activeSection === 'renewals' && (
            <RenewalsSection
              renewals={renewals}
              houses={houses}
              users={users}
              onRespond={async (id, payload) => {
                try {
                  const updated = await api.updateContractRenewal(id, payload);
                  setRenewals(renewals.map(r => r.id === id ? updated : r));
                  loadData({ silent: true });
                } catch (e) {
                  alert(e?.response?.data?.detail || 'Error');
                }
              }}
              loading={loading}
            />
          )}
          {activeSection === 'messages' && (
            <MessagesSection
              threads={directThreads}
              users={users}
              houses={accessibleHouses}
              currentUserId={user?.id}
              onRefresh={() => loadData({ silent: true })}
            />
          )}
          {activeSection === 'announcements' && (
            <AnnouncementsSection 
              announcements={filteredAnnouncements} 
              houses={accessibleHouses}
              onCreateNew={() => setShowNewAnnouncement(true)} 
              onEdit={setEditingAnnouncement}
              onDelete={handleDeleteAnnouncement}
              loading={loading} 
            />
          )}
          {activeSection === 'benefits' && (
            <BenefitsSection 
              benefits={filteredBenefits} 
              houses={accessibleHouses}
              onCreateNew={() => setShowNewBenefit(true)}
              onEdit={setEditingBenefit}
              onDelete={handleDeleteBenefit}
              onReorder={handleReorderBenefits}
              onShowQR={setQrBenefit}
              loading={loading} 
            />
          )}
          {activeSection === 'surveys' && user?.role === 'house_manager' && (
            <AdminRestrictedSection language={language} onBack={() => setActiveSection('dashboard')} />
          )}
          {activeSection === 'surveys' && user?.role !== 'house_manager' && (
            <SurveysSection emptyStateIcon={FileText} />
          )}
          {activeSection === 'popups' && user?.role === 'house_manager' && (
            <AdminRestrictedSection language={language} onBack={() => setActiveSection('dashboard')} />
          )}
          {activeSection === 'popups' && user?.role !== 'house_manager' && (
            <PopupsSection emptyStateIcon={Images} />
          )}
          {activeSection === 'houses' && (
            <HousesSection 
              houses={filteredHousesList} 
              loading={loading} 
              onCreateHouse={() => setShowNewHouse(true)}
              onEdit={setEditingHouse}
              onDelete={handleDeleteHouse}
              isSuperAdmin={!user?.assigned_house_ids?.length && user?.role === 'admin'}
            />
          )}
          {activeSection === 'spaces' && (
            <SpacesSection houses={accessibleHouses} emptyStateIcon={DoorOpen} />
          )}
          {activeSection === 'occupancy' && (
            <OccupancySection houses={accessibleHouses} />
          )}
          {activeSection === 'agenda' && (
            <AgendaSection houses={accessibleHouses} />
          )}
          {activeSection === 'users' && (
            <UsersSection 
              users={filteredUsers} 
              houses={accessibleHouses} 
              loading={loading} 
              onCreateUser={handleCreateUser}
              onUpdateUser={handleUpdateUser}
              onEdit={setEditingUser}
              onDelete={handleDeleteUser}
              currentUser={user}
            />
          )}
        </div>
      </main>

      {showNewAnnouncement && (
        <NewAnnouncementModal 
          onClose={() => setShowNewAnnouncement(false)} 
          onSubmit={handleCreateAnnouncement}
          houses={houses}
          users={users}
          allowedHouses={user?.role === 'house_manager' ? houses.filter(h => (user?.assigned_house_ids || []).includes(h.id)) : null}
        />
      )}

      {showNewHouse && (
        <NewHouseModal 
          onClose={() => setShowNewHouse(false)} 
          onSubmit={handleCreateHouse}
        />
      )}

      {showNewBenefit && (
        <NewBenefitModal
          onClose={() => setShowNewBenefit(false)}
          onSubmit={handleCreateBenefit}
          houses={houses}
        />
      )}

      {editingHouse && (
        <EditHouseModal
          house={editingHouse}
          onClose={() => setEditingHouse(null)}
          onSubmit={(data) => handleUpdateHouse(editingHouse.id, data)}
        />
      )}

      {editingAnnouncement && (
        <EditAnnouncementModal
          announcement={editingAnnouncement}
          onClose={() => setEditingAnnouncement(null)}
          onSubmit={(data) => handleUpdateAnnouncement(editingAnnouncement.id, data)}
          houses={houses}
        />
      )}

      {editingUser && (
        <EditUserModal
          userData={editingUser}
          houses={houses}
          onClose={() => setEditingUser(null)}
          onSubmit={(data) => {
            handleUpdateUser(editingUser.id, data);
            setEditingUser(null);
          }}
        />
      )}

      {editingBenefit && (
        <EditBenefitModal
          benefit={editingBenefit}
          houses={houses}
          onClose={() => setEditingBenefit(null)}
          onSubmit={(data) => handleUpdateBenefit(editingBenefit.id, data)}
        />
      )}

      {qrBenefit && (
        <BenefitQRModal
          benefit={qrBenefit}
          language={language}
          onClose={() => setQrBenefit(null)}
        />
      )}
    </div>
  );
}

function DashboardSection({ stats, claims, users = [], houses = [], loading, onNavigate }) {
  const { t, language } = useLanguage();
  const { theme } = useTheme();
  const [openHouses, setOpenHouses] = useState({});
  const [extended, setExtended] = useState(null);

  useEffect(() => {
    let mounted = true;
    api.getStatsExtended()
      .then(d => { if (mounted) setExtended(d); })
      .catch(() => {});
    return () => { mounted = false; };
  }, []);
  
  useAutoRefresh(() => api.getStatsExtended().then(setExtended).catch(() => {}), 120000);

  if (loading) {
    return <LoadingIndicator className="py-8" />;
  }

  const recentClaims = claims.slice(0, 5);
  const go = (section) => onNavigate && onNavigate(section);
  const guests = users.filter(u => u.role === 'guest' && u.is_active !== false);
  const guestsByHouse = houses.map(h => ({
    house: h,
    guests: guests.filter(u => u.house_id === h.id).sort((a, b) => {
      const ra = (a.room_number || '').toString();
      const rb = (b.room_number || '').toString();
      return ra.localeCompare(rb, undefined, { numeric: true });
    }),
  }));
  const unassigned = guests.filter(u => !u.house_id);
  const toggleHouse = (id) => setOpenHouses(prev => ({ ...prev, [id]: !prev[id] }));

  const fmtAgo = (iso) => {
    if (!iso) return '';
    const parsed = parseBackendDate(iso);
    if (!parsed) return '';
    const diffMs = Date.now() - parsed.getTime();
    const m = Math.floor(diffMs / 60000);
    if (m < 1) return language === 'es' ? 'recién' : 'just now';
    if (m < 60) return language === 'es' ? `hace ${m} min` : `${m}m ago`;
    const h = Math.floor(m / 60);
    if (h < 24) return language === 'es' ? `hace ${h} h` : `${h}h ago`;
    const d = Math.floor(h / 24);
    return language === 'es' ? `hace ${d} d` : `${d}d ago`;
  };

  const series = extended?.series_7d || [];
  const w = (extended?.weekly) || {};

  const quickActions = [
    { key: 'announcement', label: language === 'es' ? 'Nuevo comunicado' : 'New announcement', icon: Megaphone, color: 'bg-accent-yellow', go: () => go('announcements') },
    { key: 'benefit', label: language === 'es' ? 'Nuevo beneficio' : 'New benefit', icon: Gift, color: 'bg-accent-mint', go: () => go('benefits') },
    { key: 'claims', label: language === 'es' ? 'Ver reclamos' : 'View claims', icon: ClipboardList, color: 'bg-accent-peach', go: () => go('claims') },
    { key: 'visits', label: language === 'es' ? 'Visitas' : 'Visits', icon: UserRoundPlus, color: 'bg-accent-lavender', go: () => go('visits') },
    { key: 'transfers', label: language === 'es' ? 'Cambios' : 'Transfers', icon: ArrowLeftRight, color: 'bg-accent-yellow', go: () => go('transfers') },
    { key: 'analytics', label: language === 'es' ? 'Analítica' : 'Analytics', icon: BarChart3, color: 'bg-accent-mint', go: () => go('analytics') },
  ];

  const quickActionSemantics = {
    announcement: 'blue',
    benefit: 'teal',
    claims: 'mint',
    visits: 'lavender',
    transfers: 'yellow',
    analytics: 'rose',
  };

  const weeklyChartSeries = [
    { key: 'claims', label: language === 'es' ? 'Reclamos' : 'Claims', color: '#16A34A', borderColor: 'rgba(21, 128, 61, 0.42)' },
    { key: 'announcements', label: language === 'es' ? 'Comunicados' : 'Announcements', color: '#2563EB', borderColor: 'rgba(37, 99, 235, 0.42)' },
    { key: 'visits', label: language === 'es' ? 'Visitas' : 'Visits', color: '#7C3AED', borderColor: 'rgba(109, 40, 217, 0.42)' },
    { key: 'transfers', label: language === 'es' ? 'Cambios' : 'Transfers', color: '#D97706', borderColor: 'rgba(161, 98, 7, 0.42)' },
  ];
  const weeklyChartMax = Math.max(1, ...series.flatMap((day) => weeklyChartSeries.map(({ key }) => day[key] || 0)));

  return (
    <div className="space-y-7 animate-enter" data-testid="admin-dashboard-section">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold tracking-tight text-2xl">{t('dashboard')}</h1>
        <span className="text-xs font-medium capitalize text-muted-foreground">{new Date().toLocaleDateString(language === 'es' ? 'es-AR' : 'en-US', { weekday: 'long', day: 'numeric', month: 'long' })}</span>
      </div>

      {}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <DeltaStatCard label={language === 'es' ? 'Reclamos (7d)' : 'Claims (7d)'} icon={ClipboardList} color="bg-accent-peach" data={w.claims} onClick={() => go('claims')} />
        <DeltaStatCard label={language === 'es' ? 'Visitas (7d)' : 'Visits (7d)'} icon={UserRoundPlus} color="bg-accent-lavender" data={w.visits} onClick={() => go('visits')} />
        <DeltaStatCard label={language === 'es' ? 'Cambios (7d)' : 'Transfers (7d)'} icon={ArrowLeftRight} color="bg-accent-yellow" data={w.transfers} onClick={() => go('transfers')} />
        <DeltaStatCard label={language === 'es' ? 'Comunicados (7d)' : 'Announcements (7d)'} icon={Megaphone} color="icon-pill-blue" data={w.announcements} onClick={() => go('announcements')} />
      </div>

      {}
      <div className="grid grid-cols-3 gap-4">
        <button onClick={() => go('claims')} className="admin-dashboard-kpi p-5 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30">
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">{language === 'es' ? 'Pendientes' : 'Pending'}</p>
          <p className="text-4xl font-heading font-bold text-accent-peach mt-1">{stats?.pending_claims || 0}</p>
        </button>
        <button onClick={() => go('claims')} className="admin-dashboard-kpi p-5 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30">
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">{language === 'es' ? 'En progreso' : 'In progress'}</p>
          <p className="text-4xl font-heading font-bold text-accent-yellow mt-1">{stats?.in_progress_claims || 0}</p>
        </button>
        <button onClick={() => go('claims')} className="admin-dashboard-kpi p-5 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30">
          <p className="text-xs text-muted-foreground uppercase tracking-wide font-semibold">{language === 'es' ? 'Resueltos' : 'Resolved'}</p>
          <p className="text-4xl font-heading font-bold text-accent-mint mt-1">{stats?.resolved_claims || 0}</p>
        </button>
      </div>

      {}
      <div className="grid lg:grid-cols-3 gap-4">
        {}
        <div className="admin-dashboard-panel p-5 lg:col-span-2">
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading font-semibold text-lg">{language === 'es' ? 'Actividad de la semana' : 'Weekly activity'}</h2>
            <span className="text-xs text-muted-foreground">{language === 'es' ? 'Últimos 7 días' : 'Last 7 days'}</span>
          </div>
          {series.length === 0 ? (
            <LoadingIndicator label={language === 'es' ? 'Cargando...' : 'Loading...'} className="py-8" />
          ) : (
            <div>
              <div className="relative flex h-48 flex-col pt-2">
                <div className="relative min-h-0 flex-1">
                  <div className="pointer-events-none absolute inset-0 flex flex-col justify-between" aria-hidden="true">
                    {[0, 1, 2, 3].map((line) => (
                      <div
                        key={line}
                        className="border-t"
                        style={{ borderColor: theme === 'dark' ? 'rgba(148, 163, 184, 0.12)' : 'rgba(148, 163, 184, 0.20)' }}
                      />
                    ))}
                  </div>
                  <div className="relative z-10 grid h-full grid-cols-7 gap-1 sm:gap-2">
                    {series.map((day, index) => (
                      <div key={index} className="flex min-w-0 flex-col items-center justify-end">
                        <div className="flex h-40 w-full items-end justify-center gap-0.5 sm:gap-1">
                          {weeklyChartSeries.map(({ key, color, borderColor, label }) => {
                          const value = day[key] || 0;
                          const height = value > 0 ? Math.max(8, (value / weeklyChartMax) * 100) : 0;
                          return (
                            <div key={key} className="relative h-full min-w-0 flex-1" title={`${label}: ${value}`}>
                              {value > 0 && (
                                <>
                                  <span
                                    className="absolute left-1/2 -translate-x-1/2 -translate-y-full text-[9px] font-semibold text-foreground"
                                    style={{ bottom: `${height}%` }}
                                  >
                                    {value}
                                  </span>
                                  <div
                                    className="absolute inset-x-0 bottom-0 rounded-t-sm"
                                    style={{ height: `${height}%`, backgroundColor: color, border: `1px solid ${borderColor}` }}
                                  />
                                </>
                              )}
                            </div>
                          );
                          })}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
                <div className="h-px w-full shrink-0" style={{ backgroundColor: theme === 'dark' ? 'rgba(148, 163, 184, 0.24)' : 'rgba(148, 163, 184, 0.34)' }} aria-hidden="true" />
                <div className="grid grid-cols-7 gap-1 pt-2 sm:gap-2">
                  {series.map((day, index) => <div key={index} className="min-w-0 text-center text-[10px] font-medium text-muted-foreground">{day.label}</div>)}
                </div>
              </div>
              <div className="mt-4 flex flex-wrap gap-x-4 gap-y-2 text-xs text-muted-foreground">
                {weeklyChartSeries.map(({ key, label, color, borderColor }) => {
                  return (
                    <span key={key} className="flex items-center gap-1.5">
                      <span className="h-2.5 w-2.5 rounded-sm" style={{ backgroundColor: color, border: `1px solid ${borderColor}` }} />
                      {label}
                    </span>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {}
        <div className="admin-dashboard-panel p-5" data-testid="admin-quick-actions">
          <h2 className="font-heading font-semibold text-lg mb-4">{language === 'es' ? 'Accesos rápidos' : 'Quick actions'}</h2>
          <div className="grid grid-cols-2 gap-3">
            {quickActions.map((qa) => (
              <button key={qa.key} style={getAdminDashboardQuickActionStyle(theme)} onClick={qa.go} className="admin-dashboard-quick-action flex flex-col items-start gap-2 p-3 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30" data-testid={`qa-${qa.key}`}>
                <div
                  className={`flex h-9 w-9 items-center justify-center rounded-xl ${getAdminDashboardSemanticClass(quickActionSemantics[qa.key])} border border-border`}
                  style={getAdminDashboardSemanticStyle(theme, quickActionSemantics[qa.key])}
                >
                  <qa.icon className="h-4 w-4 text-current" />
                </div>
                <p className="text-xs font-semibold leading-tight">{qa.label}</p>
              </button>
            ))}
          </div>
        </div>
      </div>

      {}
      <div className="admin-dashboard-panel p-6" data-testid="admin-dashboard-recent-section">
        <div className="flex items-center justify-between mb-4">
          <h2 className="font-heading font-semibold text-lg">{language === 'es' ? 'Reclamos recientes' : 'Recent claims'}</h2>
          <button onClick={() => go('claims')} className="text-sm text-primary hover:underline font-medium">{language === 'es' ? 'Ver todos →' : 'View all →'}</button>
        </div>
        {recentClaims.length === 0 ? (
          <p className="text-muted-foreground text-center py-4">{language === 'es' ? 'No hay reclamos' : 'No claims'}</p>
        ) : (
          <div className="space-y-3">
            {recentClaims.map((claim) => {
              const houseName = houses.find(h => h.id === claim.house_id)?.name;
              const claimIconSemantic = {
                repair: 'rose',
                cleaning: 'mint',
                noise: 'lavender',
                other: 'teal',
              }[claim.claim_type] || 'blue';
              const recentClaimStatusSemantic = {
                pending: 'yellow',
                in_progress: 'blue',
                resolved: 'mint',
                closed: 'mint',
                rejected: 'rose',
                cancelled: 'rose',
              }[claim.status] || 'blue';
              const claimIconStyle = getAdminDashboardSemanticStyle(theme, claimIconSemantic) || {};
              const claimStatusStyle = getAdminDashboardSemanticStyle(theme, recentClaimStatusSemantic) || {};
              return (
                <div key={claim.id} className="admin-dashboard-recent-item admin-dashboard-recent-row flex items-center justify-between p-3">
                  <div className="admin-dashboard-recent-main flex items-center gap-3 min-w-0 flex-1">
                    <span
                      className="flex-shrink-0 rounded-xl border p-2"
                      style={{ ...claimIconStyle, borderColor: claimIconStyle.borderColor, borderWidth: '1px' }}
                    >
                      {claimTypeIcons[claim.claim_type]}
                    </span>
                    <div className="admin-dashboard-recent-copy min-w-0 flex-1">
                      <p className="admin-dashboard-recent-title font-medium truncate">{claim.title}</p>
                      <p className="admin-dashboard-recent-meta text-xs text-muted-foreground truncate">
                        {claim.user_name}
                        {claim.room_number && <span> · Hab. {claim.room_number}</span>}
                        {houseName && <span> · {houseName}</span>}
                        <span> · {fmtAgo(claim.created_at)}</span>
                      </p>
                    </div>
                  </div>
                  <span
                    className="admin-dashboard-recent-status text-xs px-2 py-1 rounded-full font-medium flex-shrink-0"
                    style={{ ...claimStatusStyle, border: `1px solid ${claimStatusStyle.borderColor}`, boxShadow: 'none' }}
                  >
                    {statusLabels[claim.status]}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {}
      <a
        href="https://wa.me/5492213192805"
        target="_blank"
        rel="noopener noreferrer"
        className="admin-dashboard-panel flex items-center gap-4 p-5 transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
        data-testid="admin-help-center"
      >
        <div
          className="flex h-12 w-12 flex-shrink-0 items-center justify-center rounded-xl border border-border icon-pill-green"
          style={getAdminDashboardSemanticStyle(theme, 'mint')}
        >
          <svg className="h-6 w-6 text-current" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
            <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.67-1.612-.916-2.207-.242-.579-.487-.5-.67-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.626.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 0 1-5.031-1.378l-.361-.214-3.741.982 1-3.648-.235-.374a9.86 9.86 0 0 1-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.45-4.437 9.884-9.887 9.884m8.413-18.297A11.815 11.815 0 0 0 12.05 0C5.495 0 .16 5.334.157 11.89c0 2.096.547 4.142 1.588 5.945L.057 24l6.304-1.654a11.875 11.875 0 0 0 5.684 1.448h.005c6.554 0 11.89-5.334 11.893-11.89a11.821 11.821 0 0 0-3.479-8.416" />
          </svg>
        </div>
        <div className="flex-1">
          <p className="font-heading font-semibold">{language === 'es' ? '¿Necesitás ayuda?' : 'Need help?'}</p>
          <p className="text-xs text-muted-foreground">{language === 'es' ? 'Centro de ayuda — Hablanos por WhatsApp' : 'Help center — WhatsApp us'}</p>
        </div>
        <ChevronRight className="w-5 h-5 text-muted-foreground" />
      </a>

      {}
      {houses.length > 0 && (
        <div className="space-y-3" data-testid="dashboard-guests-by-house">
          <div className="flex items-center justify-between">
            <h2 className="font-heading font-semibold text-lg flex items-center gap-2">
              <Building2 className="w-5 h-5" />
              {language === 'es' ? 'Huéspedes por casa' : 'Guests by house'}
            </h2>
            <button onClick={() => go('users')} className="rounded-lg px-2 py-1 text-sm font-medium text-primary transition-colors hover:bg-muted hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30">
              {language === 'es' ? 'Ver todos →' : 'View all →'}
            </button>
          </div>
          <div className="grid md:grid-cols-2 gap-4">
            {guestsByHouse.map(({ house, guests: hGuests }) => {
              const isOpen = openHouses[house.id];
              return (
                <div key={house.id} className="admin-dashboard-panel overflow-hidden" data-testid={`house-card-${house.id}`}>
                  <button
                    onClick={() => toggleHouse(house.id)}
                    className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                    data-testid={`house-toggle-${house.id}`}
                  >
                    <div className="flex items-center gap-3 text-left">
                      <div
                        className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border"
                        style={theme === 'dark'
                          ? { backgroundColor: '#ffffff', color: '#111111', borderColor: '#ffffff' }
                          : { backgroundColor: '#111111', color: '#ffffff', borderColor: '#111111' }}
                      >
                        <img
                          src="/logo192.png"
                          alt="WannaColive"
                          className="h-6 w-6 object-contain brand-logo-adaptive"
                          style={{ filter: theme === 'dark' ? 'invert(1)' : 'none' }}
                        />
                      </div>
                      <div>
                        <p className="font-heading font-semibold">{house.name}</p>
                        <p className="text-xs text-muted-foreground">{house.city || house.address}</p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <span className="px-3 py-1 bg-primary text-background rounded-full text-sm font-bold">
                        {hGuests.length}
                      </span>
                      <ChevronRight className={`w-5 h-5 transition-transform ${isOpen ? 'rotate-90' : ''}`} />
                    </div>
                  </button>
                  {isOpen && (
                    <div className="border-t border-border bg-muted/20">
                      {hGuests.length === 0 ? (
                        <p className="p-4 text-sm text-muted-foreground text-center">Sin huéspedes asignados</p>
                      ) : (
                        <ul className="divide-y divide-border">
                          {hGuests.map((g) => (
                            <li key={g.id} className="p-3 flex items-center justify-between gap-3" data-testid={`house-${house.id}-guest-${g.id}`}>
                              <div className="flex items-center gap-3 min-w-0">
                                <div className="w-8 h-8 bg-accent-mint rounded-full flex items-center justify-center flex-shrink-0">
                                  <span className="font-heading font-bold text-background text-sm" style={{ color: theme === 'dark' ? '#ffffff' : '#166534' }}>{g.name?.[0]}</span>
                                </div>
                                <div className="min-w-0">
                                  <p className="font-medium truncate">{g.name}</p>
                                  <p className="text-xs text-muted-foreground truncate">{g.email}</p>
                                </div>
                              </div>
                              {g.room_number && (
                                <span className="text-xs px-2 py-1 bg-surface border border-border rounded-full font-medium flex-shrink-0">
                                  Hab. {g.room_number}
                                </span>
                              )}
                            </li>
                          ))}
                        </ul>
                      )}
                    </div>
                  )}
                </div>
              );
            })}
            {unassigned.length > 0 && (
              <div className="admin-dashboard-panel overflow-hidden border-dashed" data-testid="house-card-unassigned">
                <button
                  onClick={() => toggleHouse('__unassigned')}
                  className="w-full p-4 flex items-center justify-between hover:bg-muted/50 transition-colors"
                >
                  <div className="flex items-center gap-3 text-left">
                    <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl border border-border bg-muted">
                      <Users className="h-5 w-5 text-muted-foreground" />
                    </div>
                    <div>
                      <p className="font-heading font-semibold">Sin asignar</p>
                      <p className="text-xs text-muted-foreground">Huéspedes sin casa</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="px-3 py-1 bg-accent-peach text-background rounded-full text-sm font-bold">
                      {unassigned.length}
                    </span>
                    <ChevronRight className={`w-5 h-5 transition-transform ${openHouses.__unassigned ? 'rotate-90' : ''}`} />
                  </div>
                </button>
                {openHouses.__unassigned && (
                  <div className="border-t border-border bg-muted/20">
                    <ul className="divide-y divide-border">
                      {unassigned.map((g) => (
                        <li key={g.id} className="p-3 flex items-center gap-3">
                          <div className="w-8 h-8 bg-muted rounded-full flex items-center justify-center flex-shrink-0">
                            <span className="font-heading font-bold text-foreground text-sm" style={{ color: theme === 'dark' ? '#ffffff' : '#111111' }}>{g.name?.[0]}</span>
                          </div>
                          <div className="min-w-0">
                            <p className="font-medium truncate">{g.name}</p>
                            <p className="text-xs text-muted-foreground truncate">{g.email}</p>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function DeltaStatCard({ label, icon: Icon, color = 'bg-accent-mint', data, onClick }) {
  const { theme } = useTheme();
  const semantic = getAdminDashboardMetricSemantic(color);
  const v = data?.current ?? 0;
  const d = data?.delta_pct;
  const dStr = d == null ? '—' : `${d > 0 ? '↗' : d < 0 ? '↘' : '→'} ${Math.abs(d)}%`;
  const dColor = d == null ? 'text-muted-foreground' : d > 0 ? 'text-accent-mint' : d < 0 ? 'text-destructive' : 'text-muted-foreground';
  return (
    <button
      onClick={onClick}
      className="admin-dashboard-kpi p-4 text-left transition-colors hover:bg-surface-hover focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary/30"
    >
      <div className="flex items-start justify-between mb-2">
        <div
          className={`flex h-9 w-9 items-center justify-center rounded-xl ${getAdminDashboardSemanticClass(semantic)} border border-border`}
          style={getAdminDashboardSemanticStyle(theme, semantic)}
        >
          <Icon className="h-4 w-4 text-current" />
        </div>
        <span className={`text-[11px] font-bold ${dColor}`}>{dStr}</span>
      </div>
      <p className="text-3xl font-heading font-bold text-foreground">{v}</p>
      <p className="text-xs text-muted-foreground mt-1">{label}</p>
    </button>
  );
}

function StatCard({ label, value, icon: Icon, color, onClick, testId, surfaceClassName = 'admin-surface-card' }) {
  const interactive = typeof onClick === 'function';
  const Tag = interactive ? 'button' : 'div';
  const interactiveClassName = surfaceClassName === 'admin-occupancy-card'
    ? 'cursor-pointer hover:bg-surface-hover transition-colors w-full'
    : 'cursor-pointer hover:shadow-brutal-sm hover:-translate-y-0.5 transition-all w-full';
  return (
    <Tag
      type={interactive ? 'button' : undefined}
      onClick={onClick}
      data-testid={testId}
      className={`${surfaceClassName} p-4 text-left ${interactive ? interactiveClassName : ''}`}
    >
      <div className={`w-10 h-10 ${color} border-2 border-foreground rounded-lg flex items-center justify-center mb-3`}>
        <Icon className="w-5 h-5" />
      </div>
      <p className="text-2xl font-heading font-bold">{value}</p>
      <p className="text-sm text-muted-foreground">{label}</p>
    </Tag>
  );
}

function ClaimsSection({ claims, allClaims = claims, houses = [], onUpdateClaim, onClaimRead, loading, currentUserId }) {
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [closedComplaintsExpanded, setClosedComplaintsExpanded] = useState(false);
  const [exportMenuOpen, setExportMenuOpen] = useState(false);
  const exportButtonRef = useRef(null);
  const [exportMenuPosition, setExportMenuPosition] = useState(null);
  const { theme } = useTheme();

  const houseById = Object.fromEntries(houses.map(h => [h.id, h]));

  const filteredClaims = claims.filter(c => {
    if (filter !== 'all' && c.status !== filter) return false;
    if (!search.trim()) return true;
    const q = search.trim().toLowerCase();
    const houseName = (houseById[c.house_id]?.name || '').toLowerCase();
    return (
      (c.user_name || '').toLowerCase().includes(q) ||
      (c.title || '').toLowerCase().includes(q) ||
      (c.description || '').toLowerCase().includes(q) ||
      (c.room_number || '').toString().toLowerCase().includes(q) ||
      (c.location || '').toLowerCase().includes(q) ||
      houseName.includes(q)
    );
  });

  const closedClaims = filteredClaims.filter(claim => claim.status === 'closed');
  const visibleClaims = filter === 'closed'
    ? filteredClaims
    : filteredClaims.filter(claim => claim.status !== 'closed');

  const exportClaimsCsv = (scope) => {
    const claimsToExport = scope === 'filtered' ? filteredClaims : allClaims;
    if (!claimsToExport.length) return;

    const headers = [
      'ID', 'Fecha', 'Usuario', 'Casa', 'Habitación', 'Categoría', 'Título',
      'Descripción', 'Ubicación', 'Estado', 'Responsable',
      'Respuesta administrativa', 'Imágenes'
    ];
    const rows = claimsToExport.map((claim) => {
      const houseName = houseById[claim.house_id]?.name || '';
      const images = (claim.photo_urls?.length ? claim.photo_urls : (claim.photo_url ? [claim.photo_url] : []))
        .filter(Boolean)
        .join(' | ');
      return [
        claim.id,
        formatCsvDate(claim.created_at),
        claim.user_name,
        houseName,
        claim.room_number,
        claimTypeLabels[claim.claim_type] || claim.claim_type,
        claim.title,
        claim.description,
        claim.location,
        statusLabels[claim.status] || claim.status,
        claim.assigned_to,
        claim.admin_response,
        images,
      ];
    });

    const csv = `\uFEFF${[headers, ...rows].map(row => row.map(csvEscape).join(',')).join('\r\n')}`;
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    const date = new Date().toISOString().slice(0, 10);
    link.href = url;
    link.download = `reclamos-${scope === 'filtered' ? 'filtrados-' : ''}wannacolive-${date}.csv`;
    document.body.appendChild(link);
    link.click();
    link.remove();
    URL.revokeObjectURL(url);
    setExportMenuOpen(false);
  };

  useEffect(() => {
    if (!exportMenuOpen || !exportButtonRef.current) {
      setExportMenuPosition(null);
      return undefined;
    }

    const updateExportMenuPosition = () => {
      const rect = exportButtonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const menuWidth = 208;
      const viewportPadding = 8;
      const left = Math.min(
        Math.max(viewportPadding, rect.right - menuWidth),
        Math.max(viewportPadding, window.innerWidth - menuWidth - viewportPadding),
      );
      setExportMenuPosition({ top: rect.bottom + 8, left });
    };

    updateExportMenuPosition();
    window.addEventListener('resize', updateExportMenuPosition);
    window.addEventListener('scroll', updateExportMenuPosition, true);
    return () => {
      window.removeEventListener('resize', updateExportMenuPosition);
      window.removeEventListener('scroll', updateExportMenuPosition, true);
    };
  }, [exportMenuOpen]);

  return (
    <div className="space-y-6 animate-enter" data-testid="admin-claims-section">
      <div className="flex flex-col gap-3 xl:flex-row xl:items-center xl:justify-between">
        <h1 className="font-heading font-bold text-2xl">Gestión de Reclamos</h1>
        <div className="flex w-full flex-wrap items-center gap-2 md:flex-nowrap md:overflow-x-auto xl:w-auto xl:justify-end">
          {['all', 'pending', 'in_progress', 'resolved', 'closed'].map((status) => (
            (() => {
              const filterSemantic = {
                all: 'blue',
                pending: 'yellow',
                in_progress: 'blue',
                resolved: 'mint',
                closed: 'lavender',
              }[status];
              const selected = filter === status;
              const semanticStyle = getAdminDashboardSemanticStyle(theme, filterSemantic);
              const neutralSelectedStyle = theme === 'dark'
                ? { backgroundColor: '#ffffff', color: '#111111', borderColor: '#ffffff', boxShadow: 'none' }
                : { backgroundColor: '#111111', color: '#ffffff', borderColor: '#111111', boxShadow: 'none' };
              const selectedStyle = status === 'all' ? neutralSelectedStyle : semanticStyle;
              const unselectedStyle = theme === 'dark'
                ? { borderColor: 'rgba(148, 163, 184, 0.38)', backgroundColor: 'rgba(255, 255, 255, 0.04)', color: '#cbd5e1' }
                : { borderColor: 'rgba(148, 163, 184, 0.35)', backgroundColor: '#ffffff', color: '#334155' };

              return (
                <button
                  key={status}
                  onClick={() => setFilter(status)}
                  style={selected ? selectedStyle : unselectedStyle}
                  className="h-11 shrink-0 rounded-xl border px-3 text-sm transition-colors hover:bg-muted"
                  data-testid={`filter-${status}`}
                >
                  {status === 'all' ? 'Todos' : statusLabels[status]}
                </button>
              );
            })()
          ))}
          <div className="relative">
            <button
              type="button"
              onClick={() => setExportMenuOpen(open => !open)}
              className="inline-flex h-11 shrink-0 items-center justify-center gap-2 rounded-xl border border-border bg-surface px-3 text-sm font-medium text-foreground transition-colors hover:bg-muted"
              aria-haspopup="menu"
              aria-expanded={exportMenuOpen}
              data-testid="claims-export-button"
              ref={exportButtonRef}
            >
              <Download className="w-4 h-4" />
              Exportar CSV
              <ChevronDown className={`w-4 h-4 transition-transform ${exportMenuOpen ? 'rotate-180' : ''}`} />
            </button>
            {exportMenuOpen && exportMenuPosition && createPortal(
              <div
                role="menu"
                className="fixed z-[100] w-52 max-w-[calc(100vw-1rem)] overflow-hidden rounded-xl border border-border bg-surface p-1 shadow-lg"
                style={{ top: exportMenuPosition.top, left: exportMenuPosition.left }}
              >
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => exportClaimsCsv('filtered')}
                  disabled={!filteredClaims.length}
                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="claims-export-filtered"
                >
                  Resultados filtrados
                </button>
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => exportClaimsCsv('all')}
                  disabled={!allClaims.length}
                  className="flex w-full items-center rounded-lg px-3 py-2 text-left text-sm text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"
                  data-testid="claims-export-all"
                >
                  Todos los reclamos
                </button>
              </div>,
              document.body,
            )}
          </div>
        </div>
      </div>

      {/* Search Input */}
      <div className="relative">
        <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Buscar reclamo por usuario, título, casa, habitación o contenido..."
          className="h-11 w-full rounded-xl border border-border bg-surface pl-11 pr-10 text-sm text-foreground outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100/70 dark:focus:ring-amber-900/20"
          data-testid="claims-search-input"
        />
        {search && (
          <button
            type="button"
            onClick={() => setSearch('')}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Limpiar búsqueda"
            data-testid="claims-search-clear"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>

      {/* Search and Filter metadata */}
      {(search || filter !== 'all') && (
        <div className="flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
          <span>
            Mostrando <strong className="text-foreground">{filteredClaims.length}</strong> de <strong className="text-foreground">{claims.length}</strong> reclamos.
          </span>
          {search && (
            <button
              onClick={() => setSearch('')}
              className="px-2 py-1 rounded-full border border-foreground bg-accent-mint text-foreground font-medium"
            >
              Búsqueda: &ldquo;{search}&rdquo; ✕
            </button>
          )}
          {filter !== 'all' && (
            <button
              onClick={() => setFilter('all')}
              className="px-2 py-1 rounded-full border border-foreground bg-accent-yellow text-foreground font-medium"
            >
              Estado: {statusLabels[filter]} ✕
            </button>
          )}
        </div>
      )}

      {loading ? (
        <LoadingIndicator className="py-8" />
      ) : filteredClaims.length === 0 ? (
        <div
          className="admin-content-surface p-8 text-center"
          style={theme === 'dark'
            ? { border: '1px solid rgba(255, 255, 255, 0.12)', boxShadow: 'none', transform: 'none' }
            : undefined}
        >
          <ClipboardList className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No se encontraron reclamos</p>
        </div>
      ) : (
        <div className="space-y-4">
          {visibleClaims.map((claim) => (
            <AdminClaimCard key={claim.id} claim={claim} house={houseById[claim.house_id]} onUpdate={onUpdateClaim} onClaimRead={onClaimRead} currentUserId={currentUserId} />
          ))}
          {filter !== 'closed' && closedClaims.length > 0 && (
            <section className="overflow-hidden rounded-xl border border-border bg-surface">
              <button
                type="button"
                onClick={() => setClosedComplaintsExpanded(expanded => !expanded)}
                className="flex w-full items-center justify-between gap-3 px-4 py-3 text-left text-sm font-medium text-foreground transition-colors hover:bg-muted"
                aria-expanded={closedComplaintsExpanded}
                aria-controls="admin-closed-claims-list"
                data-testid="closed-claims-toggle"
              >
                <span className="flex min-w-0 items-center gap-2">
                  <ChevronRight className={`h-4 w-4 shrink-0 transition-transform ${closedComplaintsExpanded ? 'rotate-90' : ''}`} aria-hidden="true" />
                  <span>Reclamos cerrados</span>
                </span>
                <span className="shrink-0 rounded-full border border-border px-2 py-0.5 text-xs text-muted-foreground">{closedClaims.length}</span>
              </button>
              <div id="admin-closed-claims-list" hidden={!closedComplaintsExpanded} className="space-y-4 border-t border-border p-4">
                {closedClaims.map((claim) => (
                  <AdminClaimCard key={claim.id} claim={claim} house={houseById[claim.house_id]} onUpdate={onUpdateClaim} onClaimRead={onClaimRead} currentUserId={currentUserId} />
                ))}
              </div>
            </section>
          )}
        </div>
      )}
    </div>
  );
}

function ClaimAutoResponseModal({ targetStatus, claim, onClose, onConfirm }) {
  const templates = targetStatus === 'in_progress' ? PROGRESS_TEMPLATES : RESOLVE_TEMPLATES;
  const [selectedKey, setSelectedKey] = useState('none');
  const [customText, setCustomText] = useState('');
  const { theme } = useTheme();
  const darkMode = theme === 'dark';
  const modalOverlayStyle = darkMode
    ? { backgroundColor: 'rgba(0, 0, 0, 0.68)', backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)' }
    : { backgroundColor: 'rgba(0, 0, 0, 0.20)', backdropFilter: 'blur(5px)', WebkitBackdropFilter: 'blur(5px)' };
  const modalSurfaceStyle = darkMode
    ? { backgroundColor: 'var(--surface)', border: '1px solid var(--border)', boxShadow: '0 18px 50px rgba(0, 0, 0, 0.28)' }
    : { backgroundColor: 'var(--surface)', border: '1px solid var(--border)', boxShadow: '0 18px 50px rgba(24, 32, 43, 0.16)' };
  const cancelButtonStyle = darkMode
    ? { backgroundColor: 'var(--bg)', color: 'var(--fg)', border: '1px solid var(--border)', boxShadow: 'none', transform: 'none' }
    : { backgroundColor: 'var(--surface)', color: 'var(--fg)', border: '1px solid rgba(148, 163, 184, 0.45)', boxShadow: 'none', transform: 'none', fontWeight: 600 };
  const confirmButtonStyle = darkMode
    ? { backgroundColor: '#ffffff', color: '#111111', border: '1px solid rgba(255, 255, 255, 0.45)', boxShadow: 'none', transform: 'none' }
    : { backgroundColor: '#111111', color: '#ffffff', border: '1px solid rgba(0, 0, 0, 0.14)', boxShadow: 'none', transform: 'none' };

  const handleSelectTemplate = (key) => {
    setSelectedKey(key);
    const tmpl = templates.find(t => t.key === key);
    setCustomText(tmpl ? tmpl.text : '');
  };

  const handleConfirm = () => {
    onConfirm({
      status: targetStatus,
      auto_response: customText.trim() || undefined
    });
  };

  const statusLabel = targetStatus === 'in_progress' ? 'En Progreso' : 'Resuelto';

  return createPortal(
    <div style={modalOverlayStyle} className="fixed inset-0 z-50 modal-backdrop flex items-center justify-center p-4" onClick={(e) => { if (e.target === e.currentTarget) onClose(); }} data-testid="claim-auto-response-modal">
      <div style={modalSurfaceStyle} className="bg-surface border-2 border-foreground rounded-2xl w-full max-w-lg modal-card p-6 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between pb-3 border-b border-border mb-4">
          <div>
            <h3 className="font-heading font-bold text-lg flex items-center gap-2">
              <span>⚡</span> Respuesta Automática
            </h3>
            <p className="text-xs text-muted-foreground mt-0.5">Cambiar estado del reclamo a: <strong className="text-foreground">{statusLabel}</strong></p>
          </div>
          <button onClick={onClose} className="p-1 text-foreground hover:bg-muted rounded"><X className="w-5 h-5" /></button>
        </div>

        <div className="space-y-4 overflow-y-auto flex-1 pr-1">
          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
              Elegí una plantilla de respuesta
            </label>
            <select
              value={selectedKey}
              onChange={(e) => handleSelectTemplate(e.target.value)}
              className="input-brutal w-full px-3 py-2 text-sm"
              data-testid="claim-template-select-modal"
            >
              {templates.map((t) => (
                <option key={t.key} value={t.key}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-1">
              Mensaje a enviar (al chat del reclamo y notificación push)
            </label>
            <textarea
              rows={6}
              value={customText}
              onChange={(e) => setCustomText(e.target.value)}
              placeholder="Sin mensaje automático. Podés editar este texto o dejarlo vacío."
              className="input-brutal w-full p-3 text-xs leading-relaxed"
              data-testid="claim-template-text-preview"
            />
            {customText ? (
              <p className="text-[10px] text-accent-mint font-semibold mt-1">✓ Se enviará este mensaje al chat del reclamo y como notificación push al huésped.</p>
            ) : (
              <p className="text-[10px] text-muted-foreground mt-1">Solo se actualizará el estado del reclamo sin enviar mensaje por chat.</p>
            )}
          </div>
        </div>

        <div className="flex gap-3 pt-4 border-t border-border mt-4">
          <button
            type="button"
            onClick={onClose}
            style={cancelButtonStyle}
            className="flex-1 py-2.5 border-2 border-foreground rounded-xl font-medium hover:bg-muted text-sm"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={handleConfirm}
            style={confirmButtonStyle}
            className="btn-brutal flex-1 py-2.5 text-sm"
            data-testid="claim-template-confirm-btn"
          >
            Confirmar y Cambiar Estado
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AdminClaimCard({ claim, house, onUpdate, onClaimRead, currentUserId }) {
  const [expanded, setExpanded] = useState(false);
  const [chatOpen, setChatOpen] = useState(false);
  const [unread, setUnread] = useState(Number(claim.unread_count || 0));
  const [lightboxImg, setLightboxImg] = useState(null);
  const [pendingStatusTarget, setPendingStatusTarget] = useState(null);
  const { theme } = useTheme();

  const claimIconSemantic = {
    repair: 'rose',
    cleaning: 'mint',
    noise: 'lavender',
    other: 'teal',
  }[claim.claim_type] || 'blue';
  const claimIconStyle = claim.claim_type === 'other'
    ? (theme === 'dark'
      ? { ...getAdminDashboardSemanticStyle(theme, claimIconSemantic), borderWidth: '1px' }
      : { color: '#5EEAD4', border: 'none' })
    : (theme === 'dark'
      ? { ...getAdminDashboardSemanticStyle(theme, claimIconSemantic), borderWidth: '1px' }
      : undefined);
  const claimExpandedStyle = {
    borderTopWidth: '1px',
    borderTopColor: theme === 'dark' ? 'var(--border)' : 'rgba(148, 163, 184, 0.25)',
  };
  const claimActionBaseStyle = {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '0.25rem',
    fontFamily: 'Outfit, sans-serif',
    fontSize: '0.875rem',
    fontWeight: 600,
    lineHeight: '1.25rem',
    paddingTop: '0.375rem',
    paddingBottom: '0.375rem',
    borderRadius: '0.5rem',
  };
  const claimActionStyles = theme === 'dark'
    ? {
      neutral: { ...claimActionBaseStyle, backgroundColor: 'rgba(255, 255, 255, 0.04)', color: 'var(--fg)', border: '1px solid var(--border)', boxShadow: 'none', transform: 'none' },
      progress: { ...claimActionBaseStyle, backgroundColor: 'rgba(186, 133, 31, 0.14)', color: '#edc86f', border: '1px solid rgba(237, 190, 88, 0.28)', boxShadow: 'none', transform: 'none' },
      resolve: { ...claimActionBaseStyle, backgroundColor: 'rgba(52, 158, 94, 0.18)', color: '#75d49a', border: '1px solid rgba(89, 210, 137, 0.28)', boxShadow: 'none', transform: 'none' },
      reopen: { ...claimActionBaseStyle, backgroundColor: 'rgba(109, 40, 217, 0.16)', color: '#C4B5FD', border: '1px solid rgba(167, 139, 250, 0.36)', boxShadow: 'none', transform: 'none' },
      close: { ...claimActionBaseStyle, backgroundColor: 'rgba(190, 24, 93, 0.16)', color: '#FDA4AF', border: '1px solid rgba(225, 29, 72, 0.36)', boxShadow: 'none', transform: 'none' },
    }
    : {
      neutral: { ...claimActionBaseStyle, backgroundColor: 'rgba(107, 114, 128, 0.08)', color: '#4b5563', border: '1px solid rgba(107, 114, 128, 0.28)', boxShadow: 'none' },
      progress: { ...claimActionBaseStyle, backgroundColor: 'var(--pill-yellow-bg)', color: 'var(--pill-yellow-fg)', border: '1px solid rgba(161, 98, 7, 0.3)', boxShadow: 'none' },
      resolve: { ...claimActionBaseStyle, backgroundColor: 'var(--pill-green-bg)', color: 'var(--pill-green-fg)', border: '1px solid rgba(21, 128, 61, 0.28)', boxShadow: 'none' },
      reopen: { ...claimActionBaseStyle, backgroundColor: 'var(--pill-violet-bg)', color: 'var(--pill-violet-fg)', border: '1px solid rgba(109, 40, 217, 0.28)', boxShadow: 'none' },
      close: { ...claimActionBaseStyle, backgroundColor: 'var(--pill-rose-bg)', color: 'var(--pill-rose-fg)', border: '1px solid rgba(225, 29, 72, 0.28)', boxShadow: 'none' },
    };
  const claimStatusStyle = {
    pending: getAdminDashboardSemanticStyle(theme, 'yellow'),
    in_progress: getAdminDashboardSemanticStyle(theme, 'blue'),
    resolved: getAdminDashboardSemanticStyle(theme, 'mint'),
    closed: getAdminDashboardSemanticStyle(theme, 'lavender'),
  }[claim.status];
  const claimStatusBadgeStyle = { ...claimStatusStyle, borderWidth: '1px', boxShadow: 'none' };

  const photos = claim.photo_urls && claim.photo_urls.length > 0
    ? claim.photo_urls
    : (claim.photo_url ? [claim.photo_url] : []);

  useEffect(() => {
    setUnread(Number(claim.unread_count || 0));
  }, [claim.unread_count]);

  const handleClaimRead = () => {
    setUnread(0);
    onClaimRead?.(claim.id);
  };

  return (
    <div className="admin-claim-card overflow-hidden" data-testid={`admin-claim-${claim.id}`}>
      <button
        type="button"
        className="flex w-full items-center p-4 text-left cursor-pointer hover:bg-muted/50 transition-colors"
        onClick={() => setExpanded(!expanded)}
        aria-expanded={expanded}
        aria-controls={`admin-claim-${claim.id}-details`}
        aria-label={`${expanded ? 'Contraer' : 'Expandir'} reclamo ${claim.title}`}
        data-testid={`admin-claim-${claim.id}-toggle`}
      >
        <div className="flex w-full items-center justify-between gap-4 max-sm:flex-col max-sm:items-stretch">
          <div className="flex min-w-0 flex-1 items-center gap-3">
            <span
              style={claimIconStyle}
              className={`p-2 rounded-lg border-2 border-foreground ${theme === 'dark' ? '' : (
              claim.claim_type === 'repair' ? 'bg-accent-peach' :
              claim.claim_type === 'cleaning' ? 'bg-accent-mint' :
              claim.claim_type === 'noise' ? 'bg-accent-lavender' : 'bg-muted'
              )}`}
            >
              {claimTypeIcons[claim.claim_type]}
            </span>
            <div className="min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="font-heading font-semibold">{claim.title}</h3>
                {unread > 0 && (
                  <span
                    className="rounded-full bg-red-500 px-2 py-0.5 text-[11px] font-semibold leading-4 text-white"
                    data-testid={`admin-claim-${claim.id}-unread`}
                  >
                    {unread}
                  </span>
                )}
              </div>
              <p className="text-sm text-muted-foreground">
                {claim.user_name}
                {house?.name && <span className="ml-1 text-foreground font-medium">· {house.name}</span>}
                {claim.room_number && <span className="ml-1 text-foreground font-medium">· Hab. {claim.room_number}</span>}
                {' • '}
                {claimTypeLabels[claim.claim_type]}
              </p>
            </div>
          </div>
          <div className="flex min-w-0 flex-1 items-center justify-end gap-3 max-sm:w-full">
            {claim.status === 'resolved' && (
              <div
                className="flex max-w-full min-w-0 shrink-0 items-center gap-2 rounded-lg border-2 border-foreground bg-accent-yellow px-2.5 py-2 text-right text-xs font-medium max-sm:flex-1"
                data-testid={`admin-claim-${claim.id}-awaiting-guest`}
              >
                <Clock className="w-4 h-4 flex-shrink-0" />
                <span className="min-w-0">Esperando confirmación del usuario</span>
              </div>
            )}
            <span style={claimStatusBadgeStyle} className={`text-xs px-2 py-1 rounded-full font-medium ${
              claim.status === 'pending' ? 'badge-pending' :
              claim.status === 'in_progress' ? 'badge-in-progress' :
              claim.status === 'closed' ? 'bg-muted text-foreground border border-border' :
              'badge-resolved'
            }`}>
              {statusLabels[claim.status]}
            </span>
            <ChevronRight className={`w-5 h-5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
          </div>
        </div>
      </button>
      
      {expanded && (
        <div id={`admin-claim-${claim.id}-details`} style={claimExpandedStyle} className="p-4 border-t bg-muted/30">
          <p className="text-sm mb-4">{claim.description}</p>
          {photos.length > 0 && (
            <div className="mb-4">
              <p className="text-xs font-semibold text-muted-foreground mb-1 font-heading">
                {photos.length > 1 ? `Fotos (${photos.length})` : 'Foto del reclamo'}:
              </p>
              <div className="flex flex-wrap gap-2">
                {photos.map((url, idx) => (
                  <button
                    key={idx}
                    type="button"
                    onClick={(e) => { e.stopPropagation(); setLightboxImg(url); }}
                    className="border-0 bg-transparent p-0 cursor-zoom-in group relative"
                    data-testid={`claim-${claim.id}-photo-btn-${idx}`}
                  >
                    <img
                      src={url}
                      alt={`Foto ${idx + 1}`}
                      className="h-24 w-24 object-cover rounded-lg border-2 border-foreground hover:scale-105 transition-transform"
                      data-testid={`claim-${claim.id}-photo-${idx}`}
                    />
                  </button>
                ))}
              </div>
            </div>
          )}
          {claim.location && (
            <p className="text-sm text-muted-foreground mb-4">📍 {claim.location}</p>
          )}
          <div className="flex items-center gap-2 text-xs text-muted-foreground mb-4">
            <Clock className="w-3 h-3" />
            Creado: {formatDateTimeAR(claim.created_at, 'es-AR')}
          </div>
          <div className="flex flex-wrap gap-2 items-center">
            <button
              onClick={(e) => { e.stopPropagation(); setChatOpen(true); }}
              style={claimActionStyles?.neutral}
              className="btn-brutal-secondary btn-brutal px-3 py-1.5 text-sm flex items-center gap-1"
              data-testid={`admin-claim-${claim.id}-chat`}
            >
              <MessageCircle className="w-4 h-4" />
              Mensajes
            </button>
            {claim.status !== 'in_progress' && (
              <button
                onClick={(e) => { e.stopPropagation(); setPendingStatusTarget('in_progress'); }}
                style={claimActionStyles?.progress}
                className="btn-brutal-secondary btn-brutal px-3 py-1.5 text-sm flex items-center gap-1"
                data-testid={`claim-${claim.id}-in-progress`}
              >
                <Clock className="w-4 h-4" />
                En Progreso
              </button>
            )}
            {claim.status !== 'resolved' && claim.status !== 'closed' && (
              <button
                onClick={(e) => { e.stopPropagation(); setPendingStatusTarget('resolved'); }}
                style={claimActionStyles?.resolve}
                className="btn-brutal-accent btn-brutal px-3 py-1.5 text-sm flex items-center gap-1"
                data-testid={`claim-${claim.id}-resolve`}
              >
                <CheckCircle
                  className="w-4 h-4"
                  style={theme === 'light' ? { color: 'var(--pill-green-fg)', stroke: 'var(--pill-green-fg)' } : undefined}
                />
                Resolver
              </button>
            )}
            {claim.status !== 'pending' && claim.status !== 'closed' && (
              <button
                onClick={() => onUpdate(claim.id, { status: 'pending' })}
                style={claimActionStyles?.reopen}
                className="px-3 py-1.5 text-sm border-2 border-foreground rounded-lg hover:bg-muted flex items-center gap-1"
                data-testid={`claim-${claim.id}-pending`}
              >
                <RotateCcw className="w-4 h-4" />
                Reabrir
              </button>
            )}
            {claim.status !== 'closed' && (
              <button
                onClick={(e) => { e.stopPropagation(); onUpdate(claim.id, { status: 'closed' }); }}
                style={claimActionStyles?.close}
                className="px-3 py-1.5 text-sm bg-muted text-foreground border-2 border-foreground rounded-lg hover:bg-destructive/20 flex items-center gap-1"
                data-testid={`claim-${claim.id}-close`}
              >
                <XCircle className="w-4 h-4 text-destructive" />
                Cerrar Reclamo
              </button>
            )}
          </div>
        </div>
      )}

      {pendingStatusTarget && (
        <ClaimAutoResponseModal
          targetStatus={pendingStatusTarget}
          claim={claim}
          onClose={() => setPendingStatusTarget(null)}
          onConfirm={(payload) => {
            onUpdate(claim.id, payload);
            setPendingStatusTarget(null);
          }}
        />
      )}
      {chatOpen && (
        <>
          <style>{`
            [data-testid="claim-chat-modal"] {
              background-color: ${theme === 'dark' ? 'rgba(0, 0, 0, 0.68)' : 'rgba(0, 0, 0, 0.20)'} !important;
              backdrop-filter: blur(5px);
              -webkit-backdrop-filter: blur(5px);
            }
            [data-testid="claim-chat-modal"] > .modal-card {
              border: 1px solid var(--border) !important;
              border-color: var(--border) !important;
              box-shadow: ${theme === 'dark' ? '0 18px 50px rgba(0, 0, 0, 0.28)' : '0 18px 50px rgba(24, 32, 43, 0.16)'} !important;
              outline: none !important;
            }
          `}</style>
          <ClaimChatModal
            claim={claim}
            currentUserId={currentUserId}
            language="es"
            onRead={handleClaimRead}
            onClose={() => setChatOpen(false)}
          />
        </>
      )}

      {lightboxImg && createPortal(
        <div
          className="fixed inset-0 bg-black/95 z-[100] flex items-center justify-center p-4 cursor-zoom-out"
          onClick={() => setLightboxImg(null)}
          data-testid={`claim-${claim.id}-lightbox`}
        >
          <button
            type="button"
            onClick={(e) => { e.stopPropagation(); setLightboxImg(null); }}
            className="absolute right-4 w-11 h-11 rounded-full bg-white text-black font-bold flex items-center justify-center hover:scale-110 transition-transform shadow-lg z-10"
            style={{ top: 'calc(env(safe-area-inset-top, 0px) + 16px)' }}
            aria-label="Cerrar"
            data-testid={`claim-${claim.id}-lightbox-close`}
          >
            <X className="w-6 h-6" />
          </button>
          <img
            src={lightboxImg}
            alt="Foto del reclamo (ampliada)"
            className="max-w-full max-h-full object-contain"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
    </div>
  );
}

const announcementPriorityMeta = {
  info: { label: 'Información', badge: 'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-800/70 dark:bg-emerald-950/40 dark:text-emerald-300', dot: 'bg-emerald-500' },
  important: { label: 'Importante', badge: 'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-800/70 dark:bg-amber-950/40 dark:text-amber-300', dot: 'bg-amber-500' },
  maintenance: { label: 'Mantenimiento', badge: 'border-blue-200 bg-blue-50 text-blue-700 dark:border-blue-800/70 dark:bg-blue-950/40 dark:text-blue-300', dot: 'bg-blue-500' },
  event: { label: 'Evento', badge: 'border-violet-200 bg-violet-50 text-violet-700 dark:border-violet-800/70 dark:bg-violet-950/40 dark:text-violet-300', dot: 'bg-violet-500' },
  urgent: { label: 'Urgente', badge: 'border-red-200 bg-red-50 text-red-700 dark:border-red-800/70 dark:bg-red-950/40 dark:text-red-300', dot: 'bg-red-500' },
  multa: { label: 'Multa / Sanción', badge: 'border-pink-200 bg-pink-50 text-pink-700 dark:border-pink-800/70 dark:bg-pink-950/40 dark:text-pink-300', dot: 'bg-pink-500' }
};

const announcementPriorityIcons = {
  info: Info,
  important: CircleAlert,
  maintenance: Wrench,
  event: CalendarDays,
  urgent: Siren,
  multa: FileWarning,
};

const announcementPriorityIconColors = {
  info: 'text-emerald-500',
  important: 'text-amber-500',
  maintenance: 'text-blue-500',
  event: 'text-violet-500',
  urgent: 'text-red-500',
  multa: 'text-pink-500',
};

function AnnouncementPreviewModal({ announcement, onClose }) {
  useEffect(() => {
    if (!announcement) return undefined;

    const previousOverflow = document.body.style.overflow;
    const handleEscape = (event) => {
      if (event.key === 'Escape') onClose();
    };

    document.body.style.overflow = 'hidden';
    document.addEventListener('keydown', handleEscape);

    return () => {
      document.body.style.overflow = previousOverflow;
      document.removeEventListener('keydown', handleEscape);
    };
  }, [announcement, onClose]);

  if (!announcement) return null;

  const priority = announcementPriorityMeta[announcement.priority] || announcementPriorityMeta.info;
  const PriorityIcon = announcementPriorityIcons[announcement.priority] || Info;
  const priorityIconColor = announcementPriorityIconColors[announcement.priority] || announcementPriorityIconColors.info;

  return createPortal(
    <div
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 p-4 modal-backdrop"
      role="presentation"
      onClick={onClose}
      data-testid={`announcement-preview-modal-${announcement.id}`}
    >
      <div
        className="flex max-h-[calc(100dvh-2rem)] w-full max-w-lg flex-col overflow-hidden rounded-2xl border border-border bg-surface shadow-2xl"
        role="dialog"
        aria-modal="true"
        aria-labelledby={`announcement-preview-title-${announcement.id}`}
        onClick={(event) => event.stopPropagation()}
      >
        <div className="shrink-0 border-b border-border p-6 pb-4">
          <div className="mb-4 flex items-start justify-between gap-4">
            <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${priority.badge}`}>
              <PriorityIcon className={`h-3.5 w-3.5 shrink-0 ${priorityIconColor}`} aria-hidden="true" />
              {priority.label}
            </span>
            <button
              type="button"
              onClick={onClose}
              className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-muted hover:text-foreground"
              aria-label="Cerrar vista previa"
              data-testid="announcement-preview-close-button"
            >
              <X className="h-5 w-5" />
            </button>
          </div>
          <h2 id={`announcement-preview-title-${announcement.id}`} className="font-heading text-xl font-bold text-foreground">
            {announcement.title}
          </h2>
          <div className="mt-3 flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-muted-foreground">
            <span className="inline-flex items-center gap-1.5">
              <Calendar className="h-3.5 w-3.5" />
              {formatDateTimeAR(announcement.created_at, 'es-AR')}
            </span>
            {announcement.author_name && (
              <>
                <span aria-hidden="true">•</span>
                <span>{announcement.author_name}</span>
              </>
            )}
          </div>
        </div>
        <div className="min-h-0 overflow-y-auto p-6" style={{ overscrollBehavior: 'contain' }}>
          <p className="whitespace-pre-wrap break-words text-sm leading-relaxed text-foreground">
            {linkify(announcement.content)}
          </p>
        </div>
      </div>
    </div>,
    document.body
  );
}

function AnnouncementsSection({ announcements, houses, onCreateNew, onEdit, onDelete, loading }) {
  const { theme } = useTheme();
  const [search, setSearch] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('all');
  const [actionsMenu, setActionsMenu] = useState(null);
  const [previewAnnouncement, setPreviewAnnouncement] = useState(null);

  const filteredAnnouncements = announcements.filter((announcement) => {
    const term = search.trim().toLowerCase();
    const matchesSearch = !term || [announcement.title, announcement.content, announcement.author_name]
      .some(value => (value || '').toLowerCase().includes(term));
    return matchesSearch && (priorityFilter === 'all' || announcement.priority === priorityFilter);
  });

  const recipientLabel = (announcement) => {
    if (announcement.target_user_ids?.length) {
      return `${announcement.target_user_ids.length} huésped${announcement.target_user_ids.length === 1 ? '' : 'es'}`;
    }
    if (announcement.target_houses?.length) {
      const names = announcement.target_houses
        .map(id => houses.find(house => house.id === id)?.name)
        .filter(Boolean);
      return names.length === 1 ? names[0] : `${announcement.target_houses.length} casas`;
    }
    return 'Todas las casas';
  };

  const openActionsMenu = (event, announcement) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 176;
    setActionsMenu({
      announcement,
      top: rect.bottom + 6,
      left: Math.max(12, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 12))
    });
  };

  const runAnnouncementAction = (action) => {
    setActionsMenu(null);
    action();
  };

  return (
    <div className="space-y-5 animate-enter" data-testid="admin-announcements-section">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-heading text-2xl font-bold text-foreground">Comunicados</h1>
          <p className="mt-1 text-sm text-muted-foreground">Informá y mantené conectada a la comunidad.</p>
        </div>
        <button
          onClick={onCreateNew}
          className="inline-flex h-11 items-center justify-center gap-2 self-start rounded-xl px-5 text-sm font-semibold shadow-sm transition-opacity hover:opacity-90"
          style={theme === 'light'
            ? { backgroundColor: '#111111', color: '#ffffff' }
            : { backgroundColor: '#ffffff', color: '#111111' }}
          data-testid="new-announcement-button"
        >
          <Plus className="h-4 w-4" />
          Nuevo comunicado
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_190px]">
        <label className="relative block">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="search"
            value={search}
            onChange={(event) => setSearch(event.target.value)}
            placeholder="Buscar por título, contenido o autor..."
            className="h-11 w-full rounded-xl border border-border bg-surface pl-11 pr-4 text-sm text-foreground outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100/70 dark:focus:ring-amber-900/20"
            aria-label="Buscar comunicados"
          />
        </label>
        <div className="relative">
          <select
            value={priorityFilter}
            onChange={(event) => setPriorityFilter(event.target.value)}
            className="h-11 w-full appearance-none rounded-xl border border-border bg-surface py-2 pl-4 pr-9 text-sm font-medium text-foreground outline-none transition"
            aria-label="Filtrar por prioridad"
          >
            <option value="all">Todas</option>
            {Object.entries(announcementPriorityMeta).map(([value, meta]) => (
              <option key={value} value={value}>{meta.label}</option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        </div>
      </div>

      {loading ? (
        <LoadingIndicator className="py-8" />
      ) : announcements.length === 0 ? (
        <div className="admin-content-surface rounded-2xl border border-border bg-surface p-10 text-center shadow-none">
          <Megaphone className="mx-auto mb-4 h-12 w-12 text-muted-foreground" />
          <p className="text-muted-foreground">No hay comunicados</p>
        </div>
      ) : filteredAnnouncements.length === 0 ? (
        <div className="admin-content-surface rounded-2xl border border-border bg-surface p-10 text-center shadow-none">
          <Search className="mx-auto mb-3 h-9 w-9 text-muted-foreground/60" />
          <p className="font-medium text-foreground">No encontramos comunicados</p>
          <p className="mt-1 text-sm text-muted-foreground">Probá con otra búsqueda o prioridad.</p>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredAnnouncements.map((announcement) => {
            const priority = announcementPriorityMeta[announcement.priority] || announcementPriorityMeta.info;
            return (
              <article key={announcement.id} className="admin-content-surface rounded-2xl border border-border bg-surface p-5 shadow-none transition-colors" data-testid={`admin-announcement-${announcement.id}`}>
                <div className="flex items-start gap-4">
                  <div className="min-w-0 flex-1">
                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <span className={`inline-flex items-center gap-1.5 rounded-full border px-2.5 py-1 text-xs font-semibold ${priority.badge}`}>
                        {(() => {
                          const PriorityIcon = announcementPriorityIcons[announcement.priority] || Info;
                          return <PriorityIcon className={`h-3.5 w-3.5 shrink-0 ${announcementPriorityIconColors[announcement.priority] || announcementPriorityIconColors.info}`} aria-hidden="true" />;
                        })()}
                        {priority.label}
                      </span>
                      <span className="inline-flex items-center gap-1.5 text-xs text-muted-foreground">
                        <UsersRound className="h-3.5 w-3.5" />
                        {recipientLabel(announcement)}
                      </span>
                    </div>
                    <h3 className="font-heading text-base font-semibold text-foreground">{announcement.title}</h3>
                    <p className="mt-1.5 line-clamp-2 text-sm leading-6 text-muted-foreground">{linkify(announcement.content)}</p>
                    <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 border-t border-border/70 pt-3 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1.5"><Calendar className="h-3.5 w-3.5" />{formatDateTimeAR(announcement.created_at, 'es-AR')}</span>
                      {announcement.author_name && <span className="inline-flex items-center gap-1.5"><Users className="h-3.5 w-3.5" />{announcement.author_name}</span>}
                      {Array.isArray(announcement.read_by) && <span className="inline-flex items-center gap-1.5"><Eye className="h-3.5 w-3.5" />{announcement.read_by.length} lectura{announcement.read_by.length === 1 ? '' : 's'}</span>}
                    </div>
                  </div>
                  <div className="flex shrink-0 items-center gap-2">
                    <button
                      type="button"
                      onClick={() => setPreviewAnnouncement(announcement)}
                      className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground"
                      aria-label="Vista previa"
                      data-testid={`announcement-preview-${announcement.id}`}
                    >
                      <Eye className="h-4 w-4" aria-hidden="true" />
                    </button>
                    <button
                      type="button"
                      onClick={(event) => openActionsMenu(event, announcement)}
                      className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border text-muted-foreground transition hover:bg-muted hover:text-foreground"
                      aria-haspopup="menu"
                      aria-expanded={actionsMenu?.announcement?.id === announcement.id}
                      aria-label={`Acciones para ${announcement.title}`}
                    >
                      <MoreHorizontal className="h-4 w-4" />
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      )}

      {actionsMenu && createPortal(
        <>
          <button type="button" className="fixed inset-0 z-[80] cursor-default" onClick={() => setActionsMenu(null)} aria-label="Cerrar menú de acciones" />
          <div role="menu" className="fixed z-[90] w-44 overflow-hidden rounded-xl border border-border bg-surface p-1.5 shadow-xl" style={{ top: actionsMenu.top, left: actionsMenu.left }}>
            <button
              type="button"
              onClick={() => runAnnouncementAction(() => onEdit(actionsMenu.announcement))}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-foreground transition hover:bg-muted"
              data-testid={`edit-announcement-${actionsMenu.announcement.id}`}
            >
              <Edit2 className="h-4 w-4" /> Editar
            </button>
            <button
              type="button"
              onClick={() => runAnnouncementAction(() => onDelete(actionsMenu.announcement.id))}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-destructive transition hover:bg-destructive/10"
              data-testid={`delete-announcement-${actionsMenu.announcement.id}`}
            >
              <Trash2 className="h-4 w-4" /> Eliminar
            </button>
          </div>
        </>,
        document.body
      )}

      <AnnouncementPreviewModal
        announcement={previewAnnouncement}
        onClose={() => setPreviewAnnouncement(null)}
      />
    </div>
  );
}

function HousesSection({ houses, loading, onCreateHouse, onEdit, onDelete, isSuperAdmin = true }) {
  const [actionsMenu, setActionsMenu] = useState(null);

  const openActionsMenu = (event, house) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 176;
    setActionsMenu({
      house,
      top: rect.bottom + 6,
      left: Math.max(12, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 12))
    });
  };

  const runHouseAction = (action) => {
    setActionsMenu(null);
    action();
  };

  return (
    <div className="space-y-5 animate-enter" data-testid="admin-houses-section">
      <style>{`
        [data-testid="admin-houses-section"] .house-card {
          overflow: hidden;
          border: 1px solid var(--border);
          box-shadow: none;
        }
        :root[data-theme="light"] [data-testid="admin-houses-section"] .house-card {
          box-shadow: 0 1px 2px rgba(15, 23, 41, 0.04),
                      0 4px 12px rgba(15, 23, 41, 0.05);
        }
        [data-testid="admin-houses-section"] .house-card-visual {
          height: 148px;
          overflow: hidden;
          border-radius: 16px 16px 0 0;
          background: var(--surface-hover);
        }
        [data-testid="admin-houses-section"] .house-card-banner,
        [data-testid="admin-houses-section"] .house-card-placeholder {
          display: block;
          width: 100%;
          height: 100%;
        }
        [data-testid="admin-houses-section"] .house-card-banner {
          object-fit: cover;
        }
        [data-testid="admin-houses-section"] .house-card-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--muted-fg);
          background: linear-gradient(135deg, var(--surface-hover), var(--muted));
        }
        [data-testid="admin-houses-section"] .house-card:hover {
          box-shadow: none;
        }
        [data-testid="admin-houses-section"] .house-card-content {
          position: relative;
        }
        [data-testid="admin-houses-section"] [data-testid="new-house-button"],
        [data-testid="admin-houses-section"] [data-testid="new-house-button"]:hover,
        [data-testid="admin-houses-section"] [data-testid="new-house-button"]:active {
          background-color: #111111 !important;
          color: #ffffff !important;
          border: 1px solid #111111 !important;
          box-shadow: none !important;
          transform: none !important;
        }
        :root[data-theme="dark"] [data-testid="admin-houses-section"] [data-testid="new-house-button"],
        :root[data-theme="dark"] [data-testid="admin-houses-section"] [data-testid="new-house-button"]:hover,
        :root[data-theme="dark"] [data-testid="admin-houses-section"] [data-testid="new-house-button"]:active {
          background-color: #ffffff !important;
          color: #111111 !important;
          border-color: var(--border) !important;
        }
      `}</style>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl text-foreground">Casas</h1>
          <p className="mt-1 text-sm text-muted-foreground">Gestioná las residencias de WannaColive.</p>
        </div>
        {isSuperAdmin && (
          <button
            onClick={onCreateHouse}
            className="btn-brutal flex items-center gap-2 self-start px-4 py-2.5"
            data-testid="new-house-button"
          >
            <Plus className="w-4 h-4" />
            Nueva casa
          </button>
        )}
      </div>
      
      {loading ? (
        <LoadingIndicator className="py-8" />
      ) : houses.length === 0 ? (
        <div className="admin-surface-card p-8 text-center">
          <Building2 className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground mb-4">No hay casas registradas</p>
          {isSuperAdmin && (
            <button
              onClick={onCreateHouse}
              className="btn-brutal px-4 py-2"
            >
              Crear primera casa
            </button>
          )}
        </div>
      ) : (
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-3">
          {houses.map((house) => {
            const amenities = house.amenities || [];
            return (
            <article key={house.id} className="house-card flex min-h-[230px] flex-col rounded-2xl bg-surface transition-all" data-testid={`house-${house.id}`}>
              <div className="house-card-visual">
                {house.cover_photo ? (
                  <img src={house.cover_photo} alt="" className="house-card-banner" />
                ) : (
                  <div className="house-card-placeholder">
                    <Building2 className="h-10 w-10" />
                  </div>
                )}
              </div>
              <div className="house-card-content flex min-h-0 flex-1 flex-col px-5 pb-4 pt-4">
              <div className="house-card-title-row flex min-w-0 items-center justify-between gap-3">
                <div className="flex min-w-0 items-center gap-2">
                  <span className="flex h-[38px] w-[38px] shrink-0 items-center justify-center rounded-lg border border-border bg-surface p-1">
                    <img src="/logo192.png" alt="WannaColive" className="h-full w-full object-contain brand-logo-adaptive" />
                  </span>
                  <h3 className="min-w-0 truncate font-heading text-lg font-semibold text-foreground" title={house.name}>{house.name}</h3>
                </div>
                <button
                  type="button"
                  onClick={(event) => openActionsMenu(event, house)}
                  className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg border border-border bg-surface text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                  aria-label={`Acciones para ${house.name}`}
                  aria-haspopup="menu"
                  aria-expanded={actionsMenu?.house?.id === house.id}
                  data-testid={`house-actions-${house.id}`}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </button>
              </div>

              <div className="mt-2 min-w-0">
                <p className="mt-2 flex items-start gap-2 text-sm text-muted-foreground">
                  <MapPin className="mt-0.5 h-4 w-4 shrink-0" />
                  <span className="line-clamp-2">{house.address || 'Sin dirección'}</span>
                </p>
                <p className="mt-1 pl-6 text-sm text-muted-foreground">{house.city || 'Sin ciudad'}</p>
              </div>

              <div className="mt-3 flex items-center gap-2 border-t border-border pt-3">
                <DoorOpen className="h-4 w-4 text-primary" />
                <span className="text-sm font-semibold text-foreground">{house.total_rooms}</span>
                <span className="text-xs text-muted-foreground">habitaciones</span>
              </div>

              <div className="mt-auto pt-3">
                {amenities.length > 0 ? (
                  <div className="flex min-w-0 items-center gap-1.5 overflow-hidden" aria-label="Amenities">
                    {amenities.slice(0, 3).map((amenity, index) => (
                      <span key={`${amenity}-${index}`} className="max-w-[110px] shrink truncate rounded-full bg-muted px-2.5 py-1 text-[11px] text-muted-foreground" title={amenity}>
                        {amenity}
                      </span>
                    ))}
                    {amenities.length > 3 && (
                      <span className="shrink-0 rounded-full bg-muted px-2.5 py-1 text-[11px] font-medium text-muted-foreground">+{amenities.length - 3}</span>
                    )}
                  </div>
                ) : (
                  <span className="text-xs text-muted-foreground">Sin amenities registrados</span>
                )}
              </div>
              </div>
            </article>
          );})}
        </div>
      )}

      {actionsMenu && createPortal(
        <>
          <button
            type="button"
            className="fixed inset-0 z-[90] cursor-default"
            onClick={() => setActionsMenu(null)}
            aria-label="Cerrar menú de acciones"
          />
          <div
            role="menu"
            className="fixed z-[100] w-44 overflow-hidden rounded-xl border border-border bg-surface p-1.5 shadow-xl"
            style={{ top: actionsMenu.top, left: actionsMenu.left }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => runHouseAction(() => onEdit(actionsMenu.house))}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-foreground hover:bg-muted"
              data-testid={`edit-house-${actionsMenu.house.id}`}
            >
              <Edit2 className="h-4 w-4" /> Editar
            </button>
            <div className="my-1 border-t border-border" />
            <button
              type="button"
              role="menuitem"
              onClick={() => runHouseAction(() => onDelete(actionsMenu.house.id))}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm text-red-700 hover:bg-red-50"
              data-testid={`delete-house-${actionsMenu.house.id}`}
            >
              <Trash2 className="h-4 w-4" /> Eliminar
            </button>
          </div>
        </>,
        document.body
      )}
    </div>
  );
}

function NewHouseModal({ onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    city: '',
    total_rooms: '',
    amenities: '',
    cover_photo: '',
    shared_room_capacity: 2,
    visit_cost_per_night: '',
    visit_cost_currency: 'ARS',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [globalVisitCost, setGlobalVisitCost] = useState(0);
  const [globalVisitCurrency, setGlobalVisitCurrency] = useState('ARS');
  const { cropImage, cropperNode } = useImageCropper();

  useEffect(() => {
    (async () => {
      try {
        const s = await api.getSettings();
        setGlobalVisitCost(s.visit_cost_per_night ?? 0);
        setGlobalVisitCurrency(s.visit_cost_currency || 'ARS');
      } catch (e) { /* usa defaults */ }
    })();
  }, []);

  const handleImage = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!f) return;
    try {
      const cropped = await cropImage(f, { aspect: 3 / 2, title: 'Encuadrar portada' });
      if (!cropped) return;
      const compressed = await compressImage(cropped, 1200, 800, 0.75);
      setFormData(p => ({ ...p, cover_photo: compressed }));
    } catch (err) {
      setError('Error al procesar la imagen');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onSubmit({
        ...formData,
        total_rooms: parseInt(formData.total_rooms),
        shared_room_capacity: parseInt(formData.shared_room_capacity) || 2,
        visit_cost_per_night: formData.visit_cost_per_night === '' ? null : parseInt(formData.visit_cost_per_night) || 0,
        visit_cost_currency: formData.visit_cost_currency || 'ARS',
        amenities: formData.amenities.split(',').map(a => a.trim()).filter(a => a)
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear la casa');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 modal-backdrop" data-testid="new-house-modal">
      {cropperNode}
      <style>{`
        [data-testid="new-house-modal"] .house-modal-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          box-shadow: none;
        }
        :root[data-theme="light"] [data-testid="new-house-modal"] .house-modal-card {
          background: #ffffff;
        }
        [data-testid="new-house-modal"] .input-brutal,
        [data-testid="new-house-modal"] .border-2 {
          border-width: 1px;
          border-color: var(--border);
          box-shadow: none;
        }
        [data-testid="new-house-modal"] [data-testid="submit-house-button"],
        [data-testid="new-house-modal"] [data-testid="submit-house-button"]:hover,
        [data-testid="new-house-modal"] [data-testid="submit-house-button"]:active {
          background-color: #111111 !important;
          color: #ffffff !important;
          border: 1px solid #111111 !important;
          box-shadow: none !important;
          transform: none !important;
        }
        :root[data-theme="dark"] [data-testid="new-house-modal"] [data-testid="submit-house-button"],
        :root[data-theme="dark"] [data-testid="new-house-modal"] [data-testid="submit-house-button"]:hover,
        :root[data-theme="dark"] [data-testid="new-house-modal"] [data-testid="submit-house-button"]:active {
          background-color: #ffffff !important;
          color: #111111 !important;
          border-color: var(--border) !important;
        }
        [data-testid="new-house-modal"] .house-modal-cancel {
          background: #ffffff;
          color: #111111;
          border: 1px solid var(--border);
          box-shadow: none;
        }
        :root[data-theme="dark"] [data-testid="new-house-modal"] .house-modal-cancel {
          background: var(--surface);
          color: var(--fg);
        }
      `}</style>
      <div className="bg-surface border rounded-xl w-full max-w-lg overflow-y-auto modal-card house-modal-card">
        <div className="p-6">
          <h2 className="font-heading font-bold text-xl mb-6 text-foreground">Nueva Casa</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-destructive/20 border border-destructive rounded-lg text-sm text-destructive">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Nombre de la casa *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-brutal w-full px-4 py-3"
                placeholder="Ej: Casa Palermo"
                required
                data-testid="house-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Dirección *</label>
              <input
                type="text"
                value={formData.address}
                onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                className="input-brutal w-full px-4 py-3"
                placeholder="Ej: Av. Santa Fe 1234"
                required
                data-testid="house-address-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Ciudad *</label>
              <input
                type="text"
                value={formData.city}
                onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                className="input-brutal w-full px-4 py-3"
                placeholder="Ej: Capital Federal"
                required
                data-testid="house-city-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Cantidad de habitaciones *</label>
              <input
                type="number"
                value={formData.total_rooms}
                onChange={(e) => setFormData({ ...formData, total_rooms: e.target.value })}
                className="input-brutal w-full px-4 py-3"
                placeholder="Ej: 12"
                required
                min="1"
                data-testid="house-rooms-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Foto de portada</label>
              <div className="flex items-center gap-3">
                {formData.cover_photo && (
                  <img src={formData.cover_photo} alt="preview" className="w-24 h-16 rounded-lg border-2 border-foreground object-cover" data-testid="house-cover-preview" />
                )}
                <label className="flex-1 cursor-pointer">
                  <input type="file" accept="image/*" onChange={handleImage} className="hidden" data-testid="house-cover-input" />
                  <div className="px-4 py-3 border-2 border-foreground border-dashed rounded-lg text-center text-sm font-medium hover:bg-muted">
                    {formData.cover_photo ? 'Cambiar foto' : 'Subir foto (opcional)'}
                  </div>
                </label>
                {formData.cover_photo && (
                  <button type="button" onClick={() => setFormData(p => ({ ...p, cover_photo: '' }))} className="px-3 py-3 border-2 border-foreground rounded-lg text-sm hover:bg-muted" data-testid="house-cover-remove">
                    Quitar
                  </button>
                )}
              </div>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Amenities (separados por coma)</label>
              <input
                type="text"
                value={formData.amenities}
                onChange={(e) => setFormData({ ...formData, amenities: e.target.value })}
                className="input-brutal w-full px-4 py-3"
                placeholder="WiFi, Cocina, Lavandería, Coworking"
                data-testid="house-amenities-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Capacidad máxima por habitación compartida *</label>
              <input
                type="number"
                min={1}
                max={10}
                value={formData.shared_room_capacity}
                onChange={(e) => setFormData({ ...formData, shared_room_capacity: e.target.value })}
                className="input-brutal w-full px-4 py-3"
                data-testid="house-shared-capacity-input"
              />
              <p className="text-xs text-muted-foreground mt-1">Ej: Casas chicas hasta 2 personas; Córdoba / Nueva Córdoba hasta 3.</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Precio de visita por noche (opcional)</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  value={formData.visit_cost_per_night}
                  onChange={(e) => setFormData({ ...formData, visit_cost_per_night: e.target.value })}
                  className="input-brutal flex-1 px-4 py-3"
                  placeholder={globalVisitCost ? `Heredado (${globalVisitCurrency} ${globalVisitCost})` : 'Heredado'}
                  data-testid="house-visit-cost-input"
                />
                <select
                  value={formData.visit_cost_currency}
                  onChange={(e) => setFormData({ ...formData, visit_cost_currency: e.target.value })}
                  className="input-brutal px-3 py-3"
                  disabled={formData.visit_cost_per_night === ''}
                  data-testid="house-visit-currency-input"
                >
                  {['ARS', 'USD', 'EUR'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Vacío = usa el precio global ({globalVisitCurrency} {globalVisitCost}).
              </p>
            </div>
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="house-modal-cancel flex-1 py-3 rounded-lg font-medium hover:bg-muted transition-colors"
                data-testid="cancel-house-button"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="btn-brutal flex-1 py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="submit-house-button"
              >
                {loading ? 'Creando...' : 'Crear Casa'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function UsersSection({ users, houses, loading, onCreateUser, onUpdateUser, onEdit, onDelete, currentUser }) {
  const { language } = useLanguage();
  const [showNewUser, setShowNewUser] = useState(false);
  const [houseFilter, setHouseFilter] = useState('all'); 
  const [resetResult, setResetResult] = useState(null); 
  const [confirmReset, setConfirmReset] = useState(null); 
  const [resetting, setResetting] = useState(false);
  const [shareAccessUser, setShareAccessUser] = useState(null); 
  const [actionsMenu, setActionsMenu] = useState(null);
  const isHM = currentUser?.role === 'house_manager';
  const guestUsers = users.filter(u => u.role === 'guest');
  const activeUsers = users.filter(u => u.is_active !== false);
  const inactiveUsers = users.filter(u => u.is_active === false);

  
  
  
  
  const validHouseIds = new Set(houses.map(h => h.id));
  const isUnassigned = (u) => !u.house_id || !validHouseIds.has(u.house_id);

  const filterByHouse = (list) => {
    if (houseFilter === 'all') return list;
    if (houseFilter === 'unassigned') return list.filter(isUnassigned);
    return list.filter(u => u.house_id === houseFilter);
  };

  const handleToggleActive = async (user) => {
    await onUpdateUser(user.id, { is_active: !user.is_active });
  };

  const handleConfirmReset = async () => {
    if (!confirmReset) return;
    setResetting(true);
    try {
      const data = await api.resetUserPassword(confirmReset.id);
      setResetResult({ user: confirmReset, temporary_password: data.temporary_password });
      setConfirmReset(null);
    } catch (err) {
      alert(err.response?.data?.detail || 'Error al resetear la contraseña');
    }
    setResetting(false);
  };

  const isLeaseExpired = (leaseEnd) => {
    if (!leaseEnd) return false;
    
    const m = typeof leaseEnd === 'string' && /^(\d{4})-(\d{2})-(\d{2})/.exec(leaseEnd.trim());
    let end;
    if (m) {
      end = new Date(parseInt(m[1], 10), parseInt(m[2], 10) - 1, parseInt(m[3], 10));
    } else {
      end = new Date(leaseEnd);
    }
    end.setHours(23, 59, 59, 999);
    return end < new Date();
  };

  const [search, setSearch] = useState('');
  
  
  
  
  const [sort, setSort] = useState({ field: 'lease_end', dir: 'asc' });
  const toggleSort = (field) => {
    setSort((prev) => prev.field === field
      ? { field, dir: prev.dir === 'asc' ? 'desc' : 'asc' }
      : { field, dir: 'asc' });
  };
  const sortUsers = (list) => {
    const { field, dir } = sort;
    const mult = dir === 'asc' ? 1 : -1;
    const value = (u) => {
      if (field === 'lease_end') {
        
        
        if (!u.lease_end) return dir === 'asc' ? Infinity : -Infinity;
        return new Date(u.lease_end).getTime();
      }
      if (field === 'name') return (u.name || '').toLowerCase();
      if (field === 'email') return (u.email || '').toLowerCase();
      return 0;
    };
    return [...list].sort((a, b) => {
      const va = value(a);
      const vb = value(b);
      if (va < vb) return -1 * mult;
      if (va > vb) return 1 * mult;
      return 0;
    });
  };
  const filterBySearch = (list) => {
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter(u =>
      (u.name || '').toLowerCase().includes(q) ||
      (u.email || '').toLowerCase().includes(q) ||
      (u.room_number || '').toString().toLowerCase().includes(q) ||
      (u.phone || '').includes(q)
    );
  };
  const filteredActive = sortUsers(filterBySearch(filterByHouse(activeUsers)));
  const filteredInactive = sortUsers(filterBySearch(filterByHouse(inactiveUsers)));
  const filteredUsers = sortUsers(filterBySearch(filterByHouse(users)));
  const activatedUsers = users.filter(u => !!u.last_login_at).length;
  const pendingUsers = users.length - activatedUsers;

  const openActionsMenu = (event, user) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const menuWidth = 224;
    setActionsMenu({
      user,
      top: rect.bottom + 6,
      left: Math.max(12, Math.min(rect.right - menuWidth, window.innerWidth - menuWidth - 12))
    });
  };

  const runMenuAction = (action) => {
    setActionsMenu(null);
    action();
  };

  const initialsFor = (name) => (name || '?')
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join('')
    .toUpperCase();

  return (
    <div className="space-y-5 animate-enter" data-testid="admin-users-section">
      <style>{`
        [data-testid="admin-users-section"] .users-new-button {
          background: #111111 !important;
          color: #ffffff !important;
          border: 1px solid #111111 !important;
          box-shadow: none !important;
          transform: none;
        }
        [data-testid="admin-users-section"] .users-new-button:hover {
          background: #222222 !important;
          box-shadow: none !important;
          transform: none;
        }
        :root[data-theme="dark"] [data-testid="admin-users-section"] .users-new-button {
          background: #ffffff !important;
          color: #111111 !important;
          border-color: #ffffff !important;
        }
        :root[data-theme="dark"] [data-testid="admin-users-section"] .users-new-button:hover {
          background: #f1f1f1 !important;
        }
        [data-testid="admin-users-section"] .users-sort-active {
          color: #111111;
        }
        :root[data-theme="dark"] [data-testid="admin-users-section"] .users-sort-active {
          color: #ffffff;
        }
        [data-testid="admin-users-section"] .users-summary-icon {
          border: 1px solid transparent;
        }
        [data-testid="admin-users-section"] .users-summary-total {
          background: #E0F2FE;
          color: #0369A1;
          border-color: rgba(3, 105, 161, 0.24);
        }
        [data-testid="admin-users-section"] .users-summary-active,
        [data-testid="admin-users-section"] .users-summary-activated {
          background: #DCFCE7;
          color: #15803D;
          border-color: rgba(21, 128, 61, 0.24);
        }
        [data-testid="admin-users-section"] .users-summary-inactive {
          background: #FEE2E2;
          color: #BE123C;
          border-color: rgba(190, 18, 60, 0.24);
        }
        [data-testid="admin-users-section"] .users-summary-pending {
          background: #FEF3C7;
          color: #B45309;
          border-color: rgba(180, 83, 9, 0.24);
        }
        :root[data-theme="dark"] [data-testid="admin-users-section"] .users-summary-total {
          background: rgba(14, 116, 144, 0.28);
          color: #7DD3FC;
          border-color: rgba(125, 211, 252, 0.3);
        }
        :root[data-theme="dark"] [data-testid="admin-users-section"] .users-summary-active,
        :root[data-theme="dark"] [data-testid="admin-users-section"] .users-summary-activated {
          background: rgba(52, 158, 94, 0.28);
          color: #75D49A;
          border-color: rgba(117, 212, 154, 0.3);
        }
        :root[data-theme="dark"] [data-testid="admin-users-section"] .users-summary-inactive {
          background: rgba(190, 18, 60, 0.25);
          color: #FDA4AF;
          border-color: rgba(253, 164, 175, 0.3);
        }
        :root[data-theme="dark"] [data-testid="admin-users-section"] .users-summary-pending {
          background: rgba(186, 133, 31, 0.3);
          color: #edc86f;
          border-color: rgba(237, 190, 88, 0.3);
        }
        [data-testid="admin-users-section"] .users-status-pill {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          border: 1px solid transparent;
        }
        [data-testid="admin-users-section"] .users-status-active {
          background: #DCFCE7;
          color: #15803D;
          border-color: rgba(21, 128, 61, 0.24);
        }
        [data-testid="admin-users-section"] .users-status-inactive {
          background: #FEE2E2;
          color: #BE123C;
          border-color: rgba(190, 18, 60, 0.24);
        }
        :root[data-theme="dark"] [data-testid="admin-users-section"] .users-status-active {
          background: rgba(52, 158, 94, 0.28);
          color: #75D49A;
          border-color: rgba(117, 212, 154, 0.3);
        }
        :root[data-theme="dark"] [data-testid="admin-users-section"] .users-status-inactive {
          background: rgba(190, 18, 60, 0.25);
          color: #FDA4AF;
          border-color: rgba(253, 164, 175, 0.3);
        }
      `}</style>
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
        <h1 className="font-heading font-bold text-2xl text-foreground">{language === 'es' ? 'Gestión de usuarios' : 'User Management'}</h1>
          <p className="mt-1 text-sm text-muted-foreground">Administrá residentes, accesos y contratos.</p>
        </div>
        <button
          onClick={() => setShowNewUser(true)}
          className="users-new-button btn-brutal px-4 py-2.5 flex items-center justify-center gap-2 self-start"
          data-testid="new-user-button"
        >
          <Plus className="w-4 h-4" />
          Nuevo usuario
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_190px]" data-testid="users-house-filter">
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Buscar por nombre, email, habitación o teléfono..."
            className="h-11 w-full rounded-xl border border-border bg-surface pl-11 pr-10 text-sm text-foreground outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100/70 dark:focus:ring-amber-900/20"
            data-testid="users-search-input"
          />
          {search && (
            <button
              type="button"
              onClick={() => setSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Limpiar búsqueda"
              data-testid="users-search-clear"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>
        <div className="relative">
          <Building2 className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <select
            value={houseFilter}
            onChange={(event) => setHouseFilter(event.target.value)}
                className="h-11 w-full appearance-none rounded-xl border border-border bg-surface py-2 pl-11 pr-9 text-sm font-medium text-foreground outline-none transition"
            aria-label="Filtrar por casa"
            data-testid="users-house-select"
          >
            <option value="all" data-testid="filter-house-all">Todas las casas ({guestUsers.length})</option>
            {houses.map((house) => (
              <option key={house.id} value={house.id} data-testid={`filter-house-${house.id}`}>
                {house.name} ({guestUsers.filter(user => user.house_id === house.id).length})
              </option>
            ))}
            {guestUsers.some(isUnassigned) && (
              <option value="unassigned" data-testid="filter-house-unassigned">
                Sin asignar / casa borrada ({guestUsers.filter(isUnassigned).length})
              </option>
            )}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        </div>
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-3 xl:grid-cols-5" data-testid="users-activation-summary">
        {[
          { label: 'Total', value: users.length, icon: UsersRound, tone: 'users-summary-total' },
          { label: 'Activos', value: activeUsers.length, icon: UserRoundCheck, tone: 'users-summary-active' },
          { label: 'Inactivos', value: inactiveUsers.length, icon: UserRoundX, tone: 'users-summary-inactive' },
          { label: 'Activados', value: activatedUsers, icon: CheckCircle, tone: 'users-summary-activated' },
          { label: 'Sin ingresar', value: pendingUsers, icon: Clock, tone: 'users-summary-pending' }
        ].map(({ label, value, icon: Icon, tone }) => (
          <div key={label} className="admin-content-surface rounded-2xl border border-border bg-surface p-4 shadow-none">
            <div className="flex items-center gap-3">
              <span className={`users-summary-icon flex h-10 w-10 shrink-0 items-center justify-center rounded-xl ${tone}`}>
                <Icon className="h-5 w-5" />
              </span>
              <div>
                <p className="text-xl font-bold leading-none text-foreground">{value}</p>
                <p className="mt-1 text-xs text-muted-foreground">{label}</p>
              </div>
            </div>
          </div>
        ))}
      </div>

      {loading ? (
        <LoadingIndicator className="py-8" />
      ) : (
        <div className="admin-content-surface admin-users-surface rounded-2xl border border-border bg-surface shadow-none">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-5 py-4" data-testid="users-result-info">
            <div className="flex items-baseline gap-2">
              <h2 className="font-heading font-semibold text-foreground">{language === 'es' ? 'Usuarios' : 'Users'}</h2>
              <span className="text-xs text-muted-foreground">{filteredUsers.length} resultado{filteredUsers.length === 1 ? '' : 's'}</span>
            </div>
            {(houseFilter !== 'all' || search) && (
              <button
                type="button"
                onClick={() => { setHouseFilter('all'); setSearch(''); }}
                className="text-xs font-medium text-primary hover:underline"
                data-testid="users-clear-house-filter"
              >
                Limpiar filtros
              </button>
            )}
          </div>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[940px]">
                  <thead className="bg-muted/60">
                    <tr>
                      <th className="text-left px-5 py-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        <button
                          type="button"
                          onClick={() => toggleSort('name')}
                          className={`inline-flex items-center gap-1 hover:opacity-80 ${sort.field === 'name' ? 'users-sort-active' : ''}`}
                          data-testid="sort-name"
                        >
                          NOMBRE
                          {sort.field === 'name' && (sort.dir === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
                        </button>
                      </th>
                      <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        <button
                          type="button"
                          onClick={() => toggleSort('email')}
                          className={`inline-flex items-center gap-1 hover:opacity-80 ${sort.field === 'email' ? 'users-sort-active' : ''}`}
                          data-testid="sort-email"
                        >
                          CONTACTO
                          {sort.field === 'email' && (sort.dir === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)}
                        </button>
                      </th>
                      <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">CASA / HABITACIÓN</th>
                      <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
                        <button
                          type="button"
                          onClick={() => toggleSort('lease_end')}
                          className={`inline-flex items-center gap-1 hover:opacity-80 ${sort.field === 'lease_end' ? 'users-sort-active' : ''}`}
                          data-testid="sort-lease-end"
                          title="Ordenar por vencimiento del contrato"
                        >
                          CONTRATO
                          {sort.field === 'lease_end'
                            ? (sort.dir === 'asc' ? <ChevronUp className="w-3.5 h-3.5" /> : <ChevronDown className="w-3.5 h-3.5" />)
                            : <ChevronDown className="w-3.5 h-3.5 opacity-30" />}
                        </button>
                      </th>
                      <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">ESTADO</th>
                      <th className="text-left px-4 py-3 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">CUENTA</th>
                      <th className="px-5 py-3 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">ACCIONES</th>
                    </tr>
                  </thead>
                  <tbody>
                    {filteredUsers.map((user) => {
                      const house = houses.find(h => h.id === user.house_id);
                      const expired = isLeaseExpired(user.lease_end);
                      const activated = !!user.last_login_at;
                      const inactive = user.is_active === false;
                      return (
                        <tr key={user.id} className={`admin-users-row border-t border-border ${inactive ? 'bg-muted/20' : ''}`} data-testid={`user-row-${user.id}`}>
                          <td className="px-5 py-4">
                            <div className="flex items-center gap-3">
                              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-accent-peach text-xs font-bold text-primary">
                                {initialsFor(user.name)}
                              </span>
                              <div className="min-w-0">
                                <p className="max-w-[190px] truncate text-sm font-semibold text-foreground">{user.name}</p>
                                <p className="max-w-[190px] truncate text-xs text-muted-foreground">{user.email}</p>
                              </div>
                            </div>
                          </td>
                          <td className="px-4 py-4 text-sm">
                            <p className="text-foreground">{user.phone || '—'}</p>
                          </td>
                          <td className="px-4 py-4 text-sm">
                            <p className="font-medium text-foreground">{house ? house.name : 'Sin casa asignada'}</p>
                            <p className="mt-1 text-xs text-muted-foreground">{user.room_number ? `Hab. ${user.room_number}` : 'Sin habitación'}</p>
                          </td>
                          <td className="px-4 py-4 text-sm">
                            {user.lease_start && user.lease_end ? (
                              <div>
                                <p className="whitespace-nowrap text-foreground">
                                  {formatDateAR(user.lease_start, 'es-AR')} <span className="mx-1 text-muted-foreground">→</span> {formatDateAR(user.lease_end, 'es-AR')}
                                </p>
                                <span className={`mt-1 inline-flex rounded-full px-2 py-0.5 text-[11px] font-medium ${expired ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'}`}>
                                  {expired ? 'Vencido' : 'Vigente'}
                                </span>
                              </div>
                            ) : (
                              <span className="text-muted-foreground">Sin definir</span>
                            )}
                          </td>
                          <td className="px-4 py-4">
                            <span className={`users-status-pill rounded-full px-2.5 py-1 text-xs font-medium ${inactive ? 'users-status-inactive' : 'users-status-active'}`}>
                              {inactive ? <CircleX className="h-3.5 w-3.5" strokeWidth={2} /> : <CircleCheck className="h-3.5 w-3.5" strokeWidth={2} />}
                              {inactive ? 'Inactivo' : 'Activo'}
                            </span>
                            {user.role !== 'guest' && <p className="mt-1 text-[11px] text-muted-foreground">{user.role === 'admin' ? 'Admin' : 'House Manager'}</p>}
                          </td>
                          <td className="px-4 py-4" data-testid={`user-activation-${user.id}`}>
                            {activated ? (
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-foreground">
                                <span className="h-2 w-2 rounded-full bg-green-500" /> Activa
                              </span>
                            ) : (
                              <span className="inline-flex items-center gap-1.5 text-xs font-medium text-muted-foreground" title="El huésped aún no ingresó por primera vez">
                                <span className="h-2 w-2 rounded-full bg-slate-300" />
                                Sin ingresar
                              </span>
                            )}
                            {activated && user.last_login_at && (
                              <p className="text-[10px] text-muted-foreground mt-1">{formatDateTimeAR(user.last_login_at, 'es-AR')}</p>
                            )}
                          </td>
                          <td className="px-5 py-4 text-right">
                            <button
                              type="button"
                              onClick={(event) => openActionsMenu(event, user)}
                              className="inline-flex h-9 w-9 items-center justify-center rounded-lg border border-border bg-surface text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
                              aria-label={`Acciones para ${user.name}`}
                              aria-haspopup="menu"
                              aria-expanded={actionsMenu?.user?.id === user.id}
                              data-testid={`user-actions-${user.id}`}
                            >
                              <MoreHorizontal className="h-4 w-4" />
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                    {filteredUsers.length === 0 && (
                      <tr>
                        <td colSpan="7" className="px-5 py-12 text-center text-sm text-muted-foreground">
                          No hay huéspedes que coincidan con la búsqueda.
                        </td>
                      </tr>
                    )}
                  </tbody>
                </table>
          </div>
        </div>
      )}

      {actionsMenu && createPortal(
        <>
          <button
            type="button"
            className="fixed inset-0 z-[90] cursor-default"
            onClick={() => setActionsMenu(null)}
            aria-label="Cerrar menú de acciones"
          />
          <div
            role="menu"
            className="fixed z-[100] w-56 overflow-hidden rounded-xl border border-border bg-surface p-1.5 shadow-xl"
            style={{ top: actionsMenu.top, left: actionsMenu.left }}
          >
            <button
              type="button"
              role="menuitem"
              onClick={() => runMenuAction(() => onEdit(actionsMenu.user))}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted"
              data-testid={`edit-user-${actionsMenu.user.id}`}
            >
              <Edit2 className="h-4 w-4" /> Editar usuario
            </button>
            <button
              type="button"
              role="menuitem"
              onClick={() => runMenuAction(() => setShareAccessUser(actionsMenu.user))}
              className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted"
              data-testid={`share-access-${actionsMenu.user.id}`}
            >
              <Copy className="h-4 w-4" /> Gestionar acceso
            </button>
            {(currentUser?.role === 'admin' || (currentUser?.role === 'house_manager' && actionsMenu.user.role === 'guest')) && (
              <button
                type="button"
                role="menuitem"
                onClick={() => runMenuAction(() => setConfirmReset(actionsMenu.user))}
                className="flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm hover:bg-muted"
                data-testid={`reset-password-${actionsMenu.user.id}`}
              >
                <KeyRound className="h-4 w-4" /> Resetear contraseña
              </button>
            )}
            {actionsMenu.user.role === 'guest' && (
              <>
                <div className="my-1 border-t border-border" />
                <button
                  type="button"
                  role="menuitem"
                  onClick={() => runMenuAction(() => handleToggleActive(actionsMenu.user))}
                  className={`flex w-full items-center gap-2.5 rounded-lg px-3 py-2 text-left text-sm ${actionsMenu.user.is_active === false ? 'text-green-700 hover:bg-green-50' : 'text-red-700 hover:bg-red-50'}`}
                  data-testid={`${actionsMenu.user.is_active === false ? 'reactivate' : 'deactivate'}-user-${actionsMenu.user.id}`}
                >
                  {actionsMenu.user.is_active === false ? <UserRoundCheck className="h-4 w-4" /> : <UserRoundX className="h-4 w-4" />}
                  {actionsMenu.user.is_active === false ? 'Reactivar' : 'Dar de baja'}
                </button>
              </>
            )}
          </div>
        </>,
        document.body
      )}

      {showNewUser && createPortal(
        <NewUserModal 
          onClose={() => setShowNewUser(false)} 
          onSubmit={async (data) => {
            
            
            await onCreateUser(data);
          }}
          houses={houses}
          allowedHouses={isHM ? houses.filter(h => (currentUser?.assigned_house_ids || []).includes(h.id)) : houses}
          restrictToGuest={isHM}
        />,
        document.body
      )}

      {confirmReset && createPortal(
        <div className="fixed inset-0 bg-black/70 z-[100] modal-backdrop users-reset-modal" data-testid="reset-confirm-modal">
          <style>{`
            [data-testid="reset-confirm-modal"] .users-reset-card {
              background: var(--surface);
              border: 1px solid var(--border);
              border-radius: 16px;
              box-shadow: 0 18px 48px rgba(15, 23, 42, 0.16);
            }
            :root[data-theme="dark"] [data-testid="reset-confirm-modal"] .users-reset-card {
              background: var(--surface);
              border-color: var(--border);
              box-shadow: none;
            }
            [data-testid="reset-confirm-modal"] .users-reset-user {
              background: var(--muted);
              border: 1px solid var(--border);
              border-radius: 10px;
            }
            [data-testid="reset-confirm-modal"] .users-reset-cancel,
            [data-testid="reset-confirm-modal"] .users-reset-submit {
              border-radius: 10px;
              box-shadow: none !important;
              font-weight: 600;
            }
            [data-testid="reset-confirm-modal"] .users-reset-cancel {
              background: var(--surface);
              border: 1px solid var(--border);
              color: var(--fg);
            }
            [data-testid="reset-confirm-modal"] .users-reset-submit {
              background: #111111;
              border: 1px solid #111111;
              color: #ffffff;
            }
            :root[data-theme="dark"] [data-testid="reset-confirm-modal"] .users-reset-submit {
              background: #ffffff;
              border-color: #ffffff;
              color: #111111;
            }
          `}</style>
          <div className="users-reset-card bg-surface border-2 border-foreground rounded-xl w-full max-w-md modal-card p-6 space-y-4">
            <h2 className="font-heading font-bold text-xl">Resetear contraseña</h2>
            <p className="text-sm text-muted-foreground">
              Se generará una contraseña temporal de 10 caracteres para:
            </p>
            <div className="users-reset-user p-3">
              <p className="font-medium text-foreground">{confirmReset.name}</p>
              <p className="text-sm text-muted-foreground">{confirmReset.email}</p>
            </div>
            <div className="flex gap-2 justify-end pt-2">
              <button
                onClick={() => setConfirmReset(null)}
                disabled={resetting}
                className="users-reset-cancel px-4 py-2 hover:bg-muted text-sm"
              >
                Cancelar
              </button>
              <button
                onClick={handleConfirmReset}
                disabled={resetting}
                className="users-reset-submit px-4 py-2 text-sm flex items-center gap-2"
                data-testid="confirm-reset-button"
              >
                {resetting ? '...' : 'Generar contraseña'}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}

      {resetResult && (
        <ResetResultModal
          result={resetResult}
          onClose={() => setResetResult(null)}
        />
      )}

      {shareAccessUser && createPortal(
        <ShareAccessModal
          user={shareAccessUser}
          onClose={() => setShareAccessUser(null)}
          onResetPassword={() => { setShareAccessUser(null); setConfirmReset(shareAccessUser); }}
          currentUser={currentUser}
        />,
        document.body
      )}
    </div>
  );
}

function ShareAccessModal({ user, onClose, onResetPassword, currentUser }) {
  const [copied, setCopied] = useState(null); 
  const appUrl = (typeof window !== 'undefined' ? window.location.origin : 'https://www.wannacolive.app');
  const message = `¡Hola ${user.name}! 👋\n\nTe compartimos tu acceso a WannaColive:\n\n🌐 Ingresá en: ${appUrl}\n📧 Email: ${user.email}\n\nSi olvidaste tu contraseña, podés pedirle al admin que te resetee una nueva.`;

  const copy = async (text, tag) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopied(tag);
      setTimeout(() => setCopied(null), 1500);
    } catch (e) {
      
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(tag);
      setTimeout(() => setCopied(null), 1500);
    }
  };

  const openWhatsApp = () => {
    
    const raw = (user.phone || '').trim();
    const digits = raw.replace(/[^\d]/g, '');
    if (!digits) {
      alert('Este huésped no tiene número de WhatsApp cargado. Editá su perfil para agregarlo.');
      return;
    }
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(message)}`;
    window.open(url, '_blank');
  };

  const canReset = currentUser?.role === 'admin' || (currentUser?.role === 'house_manager' && user.role === 'guest');

  return (
    <div className="fixed inset-0 h-screen w-screen bg-black/70 z-[100] modal-backdrop" data-testid="share-access-modal">
      <style>{`
        [data-testid="share-access-modal"] {
          position: fixed;
          inset: 0;
          width: 100vw;
          height: 100vh;
          z-index: 100;
        }
        [data-testid="share-access-modal"] .users-share-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 16px;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.16);
        }
        :root[data-theme="dark"] [data-testid="share-access-modal"] .users-share-card {
          background: var(--surface);
          border-color: var(--border);
          box-shadow: none;
        }
        [data-testid="share-access-modal"] .input-brutal {
          border-width: 1px;
          border-color: var(--border);
          border-radius: 10px;
          background: var(--surface);
          box-shadow: none;
        }
        [data-testid="share-access-modal"] .users-share-copy,
        [data-testid="share-access-modal"] .users-share-message-copy,
        [data-testid="share-access-modal"] .users-share-reset,
        [data-testid="share-access-modal"] .users-share-close {
          border: 1px solid var(--border);
          box-shadow: none;
        }
        [data-testid="share-access-modal"] .users-share-copy,
        [data-testid="share-access-modal"] .users-share-message-copy,
        [data-testid="share-access-modal"] .users-share-reset {
          background: var(--surface);
          color: var(--fg);
        }
        [data-testid="share-access-modal"] .users-share-copy:hover,
        [data-testid="share-access-modal"] .users-share-message-copy:hover,
        [data-testid="share-access-modal"] .users-share-reset:hover,
        [data-testid="share-access-modal"] .users-share-close:hover {
          background: var(--muted);
        }
        [data-testid="share-access-modal"] .users-share-message {
          border: 1px solid var(--border);
          background: var(--muted);
        }
        [data-testid="share-access-modal"] .users-share-whatsapp {
          border: 0;
          box-shadow: none;
        }
      `}</style>
      <div className="users-share-card relative z-[101] bg-surface border-2 border-foreground rounded-xl w-full max-w-md modal-card">
        <div className="p-6 space-y-4">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="font-heading font-bold text-xl">Compartir acceso</h2>
              <p className="text-sm text-muted-foreground">{user.name}</p>
            </div>
            <button onClick={onClose} className="users-share-close rounded-lg p-1 text-muted-foreground" aria-label="Close"><X className="w-5 h-5" /></button>
          </div>

          <div className="space-y-3">
            {}
            <div>
              <label className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">Link de inicio</label>
              <div className="mt-1 flex gap-2">
                <input value={appUrl} readOnly className="flex-1 input-brutal px-3 py-2 text-sm" data-testid="share-link-input" />
                <button
                  onClick={() => copy(appUrl, 'link')}
                  className="users-share-copy rounded-lg px-3 py-2 flex items-center gap-1 text-xs"
                  data-testid="share-link-copy"
                >
                  {copied === 'link' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            {}
            <div>
              <label className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground">Email del huésped</label>
              <div className="mt-1 flex gap-2">
                <input value={user.email} readOnly className="flex-1 input-brutal px-3 py-2 text-sm" data-testid="share-email-input" />
                <button
                  onClick={() => copy(user.email, 'email')}
                  className="users-share-copy rounded-lg px-3 py-2 flex items-center gap-1 text-xs"
                  data-testid="share-email-copy"
                >
                  {copied === 'email' ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                </button>
              </div>
            </div>

            <div className="users-share-message rounded-lg p-3 text-xs leading-relaxed whitespace-pre-line" data-testid="share-message-preview">
              {message}
            </div>
            <button
              onClick={() => copy(message, 'full')}
              className="users-share-message-copy w-full rounded-lg px-3 py-2 flex items-center justify-center gap-2 text-sm font-semibold"
              data-testid="share-message-copy"
            >
              {copied === 'full' ? <><Check className="w-4 h-4" /> Copiado</> : <><Copy className="w-4 h-4" /> Copiar mensaje completo</>}
            </button>
          </div>

          <div className={`grid ${canReset ? 'grid-cols-2' : 'grid-cols-1'} gap-2 pt-2 border-t border-border`}>
            <button
              onClick={openWhatsApp}
              className="users-share-whatsapp px-3 py-2.5 rounded-lg bg-[#25D366] text-white font-semibold text-sm flex items-center justify-center gap-2 hover:opacity-90 transition-opacity"
              data-testid="share-whatsapp"
            >
              WhatsApp
            </button>
            {canReset && (
              <button
                onClick={onResetPassword}
                className="users-share-reset px-3 py-2.5 rounded-lg font-semibold text-sm flex items-center justify-center gap-2 transition-colors"
                data-testid="share-reset-password"
              >
                <KeyRound className="w-4 h-4" /> Resetear clave
              </button>
            )}
          </div>

          <p className="text-[10px] text-muted-foreground text-center">
            La contraseña original no se almacena por seguridad. Si el huésped la olvidó, usá &ldquo;Resetear clave&rdquo; para generar una temporal.
          </p>
        </div>
      </div>
    </div>
  );
}

function ResetResultModal({ result, onClose }) {
  const [copied, setCopied] = useState(false);

  const copyPassword = async () => {
    const value = result.temporary_password;
    let didCopy = false;

    if (navigator.clipboard?.writeText) {
      try {
        await navigator.clipboard.writeText(value);
        didCopy = true;
      } catch {
        didCopy = false;
      }
    }

    if (!didCopy && document.execCommand) {
      const textarea = document.createElement('textarea');
      const activeElement = document.activeElement;
      const selection = document.getSelection();
      const ranges = selection ? Array.from({ length: selection.rangeCount }, (_, index) => selection.getRangeAt(index)) : [];

      textarea.value = value;
      textarea.setAttribute('readonly', '');
      textarea.style.position = 'fixed';
      textarea.style.top = '0';
      textarea.style.left = '-9999px';
      document.body.appendChild(textarea);
      textarea.focus();
      textarea.select();

      try {
        didCopy = document.execCommand('copy');
      } catch {
        didCopy = false;
      }

      document.body.removeChild(textarea);
      if (activeElement instanceof HTMLElement) activeElement.focus();
      if (selection && ranges.length) {
        selection.removeAllRanges();
        ranges.forEach((range) => selection.addRange(range));
      }
    }

    if (didCopy) {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  return createPortal((
    <div className="fixed inset-0 z-[100] modal-backdrop bg-black/70" data-testid="reset-result-modal">
      <style>{`
        [data-testid="reset-result-modal"] .reset-result-modal__card {
          box-shadow: 0 12px 32px rgba(15, 23, 42, 0.14);
        }
        :root[data-theme="dark"] [data-testid="reset-result-modal"] .reset-result-modal__card {
          box-shadow: none;
        }
      `}</style>
      <div className="reset-result-modal__card w-full max-w-md overflow-hidden rounded-2xl border border-border bg-surface">
        <div className="p-6">
          <div className="mb-5 flex items-center gap-3">
            <div className="flex h-11 w-11 items-center justify-center rounded-xl border border-accent-mint/30 bg-accent-mint/15 text-accent-mint">
              <Check className="h-5 w-5" />
            </div>
            <h2 className="font-heading font-bold text-xl">Contraseña generada</h2>
          </div>
          <p className="mb-5 text-sm leading-relaxed text-muted-foreground">
            Compartí esta contraseña temporal con <strong className="text-foreground">{result.user.name}</strong>. La pedirá la próxima vez que ingrese y luego podrá elegir una nueva.
          </p>
          <div className="mb-4 rounded-xl border border-border bg-muted p-4">
            <p className="text-xs text-muted-foreground mb-1">Contraseña temporal</p>
            <div className="flex items-center justify-between gap-2">
              <code className="min-w-0 break-all font-mono text-2xl font-bold tracking-wider text-foreground select-all" data-testid="temp-password-text">
                {result.temporary_password}
              </code>
              <button
                onClick={copyPassword}
                className="flex-shrink-0 rounded-lg border border-border bg-surface p-2 text-foreground transition-colors hover:bg-surface-hover"
                data-testid="copy-temp-password"
              >
                {copied ? <Check className="w-5 h-5 text-accent-mint" /> : <Copy className="w-5 h-5" />}
              </button>
            </div>
          </div>
          <p className="mb-6 text-xs leading-relaxed text-muted-foreground">
            ⚠ Esta contraseña no se vuelve a mostrar. Copiala antes de cerrar.
          </p>
          <button
            onClick={onClose}
            className="w-full rounded-xl bg-foreground py-3 font-heading text-sm font-semibold text-background transition-opacity hover:opacity-90"
            data-testid="close-reset-result"
          >
            Listo
          </button>
        </div>
      </div>
    </div>
  ), document.body);
}

function NewUserModal({ onClose, onSubmit, houses, allowedHouses, restrictToGuest = false }) {
  
  const selectableHouses = allowedHouses || houses;
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    password: '',
    phone: '',
    house_id: '',
    room_number: '',
    room_type: 'shared',
    lease_start: '',
    lease_end: '',
    role: 'guest',
    assigned_house_ids: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [created, setCreated] = useState(null); 

  const isStaff = formData.role === 'admin' || formData.role === 'house_manager';

  const toggleAssignedHouse = (id) => {
    setFormData((p) => {
      const set = new Set(p.assigned_house_ids);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...p, assigned_house_ids: Array.from(set) };
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (formData.role === 'guest' && !formData.phone.trim()) {
      setError('El celular es obligatorio. Incluí el código de país (ej: +5491156789012).');
      return;
    }
    if (formData.role === 'house_manager' && formData.assigned_house_ids.length === 0) {
      setError('El house manager debe tener al menos una casa asignada');
      return;
    }
    setLoading(true);
    try {
      
      const payload = {
        name: formData.name,
        email: formData.email,
        password: formData.password,
        role: formData.role,
      };
      if (formData.role === 'guest') {
        payload.house_id = formData.house_id;
        payload.room_number = formData.room_number;
        payload.room_type = formData.room_type;
        payload.lease_start = formData.lease_start;
        payload.lease_end = formData.lease_end;
        payload.phone = formData.phone.trim();
      } else {
        payload.assigned_house_ids = formData.assigned_house_ids;
      }
      await onSubmit(payload);
      
      if (formData.role === 'guest') {
        setCreated({
          name: formData.name,
          email: formData.email,
          password: formData.password,
          phone: formData.phone.trim(),
        });
        setLoading(false);
      } else {
        onClose();
      }
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear usuario');
      setLoading(false);
    }
  };

  
  const generatePassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789';
    let password = '';
    for (let i = 0; i < 8; i++) {
      password += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setFormData({ ...formData, password });
  };

  
  
  
  const buildAccessMessage = (c) =>
    `¡Hola ${c.name.split(' ')[0]}! 👋\n` +
    `Te paso los datos de acceso a WannaColive:\n\n` +
    `🔗 https://www.wannacolive.app\n` +
    `👤 Usuario: ${c.email}\n` +
    `🔑 Contraseña temporal: ${c.password}\n\n` +
    `Cualquier cosita avisame 😊`;
  const openWhatsApp = () => {
    if (!created) return;
    const digits = (created.phone || '').replace(/\D/g, '');
    const url = `https://wa.me/${digits}?text=${encodeURIComponent(buildAccessMessage(created))}`;
    window.open(url, '_blank', 'noopener,noreferrer');
  };
  const [copied, setCopied] = useState(false);
  const copyAccessInfo = async () => {
    if (!created) return;
    const text = buildAccessMessage(created);
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text);
      } else {
        
        const ta = document.createElement('textarea');
        ta.value = text;
        ta.style.position = 'fixed';
        ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.focus();
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 2500);
    } catch (e) {
      
      console.error('Copy failed', e);
    }
  };

  
  if (created) {
    return (
      <div className="fixed inset-0 bg-black/50 z-50 modal-backdrop" data-testid="user-created-modal">
        <div className="bg-surface border-2 border-foreground rounded-xl w-full max-w-md overflow-y-auto modal-card">
          <div className="p-6">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-12 h-12 rounded-full bg-accent-mint flex items-center justify-center text-2xl border-2 border-foreground">
                ✓
              </div>
              <div>
                <h2 className="font-heading font-bold text-xl">Usuario creado</h2>
                <p className="text-xs text-muted-foreground">Ahora podés compartir los datos de acceso</p>
              </div>
            </div>

            <div className="p-4 rounded-lg border-2 border-foreground bg-background space-y-2 mb-5">
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Nombre</span>
                <span className="font-medium">{created.name}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Email</span>
                <span className="font-medium break-all">{created.email}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Contraseña</span>
                <span className="font-mono font-semibold">{created.password}</span>
              </div>
              <div className="flex justify-between text-sm">
                <span className="text-muted-foreground">Celular</span>
                <span className="font-medium">{created.phone}</span>
              </div>
            </div>

            <button
              type="button"
              onClick={openWhatsApp}
              className="w-full py-3 rounded-lg border-2 border-foreground bg-[#25D366] text-white font-semibold flex items-center justify-center gap-2 hover:shadow-brutal-sm transition-all mb-3"
              data-testid="share-whatsapp-button"
            >
              <MessageCircle className="w-5 h-5" />
              Compartir por WhatsApp
            </button>

            <button
              type="button"
              onClick={copyAccessInfo}
              className="w-full py-3 rounded-lg border-2 border-foreground bg-surface text-foreground font-semibold flex items-center justify-center gap-2 hover:shadow-brutal-sm transition-all mb-3"
              data-testid="copy-access-button"
            >
              {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
              {copied ? '¡Copiado al portapapeles!' : 'Copiar info de acceso'}
            </button>

            <button
              type="button"
              onClick={onClose}
              className="w-full py-3 border-2 border-foreground rounded-lg font-medium hover:bg-muted transition-colors"
              data-testid="close-created-modal"
            >
              Cerrar
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 h-screen w-screen bg-black/50 z-[100] modal-backdrop users-new-modal" data-testid="new-user-modal">
      <style>{`
        [data-testid="new-user-modal"] {
          position: fixed;
          inset: 0;
          width: 100vw;
          height: 100vh;
          z-index: 100;
        }
        [data-testid="new-user-modal"] .users-new-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 16px;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.16);
        }
        :root[data-theme="dark"] [data-testid="new-user-modal"] .users-new-card {
          background: var(--surface);
          border-color: var(--border);
          box-shadow: none;
        }
        [data-testid="new-user-modal"] .input-brutal {
          border-width: 1px;
          border-color: var(--border);
          border-radius: 10px;
          background: var(--surface);
          box-shadow: none;
        }
        [data-testid="new-user-modal"] .input-brutal:focus {
          border-color: var(--primary);
          box-shadow: 0 0 0 3px color-mix(in srgb, var(--primary) 18%, transparent);
        }
        [data-testid="new-user-modal"] .border-2 {
          border-width: 1px;
        }
        [data-testid="new-user-modal"] .shadow-brutal-sm {
          box-shadow: none;
        }
        [data-testid="new-user-modal"] .users-new-modal-cancel,
        [data-testid="new-user-modal"] .users-new-modal-submit {
          border-radius: 10px;
          box-shadow: none !important;
          font-weight: 600;
        }
        [data-testid="new-user-modal"] .users-new-modal-cancel {
          border: 1px solid var(--border);
          background: var(--surface);
          color: var(--fg);
        }
        [data-testid="new-user-modal"] .users-new-modal-submit {
          border: 1px solid #111111 !important;
          background: #111111 !important;
          color: #ffffff !important;
          transform: none;
        }
        [data-testid="new-user-modal"] .users-new-modal-submit:hover {
          border-color: #111111 !important;
          background: #111111 !important;
          color: #ffffff !important;
          box-shadow: none !important;
          transform: none;
        }
        :root[data-theme="dark"] [data-testid="new-user-modal"] .users-new-modal-submit {
          border-color: #ffffff !important;
          background: #ffffff !important;
          color: #111111 !important;
        }
        :root[data-theme="dark"] [data-testid="new-user-modal"] .users-new-modal-submit:hover {
          border-color: #ffffff !important;
          background: #ffffff !important;
          color: #111111 !important;
        }
        [data-testid="new-user-modal"] .users-create-submit,
        [data-testid="new-user-modal"] .users-create-submit:hover,
        [data-testid="new-user-modal"] .users-create-submit:focus,
        [data-testid="new-user-modal"] .users-create-submit:focus-visible,
        [data-testid="new-user-modal"] .users-create-submit:active {
          background: #111111 !important;
          color: #ffffff !important;
          border: 0 !important;
          box-shadow: none !important;
          outline: none;
          transform: none !important;
        }
        :root[data-theme="dark"] [data-testid="new-user-modal"] .users-create-submit,
        :root[data-theme="dark"] [data-testid="new-user-modal"] .users-create-submit:hover,
        :root[data-theme="dark"] [data-testid="new-user-modal"] .users-create-submit:focus,
        :root[data-theme="dark"] [data-testid="new-user-modal"] .users-create-submit:focus-visible,
        :root[data-theme="dark"] [data-testid="new-user-modal"] .users-create-submit:active {
          background: #ffffff !important;
          color: #111111 !important;
          border: 0 !important;
          box-shadow: none !important;
          outline: none;
          transform: none !important;
        }
        :root[data-theme="light"] [data-testid="new-user-modal"] .users-role-selected,
        :root[data-theme="light"] [data-testid="new-user-modal"] .users-room-selected {
          background: #111111 !important;
          color: #ffffff !important;
          border-color: #111111 !important;
        }
        :root[data-theme="dark"] [data-testid="new-user-modal"] .users-role-selected,
        :root[data-theme="dark"] [data-testid="new-user-modal"] .users-room-selected {
          background: #ffffff !important;
          color: #111111 !important;
          border-color: #ffffff !important;
          box-shadow: none !important;
        }
        [data-testid="new-user-modal"] .users-generate-password,
        [data-testid="new-user-modal"] .users-generate-password:hover,
        [data-testid="new-user-modal"] .users-generate-password:focus,
        [data-testid="new-user-modal"] .users-generate-password:focus-visible,
        [data-testid="new-user-modal"] .users-generate-password:active {
          background: #111111 !important;
          color: #ffffff !important;
          border-color: #111111 !important;
          box-shadow: none !important;
          transform: none !important;
          outline: none;
        }
        :root[data-theme="dark"] [data-testid="new-user-modal"] .users-generate-password,
        :root[data-theme="dark"] [data-testid="new-user-modal"] .users-generate-password:hover,
        :root[data-theme="dark"] [data-testid="new-user-modal"] .users-generate-password:focus,
        :root[data-theme="dark"] [data-testid="new-user-modal"] .users-generate-password:focus-visible,
        :root[data-theme="dark"] [data-testid="new-user-modal"] .users-generate-password:active {
          background: #ffffff !important;
          color: #111111 !important;
          border-color: #ffffff !important;
          box-shadow: none !important;
          transform: none !important;
          outline: none;
        }
      `}</style>
      <div className="users-new-card relative z-[101] bg-surface border-2 border-foreground rounded-xl w-full max-w-lg overflow-y-auto modal-card">
        <div className="p-6">
          <h2 className="font-heading font-bold text-xl mb-6">Nuevo Usuario</h2>
          
          {error && (
            <div className="mb-4 p-3 bg-accent-peach border-2 border-foreground rounded-lg text-sm">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!restrictToGuest && (
              <div>
                <label className="block text-sm font-medium mb-2">Rol del usuario *</label>
                <div className="grid grid-cols-3 gap-2" data-testid="role-selector">
                  {[
                    { value: 'guest', label: 'Huésped', desc: 'Vive en una casa' },
                    { value: 'house_manager', label: 'House Manager', desc: 'Gestiona casas' },
                    { value: 'admin', label: 'Admin', desc: 'Acceso ampliado' },
                  ].map((opt) => (
                    <button
                      key={opt.value}
                      type="button"
                      onClick={() => setFormData((p) => ({ ...p, role: opt.value }))}
                      className={`p-3 border-2 border-foreground rounded-lg text-left transition-all ${
                        formData.role === opt.value
                          ? 'users-role-selected shadow-brutal-sm'
                          : 'bg-surface hover:bg-muted'
                      }`}
                      data-testid={`role-${opt.value}`}
                    >
                      <p className="font-semibold text-sm">{opt.label}</p>
                      <p className="text-xs opacity-80">{opt.desc}</p>
                    </button>
                  ))}
                </div>
              </div>
            )}
            <div>
              <label className="block text-sm font-medium mb-2">Nombre completo *</label>
              <input
                type="text"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                className="input-brutal w-full px-4 py-3"
                placeholder="Juan Pérez"
                required
                data-testid="user-name-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Email *</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-brutal w-full px-4 py-3"
                placeholder="juan@email.com"
                required
                data-testid="user-email-input"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-2">Contraseña temporal *</label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="input-brutal flex-1 px-4 py-3"
                  placeholder="Contraseña"
                  required
                  minLength={6}
                  data-testid="user-password-input"
                />
                <button
                  type="button"
                  onClick={generatePassword}
                  className="users-generate-password px-4 py-3 border-2 border-foreground rounded-lg bg-accent-yellow hover:shadow-brutal-sm transition-all text-sm font-medium"
                >
                  Generar
                </button>
              </div>
              <p className="text-xs text-muted-foreground mt-1">El huésped deberá cambiarla al ingresar</p>
            </div>
            {formData.role === 'guest' ? (
              <>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Casa *</label>
                    <select
                      value={formData.house_id}
                      onChange={(e) => setFormData({ ...formData, house_id: e.target.value })}
                      className="input-brutal w-full px-4 py-3"
                      required
                      data-testid="user-house-select"
                    >
                      <option value="">Seleccionar...</option>
                      {selectableHouses.map((house) => (
                        <option key={house.id} value={house.id}>{house.name}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Habitación</label>
                    <input
                      type="text"
                      value={formData.room_number}
                      onChange={(e) => setFormData({ ...formData, room_number: e.target.value })}
                      className="input-brutal w-full px-4 py-3"
                      placeholder="Ej: 101"
                      data-testid="user-room-input"
                    />
                  </div>
                </div>
                {formData.house_id && (
                  <HouseRoomsAvailability
                    houseId={formData.house_id}
                    currentRoomNumber={formData.room_number}
                    currentRoomType={formData.room_type}
                    onSuggestRoom={(rn) => setFormData((p) => ({ ...p, room_number: rn }))}
                  />
                )}
                <div>
                  <label className="block text-sm font-medium mb-2">Tipo de habitación *</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, room_type: 'shared' })}
                      className={`p-3 border-2 border-foreground rounded-lg text-sm font-medium transition-all ${
                        formData.room_type === 'shared'
                          ? 'users-room-selected shadow-brutal-sm'
                          : 'bg-surface hover:bg-muted'
                      }`}
                      data-testid="room-type-shared"
                    >
                      Compartida
                    </button>
                    <button
                      type="button"
                      onClick={() => setFormData({ ...formData, room_type: 'exclusive' })}
                      className={`p-3 border-2 border-foreground rounded-lg text-sm font-medium transition-all ${
                        formData.room_type === 'exclusive'
                          ? 'users-room-selected shadow-brutal-sm'
                          : 'bg-surface hover:bg-muted'
                      }`}
                      data-testid="room-type-exclusive"
                    >
                      Privada
                    </button>
                  </div>
                  <p className="text-xs text-muted-foreground mt-1">Las habitaciones privadas pueden solicitar visitas</p>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium mb-2">Inicio contrato *</label>
                    <input
                      type="date"
                      value={formData.lease_start}
                      onChange={(e) => setFormData({ ...formData, lease_start: e.target.value })}
                      className="input-brutal w-full px-4 py-3"
                      required
                      data-testid="user-lease-start-input"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-2">Fin contrato *</label>
                    <input
                      type="date"
                      value={formData.lease_end}
                      onChange={(e) => setFormData({ ...formData, lease_end: e.target.value })}
                      className="input-brutal w-full px-4 py-3"
                      required
                      data-testid="user-lease-end-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Celular (WhatsApp) *</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="input-brutal w-full px-4 py-3"
                    placeholder="+5491156789012"
                    required
                    data-testid="user-phone-input"
                  />
                  <p className="text-xs text-muted-foreground mt-1">
                    Ingresá el número completo con código de país (ej: +54 para Argentina, +1 para EE.UU., +34 para España).
                  </p>
                </div>
              </>
            ) : (
              <div>
                <label className="block text-sm font-medium mb-2">
                  Casas asignadas {formData.role === 'admin' ? '(opcional)' : '*'}
                </label>
                <p className="text-xs text-muted-foreground mb-2">
                  {formData.role === 'admin'
                    ? 'Si no asignás casas, este admin tendrá acceso global a todas.'
                    : 'El house manager solo verá las casas que selecciones.'}
                </p>
                <div className="space-y-2 max-h-56 overflow-y-auto p-3 border-2 border-foreground rounded-lg bg-background" data-testid="assigned-houses-picker">
                  {houses.map((h) => (
                    <label key={h.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.assigned_house_ids.includes(h.id)}
                        onChange={() => toggleAssignedHouse(h.id)}
                        className="w-4 h-4"
                        data-testid={`assigned-house-${h.id}`}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{h.name}</p>
                        <p className="text-xs text-muted-foreground">{h.city || h.address}</p>
                      </div>
                    </label>
                  ))}
                  {houses.length === 0 && (
                    <p className="text-sm text-muted-foreground text-center py-2">No hay casas creadas todavía.</p>
                  )}
                </div>
              </div>
            )}
            <div className="flex gap-3 pt-4">
              <button
                type="button"
                onClick={onClose}
                className="users-new-modal-cancel flex-1 py-3 hover:bg-muted transition-colors"
                data-testid="cancel-user-button"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="users-create-submit btn-brutal flex-1 py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="submit-user-button"
              >
                <UserRoundPlus className="w-4 h-4" />
                {loading ? 'Creando...' : 'Crear usuario'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function NewAnnouncementModal({ onClose, onSubmit, houses, users, allowedHouses }) {
  
  const isRestricted = !!allowedHouses;
  const selectableHouses = allowedHouses || houses;
  
  const eligibleGuests = (users || []).filter(u => u.role === 'guest' && u.is_active !== false &&
    (!isRestricted || (allowedHouses || []).some(h => h.id === u.house_id)));

  const [formData, setFormData] = useState({
    title: '',
    content: '',
    priority: 'info',
    target_houses: [],
    target_user_ids: [],
  });
  
  const [scope, setScope] = useState(isRestricted ? 'specific' : 'all');
  const [userSearch, setUserSearch] = useState('');
  const [loading, setLoading] = useState(false);

  const toggleHouse = (id) => {
    setFormData(p => ({
      ...p,
      target_houses: p.target_houses.includes(id) ? p.target_houses.filter(h => h !== id) : [...p.target_houses, id],
    }));
  };
  const toggleUser = (id) => {
    setFormData(p => ({
      ...p,
      target_user_ids: p.target_user_ids.includes(id) ? p.target_user_ids.filter(u => u !== id) : [...p.target_user_ids, id],
    }));
  };

  const filteredGuests = userSearch.trim()
    ? eligibleGuests.filter(g => (g.name || '').toLowerCase().includes(userSearch.toLowerCase()) || (g.email || '').toLowerCase().includes(userSearch.toLowerCase()))
    : eligibleGuests;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (scope === 'specific' && formData.target_houses.length === 0) {
      alert('Seleccioná al menos una casa.');
      return;
    }
    if (scope === 'users' && formData.target_user_ids.length === 0) {
      alert('Seleccioná al menos un huésped.');
      return;
    }
    setLoading(true);
    await onSubmit({
      title: formData.title,
      content: formData.content,
      priority: formData.priority,
      target_houses: scope === 'specific' ? formData.target_houses : [],
      target_user_ids: scope === 'users' ? formData.target_user_ids : [],
    });
    setLoading(false);
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 backdrop-blur-[2px] sm:p-6"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
      data-testid="new-announcement-modal"
    >
      <style>{`
        :root[data-theme="light"] [data-testid="new-announcement-modal"] .announcement-scope-option.bg-foreground,
        :root[data-theme="light"] [data-testid="new-announcement-modal"] .announcement-scope-option.bg-foreground:hover,
        :root[data-theme="light"] [data-testid="new-announcement-modal"] .announcement-scope-option.bg-foreground:focus,
        :root[data-theme="light"] [data-testid="new-announcement-modal"] .announcement-scope-option.bg-foreground:active {
          background-color: #111111 !important;
          color: #ffffff !important;
          border-color: #111111 !important;
          box-shadow: none !important;
        }
        [data-testid="new-announcement-modal"] [data-testid="submit-announcement-button"],
        [data-testid="new-announcement-modal"] [data-testid="submit-announcement-button"]:hover,
        [data-testid="new-announcement-modal"] [data-testid="submit-announcement-button"]:active {
          background-color: #111111 !important;
          color: #ffffff !important;
          border-color: #111111 !important;
          box-shadow: none !important;
        }
        :root[data-theme="dark"] [data-testid="new-announcement-modal"] [data-testid="submit-announcement-button"],
        :root[data-theme="dark"] [data-testid="new-announcement-modal"] [data-testid="submit-announcement-button"]:hover,
        :root[data-theme="dark"] [data-testid="new-announcement-modal"] [data-testid="submit-announcement-button"]:active {
          background-color: #ffffff !important;
          color: #111111 !important;
          border-color: var(--border) !important;
        }
      `}</style>
      <div className="max-h-[calc(100vh-1.5rem)] w-full max-w-3xl overflow-y-auto overscroll-contain rounded-2xl border border-border bg-surface shadow-2xl sm:max-h-[calc(100vh-3rem)]">
        <div className="p-5 sm:p-8">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground">Nuevo comunicado</h2>
              <p className="mt-1 text-sm text-muted-foreground">Compartí una novedad con la comunidad.</p>
            </div>
            <button type="button" onClick={onClose} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-muted hover:text-foreground" aria-label="Cerrar">
              <X className="h-5 w-5" />
            </button>
          </div>
          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-semibold text-foreground">Título</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="h-11 w-full rounded-xl border border-border bg-background px-4 text-sm text-foreground outline-none transition placeholder:text-muted-foreground focus:border-foreground focus:ring-0"
                placeholder="Título del comunicado"
                required
                data-testid="announcement-title-input"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-foreground">Contenido</label>
              <textarea
                value={formData.content}
                onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                className="min-h-[150px] w-full resize-none rounded-xl border border-border bg-background px-4 py-3 text-sm leading-6 text-foreground outline-none transition placeholder:text-muted-foreground focus:border-foreground focus:ring-0"
                placeholder="Contenido del comunicado"
                required
                data-testid="announcement-content-input"
              />
            </div>
            <div>
              <label className="mb-2 block text-sm font-semibold text-foreground">Prioridad</label>
              <div className="flex flex-wrap gap-2">
                {[
                  { value: 'info', label: 'Información' },
                  { value: 'important', label: 'Importante' },
                  { value: 'maintenance', label: 'Mantenimiento' },
                  { value: 'event', label: 'Evento' },
                  { value: 'urgent', label: 'Urgente' },
                  { value: 'multa', label: 'Multa' },
                ].map((priority) => (
                  <button
                    key={priority.value}
                    type="button"
                    onClick={() => setFormData({ ...formData, priority: priority.value })}
                    className={`flex min-h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl border px-3 py-2 text-xs font-semibold transition ${
                      formData.priority === priority.value
                        ? `${announcementPriorityMeta[priority.value].badge} ring-2 ring-current/10`
                        : 'border-border bg-surface text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                    data-testid={`priority-${priority.value}`}
                  >
                    {(() => {
                      const PriorityIcon = announcementPriorityIcons[priority.value] || Info;
                      return <PriorityIcon className={`h-4 w-4 shrink-0 ${announcementPriorityIconColors[priority.value]}`} aria-hidden="true" />;
                    })()}
                    {priority.label}
                  </button>
                ))}
              </div>
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-foreground">Destinatarios</label>

              {}
              {!isRestricted && (
                <div className="mb-3 grid gap-2 sm:grid-cols-3">
                  <button
                    type="button"
                    onClick={() => setScope('all')}
                    className={`announcement-scope-option min-h-12 rounded-xl border px-3 py-2.5 text-xs font-semibold transition ${
                      scope === 'all' ? 'border-foreground bg-foreground text-background' : 'border-border bg-surface text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                    data-testid="announcement-scope-all"
                  >
                    Todas las casas
                  </button>
                  <button
                    type="button"
                    onClick={() => setScope('specific')}
                    className={`announcement-scope-option min-h-12 rounded-xl border px-3 py-2.5 text-xs font-semibold transition ${
                      scope === 'specific' ? 'border-foreground bg-foreground text-background' : 'border-border bg-surface text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                    data-testid="announcement-scope-specific"
                  >
                    Casas específicas
                  </button>
                  <button
                    type="button"
                    onClick={() => setScope('users')}
                    className={`announcement-scope-option min-h-12 rounded-xl border px-3 py-2.5 text-xs font-semibold transition ${
                      scope === 'users' ? 'border-foreground bg-foreground text-background' : 'border-border bg-surface text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                    data-testid="announcement-scope-users"
                  >
                    Huésped individual
                  </button>
                </div>
              )}

              {}
              {isRestricted && (
                <p className="mb-3 rounded-xl border border-border bg-muted/60 p-3 text-xs text-muted-foreground">
                  Podés enviar comunicados solo a las casas que tenés asignadas.
                </p>
              )}

              {}
              {(scope === 'specific' || isRestricted) && (
                <div className="max-h-40 space-y-1 overflow-y-auto rounded-xl border border-border bg-background p-2" data-testid="announcement-houses-list">
                  {selectableHouses.length === 0 ? (
                    <p className="text-sm text-muted-foreground p-2">No tenés casas asignadas.</p>
                  ) : selectableHouses.map(h => (
                    <label key={h.id} className="flex cursor-pointer items-center gap-3 rounded-lg p-2 text-sm hover:bg-muted">
                      <input
                        type="checkbox"
                        checked={formData.target_houses.includes(h.id)}
                        onChange={() => toggleHouse(h.id)}
                        data-testid={`announcement-house-${h.id}`}
                      />
                      <span>{h.name}</span>
                      <span className="text-xs text-muted-foreground ml-auto">{h.city}</span>
                    </label>
                  ))}
                </div>
              )}

              {}
              {scope === 'users' && !isRestricted && (
                <div data-testid="announcement-users-list">
                  <input
                    type="text"
                    value={userSearch}
                    onChange={(e) => setUserSearch(e.target.value)}
                    className="mb-2 h-10 w-full rounded-xl border border-border bg-background px-3 text-sm text-foreground outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100/70 dark:focus:ring-amber-900/20"
                    placeholder="Buscar por nombre o email..."
                    data-testid="announcement-user-search"
                  />
                  <div className="max-h-48 space-y-1 overflow-y-auto rounded-xl border border-border bg-background p-2">
                    {filteredGuests.length === 0 ? (
                      <p className="text-sm text-muted-foreground p-2">No hay huéspedes que coincidan.</p>
                    ) : filteredGuests.map(g => {
                      const houseName = houses.find(h => h.id === g.house_id)?.name || '—';
                      return (
                        <label key={g.id} className="flex cursor-pointer items-center gap-3 rounded-lg p-2 text-sm hover:bg-muted">
                          <input
                            type="checkbox"
                            checked={formData.target_user_ids.includes(g.id)}
                            onChange={() => toggleUser(g.id)}
                            data-testid={`announcement-user-${g.id}`}
                          />
                          <span className="font-medium">{g.name}</span>
                          <span className="text-xs text-muted-foreground ml-auto">{houseName}{g.room_number ? ` · Hab. ${g.room_number}` : ''}</span>
                        </label>
                      );
                    })}
                  </div>
                </div>
              )}

              <p className="mt-2 text-xs leading-5 text-muted-foreground">
                {scope === 'all'
                  ? 'Se enviará a todos los huéspedes de todas las casas.'
                  : scope === 'users'
                    ? (formData.target_user_ids.length === 0
                        ? 'Seleccioná al menos un huésped.'
                        : `Se enviará a ${formData.target_user_ids.length} huésped${formData.target_user_ids.length === 1 ? '' : 'es'} seleccionado${formData.target_user_ids.length === 1 ? '' : 's'}.`)
                    : (formData.target_houses.length === 0
                        ? 'Seleccioná al menos una casa.'
                        : `Se enviará a los huéspedes de ${formData.target_houses.length} casa${formData.target_houses.length === 1 ? '' : 's'} seleccionada${formData.target_houses.length === 1 ? '' : 's'}.`)}
              </p>
            </div>

            <div className="flex flex-col-reverse gap-3 border-t border-border pt-5 sm:flex-row sm:justify-end">
              <button
                type="button"
                onClick={onClose}
                className="h-11 rounded-xl border border-border px-6 text-sm font-semibold text-foreground transition-colors hover:bg-muted sm:min-w-36"
                data-testid="cancel-announcement-button"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={loading}
                className="inline-flex h-11 items-center justify-center gap-2 rounded-xl bg-slate-950 px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-primary dark:text-primary-foreground sm:min-w-44"
                data-testid="submit-announcement-button"
              >
                <Send className="w-4 h-4" />
                {loading ? 'Enviando...' : 'Publicar comunicado'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}



function VisitsSection({ visits, houses, loading, onUpdateVisit }) {
  const [filter, setFilter] = useState('all');
  const [viewingPhoto, setViewingPhoto] = useState(null);
  const [guestFilter, setGuestFilter] = useState('');
  const { theme } = useTheme();
  const visitInputStyle = theme === 'dark'
    ? { backgroundColor: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--fg)', boxShadow: 'none' }
    : { border: '1px solid rgba(148, 163, 184, 0.35)', boxShadow: 'none' };
  const visitFilterStyles = theme === 'dark'
    ? {
      all: filter === 'all'
        ? { backgroundColor: '#ffffff', color: '#111111', border: '1px solid #ffffff', boxShadow: 'none' }
        : { backgroundColor: 'rgba(255, 255, 255, 0.04)', color: '#cbd5e1', border: '1px solid rgba(148, 163, 184, 0.38)', boxShadow: 'none' },
      approved: filter === 'approved'
        ? { backgroundColor: 'rgba(52, 158, 94, 0.18)', color: '#75d49a', border: '1px solid rgba(89, 210, 137, 0.28)', boxShadow: 'none' }
        : { backgroundColor: 'rgba(255, 255, 255, 0.04)', color: '#cbd5e1', border: '1px solid rgba(148, 163, 184, 0.38)', boxShadow: 'none' },
      rejected: filter === 'rejected'
        ? { backgroundColor: 'rgba(190, 24, 93, 0.16)', color: '#FDA4AF', border: '1px solid rgba(225, 29, 72, 0.36)', boxShadow: 'none' }
        : { backgroundColor: 'rgba(255, 255, 255, 0.04)', color: '#cbd5e1', border: '1px solid rgba(148, 163, 184, 0.38)', boxShadow: 'none' },
    }
    : {
      all: filter === 'all'
        ? { backgroundColor: '#111111', color: '#ffffff', border: '1px solid #111111', boxShadow: 'none' }
        : { backgroundColor: '#ffffff', color: '#334155', border: '1px solid rgba(148, 163, 184, 0.28)', boxShadow: 'none' },
      approved: filter === 'approved'
        ? { backgroundColor: 'var(--pill-green-bg)', color: 'var(--pill-green-fg)', border: '1px solid rgba(21, 128, 61, 0.28)', boxShadow: 'none' }
        : { backgroundColor: '#ffffff', color: '#334155', border: '1px solid rgba(148, 163, 184, 0.28)', boxShadow: 'none' },
      rejected: filter === 'rejected'
        ? { backgroundColor: 'var(--pill-rose-bg)', color: 'var(--pill-rose-fg)', border: '1px solid rgba(225, 29, 72, 0.28)', boxShadow: 'none' }
        : { backgroundColor: '#ffffff', color: '#334155', border: '1px solid rgba(148, 163, 184, 0.28)', boxShadow: 'none' },
    };
  const visitStatusStyle = theme === 'dark'
    ? {
      approved: { backgroundColor: 'rgba(52, 158, 94, 0.18)', color: '#75d49a', border: '1px solid rgba(89, 210, 137, 0.28)' },
      rejected: { backgroundColor: 'rgba(190, 24, 93, 0.16)', color: '#FDA4AF', border: '1px solid rgba(225, 29, 72, 0.36)' },
    }
    : {
      approved: { backgroundColor: 'var(--pill-green-bg)', color: 'var(--pill-green-fg)', border: '1px solid rgba(21, 128, 61, 0.28)' },
      rejected: { borderWidth: '1px', borderColor: 'rgba(225, 29, 72, 0.28)' },
    };
  const dniModalCloseStyle = theme === 'dark'
    ? { backgroundColor: 'var(--surface)', color: 'var(--fg)', border: '1px solid var(--border)', boxShadow: 'none' }
    : { backgroundColor: 'var(--surface)', color: 'var(--fg)', border: '1px solid rgba(148, 163, 184, 0.35)', boxShadow: 'none' };
  const filteredVisits = visits.filter(v => {
    if (filter !== 'all' && v.status !== filter) return false;
    if (guestFilter && !v.user_name.toLowerCase().includes(guestFilter.toLowerCase())) return false;
    return true;
  });

  
  const perGuest = {};
  visits.forEach(v => {
    if (v.status !== 'rejected') perGuest[v.user_name] = (perGuest[v.user_name] || 0) + 1;
  });
  const topGuests = Object.entries(perGuest).sort((a, b) => b[1] - a[1]).slice(0, 5);

  return (
    <div className="space-y-6 animate-enter" data-testid="admin-visits-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl">Registro de Visitas</h1>
          <p className="text-sm text-muted-foreground mt-1">Control de visitas registradas por los huéspedes (no requiere aprobación).</p>
        </div>
      </div>

      {}
      {topGuests.length > 0 && (
        <div className="admin-surface-card p-4" data-testid="visits-top-guests">
          <p className="text-xs uppercase font-bold text-muted-foreground tracking-wide mb-2">Top huéspedes con más visitas</p>
          <div className="flex flex-wrap gap-2">
            {topGuests.map(([name, count]) => (
              <button
                key={name}
                onClick={() => setGuestFilter(name)}
                style={theme === 'dark' ? { backgroundColor: 'var(--surface)', border: '1px solid var(--border)', color: 'var(--fg)', boxShadow: 'none' } : undefined}
                className="px-3 py-1.5 text-sm rounded-full border border-border bg-surface hover:bg-muted flex items-center gap-2"
                data-testid={`visits-top-guest-${name}`}
              >
                <span className="font-medium">{name}</span>
                <span style={theme === 'dark' ? { backgroundColor: 'rgba(255, 255, 255, 0.10)', color: 'var(--fg)' } : undefined} className="text-xs px-1.5 py-0.5 rounded-full bg-foreground text-background font-bold">{count}</span>
              </button>
            ))}
          </div>
        </div>
      )}

      <div className="flex flex-col sm:flex-row gap-2 sm:items-center">
        <input
          type="text"
          value={guestFilter}
          onChange={(e) => setGuestFilter(e.target.value)}
          placeholder="Filtrar por huésped…"
          style={visitInputStyle}
          className="input-brutal flex-1 px-3 py-2 text-sm"
          data-testid="visits-guest-search"
        />
        <div className="flex gap-2 flex-wrap">
          {['all', 'approved', 'rejected'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              style={visitFilterStyles[status]}
              className="px-3 py-1.5 text-sm rounded-lg border transition-colors"
              data-testid={`visits-filter-${status}`}
            >
              {status === 'all' ? 'Todas' : status === 'approved' ? 'Registradas' : 'Anuladas'}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <LoadingIndicator className="py-8" />
      ) : filteredVisits.length === 0 ? (
        <div className="admin-surface-card p-8 text-center">
          <UserRoundPlus className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No hay visitas registradas</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredVisits.map((visit) => {
            const house = houses.find(h => h.id === visit.house_id);
            const visitNights = Number.parseInt(visit.visit_duration, 10);
            const paymentChipStyle = theme === 'dark'
              ? (visit.is_paid
                ? { backgroundColor: 'rgba(52, 158, 94, 0.18)', color: '#75d49a', border: '1px solid rgba(89, 210, 137, 0.28)', boxShadow: 'none', transform: 'none', fontSize: 0 }
                : { backgroundColor: 'rgba(186, 133, 31, 0.16)', color: '#edc86f', border: '1px solid rgba(237, 190, 88, 0.28)', boxShadow: 'none', transform: 'none', fontSize: 0 })
              : (visit.is_paid
                ? { backgroundColor: 'var(--pill-green-bg)', color: 'var(--pill-green-fg)', border: '1px solid rgba(21, 128, 61, 0.28)', boxShadow: 'none', transform: 'none', fontSize: 0 }
                : { backgroundColor: 'var(--pill-yellow-bg)', color: 'var(--pill-yellow-fg)', border: '1px solid rgba(161, 98, 7, 0.3)', boxShadow: 'none', transform: 'none', fontSize: 0 });
            return (
              <div key={visit.id} className="admin-surface-card p-5" data-testid={`admin-visit-${visit.id}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
                  <div className="flex-1">
                    <div className="flex flex-wrap items-center gap-2 mb-2">
                      <h3 className="font-heading font-semibold">{visit.visitor_name}</h3>
                      <span style={visitStatusStyle?.[visit.status]} className={`text-xs px-2 py-1 rounded-full font-medium border-2 border-foreground ${theme === 'dark' ? '' : (
                        visit.status === 'approved' ? 'bg-accent-mint' : 'bg-destructive text-white'
                      )}`}>
                        {visit.status === 'approved' ? 'Registrada' : 'Anulada'}
                      </span>
                      <button
                        type="button"
                        onClick={() => onUpdateVisit && onUpdateVisit(visit.id, { is_paid: !visit.is_paid })}
                        style={paymentChipStyle}
                        className="text-xs px-2.5 py-1 rounded-full font-bold transition-all hover:scale-105 flex items-center gap-1 cursor-pointer"
                        data-testid={`visit-paid-toggle-${visit.id}`}
                        title="Cambiar estado de pago"
                      >
                        {visit.is_paid ? <CheckCircle className="w-3 h-3" style={{ width: '0.75rem', height: '0.75rem' }} /> : <Clock className="w-3 h-3" style={{ width: '0.75rem', height: '0.75rem' }} />}
                        <span style={{ fontSize: '0.75rem' }}>{visit.is_paid ? 'Pagada' : 'Pendiente de pago'}</span>
                        {visit.is_paid ? '💵 Pagada' : '⏳ Pendiente de pago'}
                      </button>
                    </div>
                    <p className="text-sm text-muted-foreground mb-1">
                      Huésped: <span className="text-foreground">{visit.user_name}</span>
                      {house && <> · Casa: <span className="text-foreground">{house.name}</span></>}
                      {visit.room_number && <> · Hab. <span className="text-foreground">{visit.room_number}</span></>}
                    </p>
                    <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mt-2">
                      <span className="flex items-center gap-1"><IdCard className="w-3 h-3" /> DNI: {visit.visitor_dni}</span>
                      <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {visit.visit_date}</span>
                      <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {Number.isNaN(visitNights) ? visit.visit_duration : `${visitNights} ${visitNights === 1 ? 'noche' : 'noches'}`}</span>
                    </div>
                    {visit.notes && <p className="text-sm mt-2 p-2 bg-muted rounded-lg">{visit.notes}</p>}
                  </div>
                  {visit.visitor_photo_dni && (
                    <button
                      onClick={() => setViewingPhoto(visit.visitor_photo_dni)}
                      style={theme === 'dark' ? { backgroundColor: 'rgba(255, 255, 255, 0.04)', color: '#cbd5e1', border: '1px solid var(--border)', boxShadow: 'none', transform: 'none' } : { border: '1px solid rgba(148, 163, 184, 0.35)', boxShadow: 'none' }}
                      className="flex items-center gap-2 px-3 py-2 border-2 border-foreground rounded-lg bg-surface hover:bg-muted text-sm flex-shrink-0"
                      data-testid={`visit-${visit.id}-view-dni`}
                    >
                      <IdCard className="w-4 h-4" />
                      Ver DNI
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {viewingPhoto && createPortal(
        <div
          className="fixed inset-0 bg-black/90 z-50 modal-backdrop"
          onClick={() => setViewingPhoto(null)}
          data-testid="dni-photo-modal"
        >
          <img src={viewingPhoto} alt="DNI" className="max-w-full max-h-full rounded-lg" />
          <button
            onClick={() => setViewingPhoto(null)}
            className="absolute w-11 h-11 rounded-lg flex items-center justify-center"
            style={{
              ...dniModalCloseStyle,
              top: 'calc(env(safe-area-inset-top, 0px) + 12px)',
              right: 'calc(env(safe-area-inset-right, 0px) + 12px)',
            }}
          >
            <X className="w-6 h-6" />
          </button>
        </div>
      , document.body)}
    </div>
  );
}


function TransfersSection({ transfers, houses, onRespond, loading }) {
  const [filter, setFilter] = useState('all');
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');
  const { theme } = useTheme();

  const statusLabel = { pending: 'Pendiente', approved: 'Aprobado', rejected: 'Rechazado' };
  const filteredTransfers = filter === 'all' ? transfers : transfers.filter(t => t.status === filter);
  const transferFilterStyles = theme === 'dark'
    ? {
      all: filter === 'all'
        ? { backgroundColor: '#ffffff', color: '#111111', border: '1px solid #ffffff', boxShadow: 'none' }
        : { backgroundColor: 'rgba(255, 255, 255, 0.04)', color: '#cbd5e1', border: '1px solid rgba(148, 163, 184, 0.38)', boxShadow: 'none' },
      pending: filter === 'pending'
        ? { backgroundColor: 'rgba(186, 133, 31, 0.16)', color: '#edc86f', border: '1px solid rgba(237, 190, 88, 0.28)', boxShadow: 'none' }
        : { backgroundColor: 'rgba(255, 255, 255, 0.04)', color: '#cbd5e1', border: '1px solid rgba(148, 163, 184, 0.38)', boxShadow: 'none' },
      approved: filter === 'approved'
        ? { backgroundColor: 'rgba(52, 158, 94, 0.18)', color: '#75d49a', border: '1px solid rgba(89, 210, 137, 0.28)', boxShadow: 'none' }
        : { backgroundColor: 'rgba(255, 255, 255, 0.04)', color: '#cbd5e1', border: '1px solid rgba(148, 163, 184, 0.38)', boxShadow: 'none' },
      rejected: filter === 'rejected'
        ? { backgroundColor: 'rgba(190, 24, 93, 0.16)', color: '#FDA4AF', border: '1px solid rgba(225, 29, 72, 0.36)', boxShadow: 'none' }
        : { backgroundColor: 'rgba(255, 255, 255, 0.04)', color: '#cbd5e1', border: '1px solid rgba(148, 163, 184, 0.38)', boxShadow: 'none' },
    }
    : {
      all: filter === 'all'
        ? { backgroundColor: '#111111', color: '#ffffff', border: '1px solid #111111', boxShadow: 'none' }
        : { backgroundColor: '#ffffff', color: '#334155', border: '1px solid rgba(148, 163, 184, 0.28)', boxShadow: 'none' },
      pending: filter === 'pending'
        ? { backgroundColor: 'var(--pill-yellow-bg)', color: 'var(--pill-yellow-fg)', border: '1px solid rgba(161, 98, 7, 0.3)', boxShadow: 'none' }
        : { backgroundColor: '#ffffff', color: '#334155', border: '1px solid rgba(148, 163, 184, 0.28)', boxShadow: 'none' },
      approved: filter === 'approved'
        ? { backgroundColor: 'var(--pill-green-bg)', color: 'var(--pill-green-fg)', border: '1px solid rgba(21, 128, 61, 0.28)', boxShadow: 'none' }
        : { backgroundColor: '#ffffff', color: '#334155', border: '1px solid rgba(148, 163, 184, 0.28)', boxShadow: 'none' },
      rejected: filter === 'rejected'
        ? { backgroundColor: 'var(--pill-rose-bg)', color: 'var(--pill-rose-fg)', border: '1px solid rgba(225, 29, 72, 0.28)', boxShadow: 'none' }
        : { backgroundColor: '#ffffff', color: '#334155', border: '1px solid rgba(148, 163, 184, 0.28)', boxShadow: 'none' },
    };
  const handleReject = (transferId) => {
    if (!rejectionReason.trim()) {
      alert('Por favor ingresá un motivo para el rechazo');
      return;
    }
    onRespond(transferId, 'rejected', rejectionReason);
    setRejectingId(null);
    setRejectionReason('');
  };

  return (
    <div className="space-y-6 animate-enter" data-testid="admin-transfers-section">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h1 className="font-heading font-bold text-2xl">Cambios de Casa</h1>
        <div className="flex gap-2 flex-wrap">
          {['all', 'pending', 'approved', 'rejected'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              style={transferFilterStyles[status]}
              className="px-3 py-1.5 text-sm rounded-lg border transition-colors"
              data-testid={`transfers-filter-${status}`}
            >
              {status === 'all' ? 'Todas' : statusLabel[status]}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <LoadingIndicator className="py-8" />
      ) : filteredTransfers.length === 0 ? (
        <div className="admin-surface-card p-8 text-center">
          <ArrowLeftRight className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No hay solicitudes de cambio</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filteredTransfers.map((t) => {
            const currentHouse = houses.find(h => h.id === t.current_house_id);
            const isRejecting = rejectingId === t.id;
            return (
              <div key={t.id} className="admin-surface-card p-5" data-testid={`admin-transfer-${t.id}`}>
                <div className="flex items-center justify-between mb-3">
                  <h3 className="font-heading font-semibold">{t.user_name}</h3>
                  <span className={`text-xs px-2 py-1 rounded-full font-medium border-2 border-foreground ${
                    t.status === 'pending' ? 'bg-accent-peach' :
                    t.status === 'approved' ? 'bg-accent-mint' : 'bg-destructive text-white'
                  }`}>
                    {statusLabel[t.status]}
                  </span>
                </div>
                <div className="flex items-center gap-3 text-sm mb-3">
                  <div className="flex-1 p-2 bg-muted rounded-lg">
                    <p className="text-xs text-muted-foreground">Desde</p>
                    <p className="font-medium">{currentHouse?.name || '-'}</p>
                  </div>
                  <ArrowLeftRight className="w-4 h-4" />
                  <div className="flex-1 p-2 bg-accent-mint rounded-lg">
                    <p className="text-xs">Hacia</p>
                    <p className="font-medium">{t.target_house_name}</p>
                  </div>
                </div>
                <div className="flex flex-wrap gap-3 text-sm text-muted-foreground mb-3">
                  <span className="flex items-center gap-1"><Calendar className="w-3 h-3" /> {t.start_date} → {t.end_date}</span>
                </div>
                {t.reason && <p className="text-sm p-2 bg-muted rounded-lg mb-3"><strong>Motivo:</strong> {t.reason}</p>}
                {t.rejection_reason && (
                  <p className="text-sm p-2 bg-destructive/10 border border-destructive rounded-lg mb-3">
                    <strong>Motivo del rechazo:</strong> {t.rejection_reason}
                  </p>
                )}

                {t.status === 'pending' && !isRejecting && (
                  <div className="flex gap-2">
                    <button
                      onClick={() => onRespond(t.id, 'approved')}
                      className="btn-brutal-accent btn-brutal px-4 py-2 text-sm flex items-center gap-1"
                      data-testid={`transfer-${t.id}-approve`}
                    >
                      <CheckCircle className="w-4 h-4" />
                      Aprobar
                    </button>
                    <button
                      onClick={() => setRejectingId(t.id)}
                      className="px-4 py-2 text-sm border-2 border-foreground rounded-lg bg-destructive text-white hover:shadow-brutal-sm flex items-center gap-1"
                      data-testid={`transfer-${t.id}-reject`}
                    >
                      <XCircle className="w-4 h-4" />
                      Rechazar
                    </button>
                  </div>
                )}

                {isRejecting && (
                  <div className="space-y-2 p-3 border-2 border-destructive rounded-lg bg-destructive/5">
                    <label className="text-sm font-medium">Motivo del rechazo</label>
                    <textarea
                      value={rejectionReason}
                      onChange={(e) => setRejectionReason(e.target.value)}
                      className="input-brutal w-full px-3 py-2 text-sm"
                      rows={2}
                      placeholder="Explicá el motivo..."
                      data-testid={`transfer-${t.id}-rejection-reason`}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => handleReject(t.id)}
                        className="px-3 py-1.5 text-sm bg-destructive text-white border-2 border-foreground rounded-lg"
                        data-testid={`transfer-${t.id}-confirm-reject`}
                      >
                        Confirmar rechazo
                      </button>
                      <button
                        onClick={() => { setRejectingId(null); setRejectionReason(''); }}
                        className="px-3 py-1.5 text-sm border-2 border-foreground rounded-lg hover:bg-muted"
                      >
                        Cancelar
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function MessagesSection({ threads, users, houses, currentUserId, onRefresh }) {
  const { theme } = useTheme();
  const [activeThread, setActiveThread] = useState(null);
  const [showGuestPicker, setShowGuestPicker] = useState(false);
  const [guestSearch, setGuestSearch] = useState('');
  const [threadSearch, setThreadSearch] = useState('');
  const [houseFilter, setHouseFilter] = useState('all'); 

  const guests = (users || []).filter(u => u.role === 'guest' && u.is_active !== false);
  const housesById = Object.fromEntries((houses || []).map(h => [h.id, h]));
  const usersById = Object.fromEntries((users || []).map(user => [user.id, user]));
  const filteredGuests = guestSearch.trim()
    ? guests.filter(g =>
        g.name?.toLowerCase().includes(guestSearch.toLowerCase()) ||
        g.email?.toLowerCase().includes(guestSearch.toLowerCase())
      )
    : guests;

  
  const sortedThreads = [...(threads || [])].sort((a, b) => {
    if ((b.unread_admin || 0) !== (a.unread_admin || 0)) return (b.unread_admin || 0) - (a.unread_admin || 0);
    return new Date(b.last_message_at || 0) - new Date(a.last_message_at || 0);
  });

  const filteredThreads = sortedThreads.filter(t => {
    if (houseFilter !== 'all' && t.house_id !== houseFilter) return false;
    const query = threadSearch.trim().toLowerCase();
    if (!query) return true;
    const house = housesById[t.house_id];
    return (t.guest_name || '').toLowerCase().includes(query) ||
      (t.guest_email || '').toLowerCase().includes(query) ||
      (t.last_message_text || '').toLowerCase().includes(query) ||
      (house?.name || '').toLowerCase().includes(query);
  });

  const threadTimestamp = (value) => {
    if (!value) return '';
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return '';
    const today = new Date();
    if (date.toDateString() === today.toDateString()) {
      return date.toLocaleTimeString('es-AR', { hour: '2-digit', minute: '2-digit' });
    }
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);
    if (date.toDateString() === yesterday.toDateString()) return 'Ayer';
    return date.toLocaleDateString('es-AR', { day: '2-digit', month: 'short' }).replace('.', '');
  };

  const startThread = async (guestId) => {
    try {
      const thread = await api.createDirectThread(guestId);
      setShowGuestPicker(false);
      setActiveThread(thread);
      onRefresh?.();
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al iniciar conversación');
    }
  };

  const handleToggleUnread = async (e, threadId, currentlyUnread) => {
    e.stopPropagation();
    try {
      if (currentlyUnread) {
        await api.markDirectThreadRead(threadId);
      } else {
        await api.markDirectThreadUnread(threadId);
      }
      onRefresh?.();
    } catch (err) {
      console.error('Error toggling unread status:', err);
    }
  };

  return (
    <div className="space-y-5 animate-enter" data-testid="admin-messages-section">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl">Mensajes</h1>
          <p className="text-sm text-muted-foreground mt-1">Chat directo con cada huésped.</p>
        </div>
        <button
          onClick={() => setShowGuestPicker(true)}
          className="btn-brutal px-4 py-2.5 flex items-center gap-2 self-start"
          style={theme === 'light' ? {
            backgroundColor: '#111111',
            color: '#ffffff',
            border: '1px solid #111111',
            boxShadow: 'none',
            transform: 'none',
          } : {
            backgroundColor: '#ffffff',
            color: '#111111',
            border: '1px solid var(--border)',
            boxShadow: 'none',
            transform: 'none',
          }}
          data-testid="new-message-button"
        >
          <Plus className="w-4 h-4" />
          Nuevo chat
        </button>
      </div>

      <div className="grid gap-3 sm:grid-cols-[minmax(0,1fr)_190px]" data-testid="messages-house-filter">
        <div className="relative">
          <Search className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <input
            type="text"
            value={threadSearch}
            onChange={(event) => setThreadSearch(event.target.value)}
            placeholder="Buscar por nombre, casa o mensaje..."
            className="h-11 w-full rounded-xl border border-border bg-surface pl-11 pr-10 text-sm text-foreground outline-none transition focus:border-amber-400 focus:ring-4 focus:ring-amber-100/70 dark:focus:ring-amber-900/20"
            data-testid="messages-search-input"
          />
          {threadSearch && (
            <button
              type="button"
              onClick={() => setThreadSearch('')}
              className="absolute right-3 top-1/2 -translate-y-1/2 rounded-md p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
              aria-label="Limpiar búsqueda"
              data-testid="messages-search-clear"
            >
              <X className="h-4 w-4" />
            </button>
          )}
        </div>
        <div className="relative">
          <Building2 className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <select
            value={houseFilter}
            onChange={(event) => setHouseFilter(event.target.value)}
            className="messages-house-select h-11 w-full appearance-none rounded-xl border border-border bg-surface py-2 pl-11 pr-9 text-sm font-medium text-foreground outline-none transition"
            aria-label="Filtrar conversaciones por casa"
            data-testid="messages-house-select"
          >
            <option value="all" data-testid="filter-house-all">Todas las casas ({threads?.length || 0})</option>
            {houses.map((house) => (
              <option key={house.id} value={house.id} data-testid={`filter-house-${house.id}`}>
                {house.name} ({(threads || []).filter(thread => thread.house_id === house.id).length})
              </option>
            ))}
          </select>
          <ChevronDown className="pointer-events-none absolute right-3.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
        </div>
      </div>

      <div className="admin-content-surface admin-messages-surface grid h-[calc(100vh-24rem)] min-h-[360px] overflow-hidden rounded-2xl border border-border bg-surface shadow-none lg:h-[calc(100vh-14rem)] lg:min-h-[520px] lg:grid-cols-[360px_minmax(0,1fr)]">
        <section className={`${activeThread ? 'hidden lg:flex' : 'flex'} min-h-0 flex-col border-r border-border`} aria-label="Conversaciones">
          <div className="flex items-center justify-between border-b border-border px-4 py-3.5">
            <div className="flex items-baseline gap-2">
              <h2 className="font-heading font-semibold">Conversaciones</h2>
              <span className="text-xs text-muted-foreground">({filteredThreads.length})</span>
            </div>
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto" data-testid="messages-thread-list">
            {filteredThreads.length === 0 ? (
              <div className="px-6 py-12 text-center">
                <MessageCircle className="mx-auto mb-3 h-9 w-9 text-muted-foreground/50" />
                <p className="text-sm font-medium text-foreground">No hay conversaciones</p>
                <p className="mt-1 text-xs text-muted-foreground">Probá otra búsqueda o iniciá un nuevo chat.</p>
              </div>
            ) : filteredThreads.map((thread) => {
              const house = housesById[thread.house_id];
              const hasUnread = thread.unread_admin > 0;
              const selected = activeThread?.id === thread.id;
              return (
                <div
                  key={thread.id}
                  onClick={() => setActiveThread(thread)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' || event.key === ' ') setActiveThread(thread);
                  }}
                  role="button"
                  tabIndex={0}
                  className={`group flex cursor-pointer items-center gap-3 border-b border-border px-4 py-3 text-left transition-colors ${selected ? 'direct-thread-selected' : 'direct-thread-hover'}`}
                  data-testid={`thread-${thread.id}`}
                >
                  <span className={`flex h-11 w-11 shrink-0 items-center justify-center rounded-full text-sm font-bold ${selected ? 'direct-thread-avatar-selected' : 'bg-accent-mint text-green-700'}`}>
                    {(thread.guest_name || '?').charAt(0).toUpperCase()}
                  </span>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <p className={`min-w-0 flex-1 truncate text-sm ${hasUnread ? 'font-bold' : 'font-semibold'}`}>{thread.guest_name || '—'}</p>
                      <time className="shrink-0 text-[10px] text-muted-foreground">{threadTimestamp(thread.last_message_at)}</time>
                    </div>
                    <p className="truncate text-[11px] font-medium text-muted-foreground">{house?.name || 'Sin casa asignada'}</p>
                    <div className="mt-0.5 flex min-w-0 items-center gap-2">
                      <p className={`min-w-0 flex-1 truncate text-xs ${hasUnread ? 'font-medium text-foreground' : 'text-muted-foreground'}`}>
                        {thread.last_message_text || <span className="italic">Sin mensajes aún</span>}
                      </p>
                    </div>
                  </div>
                  {hasUnread && (
                    <span className="flex h-5 min-w-5 shrink-0 items-center justify-center rounded-full bg-red-500 px-1.5 text-[10px] font-bold leading-none text-white">
                      {thread.unread_admin}
                    </span>
                  )}
                  <button
                    type="button"
                    onClick={(event) => handleToggleUnread(event, thread.id, hasUnread)}
                    className="hidden shrink-0 rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-surface hover:text-foreground group-hover:flex group-focus-within:flex"
                    title={hasUnread ? 'Marcar como leído' : 'Marcar como no leído'}
                    aria-label={hasUnread ? 'Marcar como leído' : 'Marcar como no leído'}
                    data-testid={`thread-toggle-unread-${thread.id}`}
                  >
                    {hasUnread ? <Mail className="h-4 w-4" /> : <MailOpen className="h-4 w-4" />}
                  </button>
                </div>
              );
            })}
          </div>
        </section>

        <section className={`${activeThread ? 'flex' : 'hidden lg:flex'} min-h-0 flex-col`} aria-label="Chat seleccionado">
          {activeThread ? (
            <DirectChatModal
              key={activeThread.id}
              thread={activeThread}
              currentUserId={currentUserId}
              viewerIsStaff
              language="es"
              embedded
              houseName={housesById[activeThread.house_id]?.name}
              roomNumber={usersById[activeThread.guest_user_id]?.room_number}
              onClose={() => { setActiveThread(null); onRefresh?.(); }}
              onMessagesRead={onRefresh}
            />
          ) : (
            <div className="flex h-full flex-col items-center justify-center px-8 text-center">
              <span className="mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-accent-yellow text-blue-500">
                <MessageCircle className="h-7 w-7" />
              </span>
              <h3 className="font-heading font-semibold text-foreground">Seleccioná una conversación</h3>
              <p className="mt-1 max-w-xs text-sm text-muted-foreground">Elegí un huésped de la lista para ver sus mensajes.</p>
            </div>
          )}
        </section>
      </div>

      {}
      {showGuestPicker && createPortal(
        <div
          className="fixed inset-0 z-[60] modal-backdrop"
          style={{
            backgroundColor: theme === 'dark' ? 'rgba(0, 0, 0, 0.78)' : 'rgba(0, 0, 0, 0.24)',
            backdropFilter: 'blur(2px)',
            WebkitBackdropFilter: 'blur(2px)',
          }}
          data-testid="guest-picker-modal"
        >
          <div
            className="w-full max-w-md modal-card max-h-[80vh] flex flex-col"
            style={{
              backgroundColor: theme === 'dark' ? 'var(--surface)' : '#ffffff',
              border: '1px solid var(--border)',
              borderRadius: '16px',
              boxShadow: 'none',
            }}
          >
            <div className="px-5 py-4 border-b border-border flex items-center justify-between">
              <h3 className="font-heading font-bold">Elegí un huésped</h3>
              <button
                onClick={() => setShowGuestPicker(false)}
                className="rounded-md p-1 transition-colors hover:bg-muted"
                style={{ color: theme === 'dark' ? '#ffffff' : '#111111' }}
                aria-label="Cerrar"
              >
                <X className="h-5 w-5" />
              </button>
            </div>
            <div className="p-4 border-b border-border">
              <input
                type="text"
                value={guestSearch}
                onChange={(e) => setGuestSearch(e.target.value)}
                placeholder="Buscar por nombre o email..."
                className="input-brutal w-full px-3 py-2"
                style={{ fontSize: '16px' }}
                autoFocus
              />
            </div>
            <div className="flex-1 overflow-y-auto p-2">
              {filteredGuests.length === 0 ? (
                <p className="text-center text-sm text-muted-foreground py-6">Sin resultados</p>
              ) : (
                filteredGuests.map(g => (
                  <button
                    key={g.id}
                    onClick={() => startThread(g.id)}
                    className="w-full text-left px-3 py-2 rounded-lg hover:bg-muted flex items-center gap-3"
                    data-testid={`pick-guest-${g.id}`}
                  >
                    <div className="w-8 h-8 rounded-full bg-accent-lavender border-2 border-foreground flex items-center justify-center text-xs font-bold">
                      {g.name?.charAt(0).toUpperCase() || '?'}
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium truncate">{g.name}</p>
                      <p className="text-[10px] text-muted-foreground truncate">{g.email}</p>
                    </div>
                  </button>
                ))
              )}
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}


function RenewalThresholdSettings() {
  const { theme } = useTheme();
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [t1m, setT1m] = useState(10);
  const [t3m, setT3m] = useState(30);
  const [t6m, setT6m] = useState(60);
  const [visitCost, setVisitCost] = useState(0);
  const [visitCurrency, setVisitCurrency] = useState('ARS');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const s = await api.getSettings();
        setT1m(s.renewal_threshold_1m ?? 10);
        setT3m(s.renewal_threshold_3m ?? 30);
        setT6m(s.renewal_threshold_6m ?? 60);
        setVisitCost(s.visit_cost_per_night ?? 0);
        setVisitCurrency(s.visit_cost_currency || 'ARS');
      } catch (e) {  }
      setLoading(false);
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      await api.updateSettings({
        renewal_threshold_1m: parseInt(t1m) || 0,
        renewal_threshold_3m: parseInt(t3m) || 0,
        renewal_threshold_6m: parseInt(t6m) || 0,
        visit_cost_per_night: parseInt(visitCost) || 0,
        visit_cost_currency: visitCurrency,
      });
      setSavedAt(Date.now());
      setTimeout(() => setSavedAt(null), 2500);
    } catch (e) {
      alert(e?.response?.data?.detail || 'Error al guardar');
    }
    setSaving(false);
  };

  if (loading) return null;

  return (
    <div
      className="admin-surface-card p-5"
      data-testid="renewal-threshold-settings"
    >
      <style>{`
        :root[data-theme="dark"] [data-testid="visit-currency-select"] {
          color: #ffffff !important;
          -webkit-text-fill-color: #ffffff !important;
          opacity: 1 !important;
          background: var(--surface) !important;
          border: 1px solid var(--border) !important;
          color-scheme: dark;
        }
        :root[data-theme="dark"] [data-testid="visit-currency-select"] option {
          color: #ffffff;
          background: var(--surface);
          opacity: 1;
        }
      `}</style>
      <button
        type="button"
        onClick={() => setSettingsOpen((open) => !open)}
        className="flex min-h-11 w-full items-center justify-between gap-3 text-left"
        aria-expanded={settingsOpen}
        aria-controls="renewal-settings-panel"
        data-testid="renewal-settings-toggle"
      >
        <span>
          <span className="block font-heading text-lg font-bold">Configuración</span>
          <span className="mt-0.5 block text-xs text-muted-foreground">Ajustes que aplican a todos los huéspedes.</span>
        </span>
        <ChevronDown className={`h-5 w-5 shrink-0 text-muted-foreground transition-transform ${settingsOpen ? 'rotate-180' : ''}`} aria-hidden="true" />
      </button>

      {settingsOpen && <div id="renewal-settings-panel" className="mt-4" data-testid="renewal-settings-panel">
      <div className="space-y-3 mb-5">
        <p className="text-sm font-semibold">¿Cuándo aparece el botón &ldquo;Renovar contrato&rdquo;?</p>
        <p className="text-xs text-muted-foreground">Configurá los días previos al vencimiento según la duración del contrato del huésped.</p>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Contratos de 1 mes (≤45 días)</label>
            <div className="flex items-center gap-2">
              <input type="number" min="0" max="365" value={t1m} onChange={(e) => setT1m(e.target.value)} className="input-brutal w-20 px-3 py-2" data-testid="threshold-1m-input" />
              <span className="text-xs text-muted-foreground">días antes</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Contratos de 3 meses (46-120 d)</label>
            <div className="flex items-center gap-2">
              <input type="number" min="0" max="365" value={t3m} onChange={(e) => setT3m(e.target.value)} className="input-brutal w-20 px-3 py-2" data-testid="threshold-3m-input" />
              <span className="text-xs text-muted-foreground">días antes</span>
            </div>
          </div>
          <div>
            <label className="block text-xs text-muted-foreground mb-1">Contratos de 6+ meses (&gt;120 d)</label>
            <div className="flex items-center gap-2">
              <input type="number" min="0" max="365" value={t6m} onChange={(e) => setT6m(e.target.value)} className="input-brutal w-20 px-3 py-2" data-testid="threshold-6m-input" />
              <span className="text-xs text-muted-foreground">días antes</span>
            </div>
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-4">
        <p className="text-sm font-semibold mb-1">Costo por noche de visita</p>
        <p className="text-xs text-muted-foreground mb-2">Importe que se le muestra al huésped al confirmar una visita y debe abonarse en administración. Poné 0 para no cobrar.</p>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={visitCurrency}
            onChange={(e) => setVisitCurrency(e.target.value)}
            className="agenda-control px-3 py-2 text-sm"
            style={theme === 'dark' ? {
              color: '#ffffff',
              WebkitTextFillColor: '#ffffff',
              opacity: 1,
              backgroundColor: 'var(--surface)',
              borderColor: 'var(--border)',
              colorScheme: 'dark',
            } : undefined}
            data-testid="visit-currency-select"
          >
            <option value="ARS">ARS ($)</option>
            <option value="USD">USD (US$)</option>
            <option value="EUR">EUR (€)</option>
            <option value="UYU">UYU ($U)</option>
            <option value="BRL">BRL (R$)</option>
          </select>
          <input type="number" min="0" step="100" value={visitCost} onChange={(e) => setVisitCost(e.target.value)} className="input-brutal w-32 px-3 py-2" data-testid="visit-cost-input" />
          <span className="text-sm text-muted-foreground">/ noche</span>
        </div>
      </div>

      <div className="mt-4 flex items-center gap-3">
        <button
          onClick={save}
          disabled={saving}
          className="btn-brutal px-4 py-2 text-sm disabled:opacity-50"
          style={theme === 'light' ? {
            backgroundColor: '#111111',
            color: '#ffffff',
            border: '1px solid #111111',
            boxShadow: 'none',
            transform: 'none',
          } : { boxShadow: 'none' }}
          data-testid="save-settings-button"
        >
          {saving ? 'Guardando...' : 'Guardar configuración'}
        </button>
        {savedAt && <span className="text-xs text-accent-mint font-semibold">✓ Guardado</span>}
      </div></div>}
    </div>
  );
}

function RenewalsSection({ renewals, houses, users, onRespond, loading }) {
  const { theme } = useTheme();
  const [filter, setFilter] = useState('all');
  const [approvingId, setApprovingId] = useState(null);
  const [rejectingId, setRejectingId] = useState(null);
  const [newLeaseEnd, setNewLeaseEnd] = useState('');
  const [adminNotes, setAdminNotes] = useState('');

  const statusLabel = { pending: 'Pendiente', approved: 'Aprobada', rejected: 'Rechazada' };
  const filtered = filter === 'all' ? renewals : renewals.filter(r => r.status === filter);
  const renewalNeutralStyle = theme === 'dark'
    ? {
        backgroundColor: 'rgba(255, 255, 255, 0.04)',
        color: '#cbd5e1',
        border: '1px solid rgba(148, 163, 184, 0.38)',
        boxShadow: 'none',
      }
    : {
        backgroundColor: '#ffffff',
        color: '#4b5563',
        border: '1px solid #d1d5db',
        boxShadow: 'none',
      };
  const renewalFilterStyles = {
    all: filter === 'all'
      ? (theme === 'dark'
        ? { backgroundColor: '#ffffff', color: '#111111', border: '1px solid #ffffff', boxShadow: 'none' }
        : { backgroundColor: '#111111', color: '#ffffff', border: '1px solid #111111', boxShadow: 'none' })
      : renewalNeutralStyle,
    pending: filter === 'pending'
      ? { ...getAdminDashboardSemanticStyle(theme, 'yellow'), border: '1px solid currentColor', boxShadow: 'none' }
      : renewalNeutralStyle,
    approved: filter === 'approved'
      ? { ...getAdminDashboardSemanticStyle(theme, 'mint'), border: '1px solid currentColor', boxShadow: 'none' }
      : renewalNeutralStyle,
    rejected: filter === 'rejected'
      ? { ...getAdminDashboardSemanticStyle(theme, 'rose'), border: '1px solid currentColor', boxShadow: 'none' }
      : renewalNeutralStyle,
  };
  const startApprove = (r) => {
    setApprovingId(r.id);
    setRejectingId(null);
    setNewLeaseEnd(r.preferred_lease_end || '');
    setAdminNotes('');
  };
  const startReject = (r) => {
    setRejectingId(r.id);
    setApprovingId(null);
    setAdminNotes('');
  };

  const confirmApprove = (id) => {
    if (!newLeaseEnd) { alert('Ingresá la nueva fecha de vencimiento'); return; }
    onRespond(id, { status: 'approved', new_lease_end: newLeaseEnd, admin_notes: adminNotes });
    setApprovingId(null);
  };
  const confirmReject = (id) => {
    if (!adminNotes.trim()) { alert('Ingresá un motivo para el rechazo'); return; }
    onRespond(id, { status: 'rejected', admin_notes: adminNotes });
    setRejectingId(null);
  };

  return (
    <div className="space-y-6 animate-enter" data-testid="admin-renewals-section">
      <RenewalThresholdSettings />
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-heading font-bold text-2xl">Renovaciones de contrato</h1>
          <p className="text-sm text-muted-foreground mt-1">Solicitudes enviadas por los huéspedes.</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          {['all', 'pending', 'approved', 'rejected'].map((status) => (
            <button
              key={status}
              onClick={() => setFilter(status)}
              className="px-3 py-1.5 text-sm rounded-lg border transition-colors"
              style={renewalFilterStyles[status]}
              data-testid={`renewals-filter-${status}`}
            >
              {status === 'all' ? 'Todas' : statusLabel[status]}
            </button>
          ))}
        </div>
      </div>

      {loading ? (
        <LoadingIndicator className="py-8" />
      ) : filtered.length === 0 ? (
          <div className="admin-surface-card p-8 text-center">
          <CalendarSync className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No hay solicitudes {filter !== 'all' ? statusLabel[filter].toLowerCase() : ''}</p>
        </div>
      ) : (
        <div className="space-y-4">
          {filtered.map((r) => {
            const house = houses.find(h => h.id === r.house_id);
            const guestData = users.find(u => u.id === r.user_id);
            return (
              <div key={r.id} className="admin-surface-card p-5" data-testid={`renewal-${r.id}`}>
                <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4 mb-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <h3 className="font-heading font-semibold">{r.user_name}</h3>
                      <span className={`text-xs px-2 py-1 rounded-full font-medium border-2 border-foreground ${
                        r.status === 'pending' ? 'bg-accent-peach' :
                        r.status === 'approved' ? 'bg-accent-mint' : 'bg-destructive text-white'
                      }`}>
                        {statusLabel[r.status]}
                      </span>
                    </div>
                    <p className="text-xs text-muted-foreground mb-2">{r.user_email}{house ? ` · ${house.name}` : ''}{guestData?.room_number ? ` · Hab. ${guestData.room_number}` : ''}</p>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mt-3 text-sm">
                      <div className="p-2 rounded-lg bg-muted/50 border border-border">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Vence actual</p>
                        <p className="text-foreground font-semibold">{r.current_lease_end || '—'}</p>
                      </div>
                      <div className="p-2 rounded-lg bg-muted/50 border border-border">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Fecha deseada</p>
                        <p className="text-foreground font-semibold">{r.preferred_lease_end || '—'}</p>
                      </div>
                      <div className="p-2 rounded-lg bg-muted/50 border border-border">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground">Nueva fecha</p>
                        <p className="text-foreground font-semibold">{r.new_lease_end || '—'}</p>
                      </div>
                    </div>
                    {r.notes && (
                      <div className="mt-3 p-2 bg-muted rounded-lg">
                        <p className="text-[10px] uppercase tracking-wide text-muted-foreground mb-1">Comentario del huésped</p>
                        <p className="text-sm text-foreground">{r.notes}</p>
                      </div>
                    )}
                    {r.admin_notes && (
                      <div className="mt-2 p-2 bg-accent-yellow/30 border border-accent-yellow rounded-lg">
                        <p className="text-[10px] uppercase tracking-wide text-foreground mb-1">Respuesta del admin</p>
                        <p className="text-sm text-foreground">{r.admin_notes}</p>
                      </div>
                    )}
                  </div>
                </div>

                {r.status === 'pending' && approvingId !== r.id && rejectingId !== r.id && (
                  <div className="flex gap-2 flex-wrap">
                    <button
                      onClick={() => startApprove(r)}
                      className="btn-brutal-accent btn-brutal px-4 py-2 text-sm flex items-center gap-1"
                      data-testid={`renewal-${r.id}-approve`}
                    >
                      <CheckCircle className="w-4 h-4" />
                      Aprobar renovación
                    </button>
                    <button
                      onClick={() => startReject(r)}
                      className="px-4 py-2 text-sm border-2 border-foreground rounded-lg bg-destructive text-white hover:shadow-brutal-sm flex items-center gap-1"
                      data-testid={`renewal-${r.id}-reject`}
                    >
                      <XCircle className="w-4 h-4" />
                      Rechazar
                    </button>
                  </div>
                )}

                {approvingId === r.id && (
                  <div className="space-y-2 p-3 border-2 border-accent-mint rounded-lg bg-accent-mint/10">
                    <label className="text-sm font-medium">Nueva fecha de vencimiento</label>
                    <input
                      type="date"
                      value={newLeaseEnd}
                      onChange={(e) => setNewLeaseEnd(e.target.value)}
                      className="input-brutal w-full px-3 py-2 text-sm"
                      data-testid={`renewal-${r.id}-new-end`}
                    />
                    <label className="text-sm font-medium">Comentario (opcional)</label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={2}
                      className="input-brutal w-full px-3 py-2 text-sm"
                      placeholder="Ej: Renovación confirmada por 1 año más"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => confirmApprove(r.id)}
                        className="btn-brutal-accent btn-brutal px-3 py-1.5 text-sm"
                        data-testid={`renewal-${r.id}-confirm-approve`}
                      >Confirmar aprobación</button>
                      <button
                        onClick={() => setApprovingId(null)}
                        className="px-3 py-1.5 text-sm border-2 border-foreground rounded-lg hover:bg-muted"
                      >Cancelar</button>
                    </div>
                  </div>
                )}

                {rejectingId === r.id && (
                  <div className="space-y-2 p-3 border-2 border-destructive rounded-lg bg-destructive/5">
                    <label className="text-sm font-medium">Motivo del rechazo</label>
                    <textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      rows={2}
                      className="input-brutal w-full px-3 py-2 text-sm"
                      placeholder="Explicale al huésped por qué no podés renovar"
                      data-testid={`renewal-${r.id}-reject-reason`}
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={() => confirmReject(r.id)}
                        className="px-3 py-1.5 text-sm bg-destructive text-white border-2 border-foreground rounded-lg"
                        data-testid={`renewal-${r.id}-confirm-reject`}
                      >Confirmar rechazo</button>
                      <button
                        onClick={() => setRejectingId(null)}
                        className="px-3 py-1.5 text-sm border-2 border-foreground rounded-lg hover:bg-muted"
                      >Cancelar</button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function BenefitsSection({ benefits, houses, onCreateNew, onEdit, onDelete, onReorder, onShowQR, loading }) {
  const { theme } = useTheme();
  const sortedBenefits = [...benefits].sort((a, b) => (a.display_order || 0) - (b.display_order || 0));
  return (
    <div className="space-y-6 animate-enter" data-testid="admin-benefits-section">
      <style>{`
        [data-testid="admin-benefits-section"] [data-testid="new-benefit-button"],
        [data-testid="admin-benefits-section"] [data-testid="new-benefit-button"]:hover,
        [data-testid="admin-benefits-section"] [data-testid="new-benefit-button"]:active {
          background-color: #111111 !important;
          color: #ffffff !important;
          border-color: #111111 !important;
          box-shadow: none !important;
          transform: none !important;
        }
        :root[data-theme="dark"] [data-testid="admin-benefits-section"] [data-testid="new-benefit-button"],
        :root[data-theme="dark"] [data-testid="admin-benefits-section"] [data-testid="new-benefit-button"]:hover,
        :root[data-theme="dark"] [data-testid="admin-benefits-section"] [data-testid="new-benefit-button"]:active {
          background-color: #ffffff !important;
          color: #111111 !important;
          border-color: var(--border) !important;
        }
        [data-testid="admin-benefits-section"] .benefit-card {
          overflow: visible;
          padding: 0;
        }
        [data-testid="admin-benefits-section"] .benefit-card-visual-shell {
          position: relative;
          height: 148px;
          overflow: visible;
          z-index: 2;
        }
        [data-testid="admin-benefits-section"] .benefit-card-visual {
          position: relative;
          height: 148px;
          overflow: hidden;
          background: var(--surface-hover);
          border-bottom: 1px solid var(--border);
        }
        [data-testid="admin-benefits-section"] .benefit-card-banner,
        [data-testid="admin-benefits-section"] .benefit-card-placeholder {
          width: 100%;
          height: 100%;
        }
        [data-testid="admin-benefits-section"] .benefit-card-banner {
          display: block;
          object-fit: cover;
          overflow: hidden;
          border-radius: 12px 12px 0 0;
        }
        [data-testid="admin-benefits-section"] .benefit-card-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--muted-fg);
          background: linear-gradient(135deg, var(--surface-hover), var(--muted));
          overflow: hidden;
          border-radius: 12px 12px 0 0;
        }
        [data-testid="admin-benefits-section"] .benefit-card-order {
          position: absolute;
          top: 24px;
          right: 12px;
          display: flex;
          flex-direction: column;
          gap: 2px;
          padding: 4px;
          border: 1px solid var(--border);
          border-radius: 10px;
          background: var(--surface-hover);
          color: var(--fg);
          z-index: 2;
        }
        [data-testid="admin-benefits-section"] .benefit-card-order button {
          border: 1px solid var(--border);
          border-radius: 6px;
          background-color: transparent;
          color: inherit;
          transition: background-color 0.15s ease, color 0.15s ease;
        }
        [data-testid="admin-benefits-section"] .benefit-card-order button:hover:not(:disabled) {
          background-color: var(--muted);
        }
        [data-testid="admin-benefits-section"] .benefit-card-discount {
          position: absolute;
          top: 12px;
          right: 12px;
          border: 1px solid rgba(21, 128, 61, 0.28);
          border-radius: 999px;
          background: rgba(220, 252, 231, 0.94);
          color: #15803d;
          font-size: 0.875rem;
          font-weight: 700;
          padding: 6px 10px;
        }
        [data-testid="admin-benefits-section"] .benefit-card-logo {
          position: absolute;
          left: 16px;
          bottom: -22px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 58px;
          height: 58px;
          overflow: hidden;
          border: 1px solid var(--border);
          border-radius: 12px;
          background: #ffffff;
          box-shadow: 0 6px 16px rgba(15, 23, 41, 0.12);
          z-index: 3;
        }
        [data-testid="admin-benefits-section"] .benefit-card-logo img {
          width: 100%;
          height: 100%;
          object-fit: contain;
          padding: 0;
        }
        [data-testid="admin-benefits-section"] .benefit-card-body {
          position: relative;
          display: flex;
          min-width: 0;
          flex: 1;
          flex-direction: column;
          padding: 34px 16px 16px;
        }
        [data-testid="admin-benefits-section"] .benefit-card-description {
          display: -webkit-box;
          overflow: hidden;
          -webkit-box-orient: vertical;
          -webkit-line-clamp: 3;
        }
        :root[data-theme="dark"] [data-testid="admin-benefits-section"] .benefit-card-logo {
          box-shadow: none;
        }
        :root[data-theme="dark"] [data-testid="admin-benefits-section"] .benefit-card-visual {
          background-color: transparent;
        }
      `}</style>
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading font-bold text-2xl">Beneficios y Descuentos</h1>
          <p className="text-xs text-muted-foreground mt-1">Usá las flechas ↑↓ para cambiar el orden de aparición en la app del huésped.</p>
        </div>
        <button
          onClick={onCreateNew}
          className="inline-flex h-11 items-center justify-center gap-2 self-start rounded-xl px-5 text-sm font-semibold shadow-sm transition-opacity hover:opacity-90"
          style={theme === 'light'
            ? { backgroundColor: '#111111', color: '#ffffff' }
            : { backgroundColor: '#ffffff', color: '#111111' }}
          data-testid="new-benefit-button"
        >
          <Plus className="w-4 h-4" />
          Nuevo Beneficio
        </button>
      </div>

      {loading ? (
        <LoadingIndicator className="py-8" />
      ) : sortedBenefits.length === 0 ? (
        <div className="admin-surface-card p-8 text-center">
          <Gift className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground mb-4">No hay beneficios</p>
          <button onClick={onCreateNew} className="btn-brutal px-4 py-2">
            Crear primer beneficio
          </button>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {sortedBenefits.map((b, idx) => {
            const targetHouses = b.target_houses?.length > 0
              ? b.target_houses.map(hid => houses.find(h => h.id === hid)?.name).filter(Boolean).join(', ')
              : 'Todas las casas';
            const pmLabels = { cash: 'Efectivo', debit: 'Débito', credit: 'Crédito', mp: 'Mercado Pago', transfer: 'Transferencia' };
            return (
              <div key={b.id} className="admin-surface-card benefit-card relative flex flex-col" data-testid={`admin-benefit-${b.id}`}>
                <div className="benefit-card-visual-shell">
                  <div className="benefit-card-visual">
                  {b.image_url ? (
                    <img src={b.image_url} alt="" className="benefit-card-banner" />
                  ) : (
                    <div className="benefit-card-placeholder">
                      <Gift className="h-10 w-10" />
                    </div>
                  )}
                  {b.discount && <span className="benefit-card-discount">{b.discount}</span>}
                  </div>
                  <div className="benefit-card-logo">
                    {b.business_logo ? <img src={b.business_logo} alt="" /> : <Gift className="h-6 w-6 text-muted-foreground" />}
                  </div>
                </div>
                <div className="benefit-card-body">
                  <div className="benefit-card-order">
                    <button
                      onClick={() => onReorder && onReorder(b.id, 'up')}
                      disabled={idx === 0}
                      className="flex h-6 w-6 items-center justify-center disabled:cursor-not-allowed disabled:opacity-30"
                      title="Mover arriba"
                      data-testid={`reorder-benefit-up-${b.id}`}
                    >
                      <ChevronUp className="h-3 w-3" />
                    </button>
                    <button
                      onClick={() => onReorder && onReorder(b.id, 'down')}
                      disabled={idx === sortedBenefits.length - 1}
                      className="flex h-6 w-6 items-center justify-center disabled:cursor-not-allowed disabled:opacity-30"
                      title="Mover abajo"
                      data-testid={`reorder-benefit-down-${b.id}`}
                    >
                      <ChevronDown className="h-3 w-3" />
                    </button>
                  </div>
                  <h3 className="mb-1 font-heading font-semibold text-foreground">{b.title}</h3>
                  <p className="mb-2 text-sm text-muted-foreground">{b.business_name}</p>
                  <p className="benefit-card-description mb-3 text-sm">{b.description}</p>
                  {b.business_address && (
                    <p className="mb-2 flex items-center gap-1 text-xs text-muted-foreground">
                      <MapPin className="h-3 w-3" /> {b.business_address}
                    </p>
                  )}
                  {b.payment_methods && b.payment_methods.length > 0 && (
                    <div className="mb-3 flex flex-wrap gap-1">
                      {b.payment_methods.map(m => (
                        <span key={m} className="rounded-full border border-border bg-muted px-2 py-0.5 text-[10px] font-medium text-foreground">
                          {pmLabels[m] || m}
                        </span>
                      ))}
                    </div>
                  )}
                  <p className="mb-3 text-xs text-muted-foreground">
                    <strong>Para:</strong> {targetHouses}
                  </p>
                  <div className="mt-auto flex w-full flex-nowrap items-stretch gap-2">
                  <button
                    onClick={() => onShowQR && onShowQR(b)}
                    className="shrink-0 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted flex items-center justify-center gap-1"
                    data-testid={`qr-benefit-${b.id}`}
                  >
                    <QrCode className="w-4 h-4" />
                    QR
                  </button>
                  <button
                    onClick={() => onEdit(b)}
                    className="min-w-0 flex-1 px-3 py-1.5 text-sm border border-border rounded-lg hover:bg-muted flex items-center justify-center gap-1"
                    data-testid={`edit-benefit-${b.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                    Editar
                  </button>
                  <button
                    onClick={() => onDelete(b.id)}
                    className="min-w-0 flex-1 px-3 py-1.5 text-sm border border-border rounded-lg bg-destructive text-white hover:shadow-brutal-sm flex items-center justify-center gap-1"
                    data-testid={`delete-benefit-${b.id}`}
                  >
                    <Trash2 className="w-4 h-4" />
                    Eliminar
                  </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


function BenefitFormFields({ formData, setFormData, houses, onImage, onLogo, error }) {
  const { theme } = useTheme();
  const toggleHouse = (houseId) => {
    setFormData(prev => ({
      ...prev,
      target_houses: prev.target_houses.includes(houseId)
        ? prev.target_houses.filter(id => id !== houseId)
        : [...prev.target_houses, houseId]
    }));
  };

  
  
  const PAYMENT_METHODS = [
    { value: 'cash', label: 'Efectivo' },
    { value: 'debit', label: 'Débito' },
    { value: 'credit', label: 'Crédito' },
    { value: 'mp', label: 'Mercado Pago' },
    { value: 'transfer', label: 'Transferencia' },
  ];
  const currentMethods = formData.payment_methods || [];
  const togglePM = (m) => {
    const set = new Set(currentMethods);
    if (set.has(m)) set.delete(m); else set.add(m);
    setFormData({ ...formData, payment_methods: Array.from(set) });
  };
  const toggleAllPM = () => {
    const allSelected = PAYMENT_METHODS.every(p => currentMethods.includes(p.value));
    setFormData({ ...formData, payment_methods: allSelected ? [] : PAYMENT_METHODS.map(p => p.value) });
  };
  const allSelected = PAYMENT_METHODS.every(p => currentMethods.includes(p.value));

  return (
    <>
      {error && (
        <div className="p-3 bg-destructive/20 border border-destructive rounded-lg text-sm text-destructive">{error}</div>
      )}

      <div className="benefit-modal-section">
        <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Nombre del negocio *</label>
          <input
            type="text"
            value={formData.business_name}
            onChange={(e) => setFormData({ ...formData, business_name: e.target.value })}
            className="input-brutal w-full px-4 py-3"
            placeholder="Ej: FitLife Gym"
            required
            data-testid="benefit-business-input"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Categoría</label>
          <select
            value={formData.category}
            onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            className="input-brutal w-full px-4 py-3"
            data-testid="benefit-category-input"
          >
            <option value="other">Otro</option>
            <option value="food">Comida</option>
            <option value="fitness">Fitness</option>
            <option value="study">Estudio</option>
            <option value="wellness">Bienestar</option>
          </select>
        </div>
        </div>

        <div>
        <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Título del beneficio *</label>
        <input
          type="text"
          value={formData.title}
          onChange={(e) => setFormData({ ...formData, title: e.target.value })}
          className="input-brutal w-full px-4 py-3"
          placeholder="Ej: Gimnasio FitLife"
          required
          data-testid="benefit-title-input"
        />
        </div>

        <div>
        <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Descripción corta *</label>
        <textarea
          value={formData.description}
          onChange={(e) => setFormData({ ...formData, description: e.target.value })}
          className="input-brutal w-full px-4 py-3 min-h-[70px] resize-none"
          placeholder="Aparece debajo del nombre en la card del huésped..."
          required
          data-testid="benefit-description-input"
        />
        </div>

        <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Descuento</label>
          <input
            type="text"
            value={formData.discount}
            onChange={(e) => setFormData({ ...formData, discount: e.target.value })}
            className="input-brutal w-full px-4 py-3"
            placeholder="Ej: 20%"
            data-testid="benefit-discount-input"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Vence el</label>
          <input
            type="date"
            value={formData.valid_until}
            onChange={(e) => setFormData({ ...formData, valid_until: e.target.value })}
            className="input-brutal w-full px-4 py-3"
            data-testid="benefit-valid-input"
          />
        </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Días válidos</label>
          <input
            type="text"
            value={formData.valid_days}
            onChange={(e) => setFormData({ ...formData, valid_days: e.target.value })}
            className="input-brutal w-full px-4 py-3"
            placeholder="Ej: Todos los días"
            data-testid="benefit-validdays-input"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Usos por coliver</label>
          <input
            type="number"
            min="0"
            value={formData.uses_per_user ?? ''}
            onChange={(e) => setFormData({ ...formData, uses_per_user: e.target.value === '' ? null : parseInt(e.target.value, 10) })}
            className="input-brutal w-full px-4 py-3"
            placeholder="Vacío = ilimitado"
            data-testid="benefit-uses-input"
          />
        </div>
        </div>
      </div>

      <div className="benefit-modal-section">
        <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Dirección</label>
          <input
            type="text"
            value={formData.business_address}
            onChange={(e) => setFormData({ ...formData, business_address: e.target.value })}
            className="input-brutal w-full px-4 py-3"
            placeholder="Av. Corrientes 1500"
            data-testid="benefit-address-input"
          />
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Sucursales</label>
          <input
            type="text"
            value={formData.branches}
            onChange={(e) => setFormData({ ...formData, branches: e.target.value })}
            className="input-brutal w-full px-4 py-3"
            placeholder="Ej: +50 sucursales"
            data-testid="benefit-branches-input"
          />
        </div>
        </div>

        <div>
        <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Cómo funciona</label>
        <textarea
          value={formData.how_it_works}
          onChange={(e) => setFormData({ ...formData, how_it_works: e.target.value })}
          className="input-brutal w-full px-4 py-3 min-h-[60px] resize-none"
          placeholder="Aparece en la pantalla de detalle. Ej: Mostrá tu QR al cajero al pagar."
          data-testid="benefit-howitworks-input"
        />
        </div>
        <div>
        <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Link de más info (opcional)</label>
        <input
          type="url"
          value={formData.more_info_url}
          onChange={(e) => setFormData({ ...formData, more_info_url: e.target.value })}
          className="input-brutal w-full px-4 py-3"
          placeholder="https://comercio.com"
          data-testid="benefit-moreinfourl-input"
        />
        </div>
      </div>

      <div className="benefit-modal-section">
        <div>
        <label className="block text-xs font-semibold mb-2 text-muted-foreground uppercase tracking-wide">Medios de pago aceptados</label>
        <div className="flex flex-wrap gap-2">
          <button
            type="button"
            onClick={toggleAllPM}
            className="px-3 py-1.5 text-xs font-semibold rounded-full border"
            style={allSelected
              ? (theme === 'dark'
                ? { backgroundColor: '#ffffff', color: '#111111', border: '1px solid #ffffff', boxShadow: 'none' }
                : { backgroundColor: '#111111', color: '#ffffff', border: '1px solid #111111', boxShadow: 'none' })
              : { backgroundColor: 'var(--surface)', color: 'var(--fg)', border: '1px solid var(--border)', boxShadow: 'none' }}
            data-testid="payment-toggle-all"
          >
            Todos
          </button>
          {PAYMENT_METHODS.map((p) => (
            <button
              key={p.value}
              type="button"
              onClick={() => togglePM(p.value)}
              className="px-3 py-1.5 text-xs font-medium rounded-full border"
              style={currentMethods.includes(p.value)
                ? (theme === 'dark'
                  ? { backgroundColor: '#ffffff', color: '#111111', border: '1px solid #ffffff', boxShadow: 'none' }
                  : { backgroundColor: '#111111', color: '#ffffff', border: '1px solid #111111', boxShadow: 'none' })
                : { backgroundColor: 'var(--surface)', color: 'var(--fg)', border: '1px solid var(--border)', boxShadow: 'none' }}
              data-testid={`payment-toggle-${p.value}`}
            >
              {p.label}
            </button>
          ))}
        </div>
        <p className="text-[10px] text-muted-foreground mt-1">Estos chips se mostrarán en la card del huésped.</p>
        </div>
      </div>

      <div className="benefit-modal-section">
        <div className="grid grid-cols-2 gap-3">
        <div>
          <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Imagen / banner</label>
          <div className="flex items-center gap-2">
            {formData.image_url ? (
              <img src={formData.image_url} alt="" className="w-16 h-16 rounded-lg border-2 border-foreground object-contain bg-white p-1 flex-shrink-0" />
            ) : (
              <div className="w-16 h-16 rounded-lg border-2 border-dashed border-foreground bg-muted flex items-center justify-center text-xs text-muted-foreground flex-shrink-0">—</div>
            )}
            <label className="flex-1 cursor-pointer">
              <input type="file" accept="image/*" onChange={onImage} className="hidden" data-testid="benefit-image-input" />
              <div className="px-3 py-2 border-2 border-foreground border-dashed rounded-lg text-center text-xs font-medium hover:bg-muted">
                {formData.image_url ? 'Cambiar' : 'Subir'}
              </div>
            </label>
            {formData.image_url && (
              <button type="button" onClick={() => setFormData(p => ({ ...p, image_url: '' }))} className="text-xs text-destructive">×</button>
            )}
          </div>
        </div>
        <div>
          <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Logo del comercio</label>
          <div className="flex items-center gap-2">
            {formData.business_logo ? (
              <img src={formData.business_logo} alt="" className="w-16 h-16 rounded-lg border-2 border-foreground object-contain bg-white p-1 flex-shrink-0" />
            ) : (
              <div className="w-16 h-16 rounded-lg border-2 border-dashed border-foreground bg-muted flex items-center justify-center text-xs text-muted-foreground flex-shrink-0">—</div>
            )}
            <label className="flex-1 cursor-pointer">
              <input type="file" accept="image/*" onChange={onLogo} className="hidden" data-testid="benefit-logo-input" />
              <div className="px-3 py-2 border-2 border-foreground border-dashed rounded-lg text-center text-xs font-medium hover:bg-muted">
                {formData.business_logo ? 'Cambiar' : 'Subir'}
              </div>
            </label>
            {formData.business_logo && (
              <button type="button" onClick={() => setFormData(p => ({ ...p, business_logo: '' }))} className="text-xs text-destructive">×</button>
            )}
          </div>
        </div>
        </div>
      </div>

      <div className="benefit-modal-section">
        <div>
        <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Casas destino (vacío = todas)</label>
        <div className="space-y-1 max-h-32 overflow-y-auto p-3 border-2 border-foreground rounded-lg bg-muted/30">
          {houses.map(h => (
            <label key={h.id} className="flex items-center gap-2 text-sm cursor-pointer">
              <input
                type="checkbox"
                checked={formData.target_houses.includes(h.id)}
                onChange={() => toggleHouse(h.id)}
                data-testid={`benefit-house-${h.id}`}
              />
              {h.name}
            </label>
          ))}
        </div>
      </div>
      </div>
    </>
  );
}

const _emptyBenefitForm = {
  title: '',
  description: '',
  discount: '',
  business_name: '',
  business_address: '',
  target_houses: [],
  valid_until: '',
  image_url: '',
  category: 'other',
  business_logo: '',
  branches: '',
  valid_days: '',
  uses_per_user: null,
  how_it_works: '',
  more_info_url: '',
  payment_methods: [],
};

const benefitPaymentLabels = {
  cash: 'Efectivo',
  debit: 'Débito',
  credit: 'Crédito',
  mp: 'Mercado Pago',
  transfer: 'Transferencia',
};

function BenefitPreviewModal({ formData, houses, theme, onClose }) {
  const targetHouses = formData.target_houses?.length
    ? formData.target_houses
      .map(houseId => houses.find(house => house.id === houseId)?.name)
      .filter(Boolean)
      .join(', ')
    : 'Todas las casas';
  const paymentMethods = (formData.payment_methods || [])
    .map(method => benefitPaymentLabels[method] || method)
    .filter(Boolean);

  return (
    <div className="fixed inset-0 bg-black/70 z-[60] modal-backdrop flex items-center justify-center p-4" data-testid="benefit-preview-modal">
      <style>{`
        [data-testid="benefit-preview-modal"] .benefit-preview-card {
          width: min(100%, 390px);
          overflow: visible;
          border: 1px solid var(--border);
          border-radius: 12px;
          background: var(--surface);
          box-shadow: none;
        }
        :root[data-theme="light"] [data-testid="benefit-preview-modal"] .benefit-preview-card {
          background: #ffffff;
          box-shadow: 0 18px 50px rgba(24, 32, 43, 0.16);
        }
        [data-testid="benefit-preview-modal"] .benefit-preview-visual {
          position: relative;
          height: 168px;
          overflow: visible;
          border-bottom: 1px solid var(--border);
          border-radius: 12px 12px 0 0;
          background: var(--surface-hover);
        }
        [data-testid="benefit-preview-modal"] .benefit-preview-banner,
        [data-testid="benefit-preview-modal"] .benefit-preview-placeholder {
          width: 100%;
          height: 100%;
          border-radius: 12px 12px 0 0;
        }
        [data-testid="benefit-preview-modal"] .benefit-preview-banner {
          display: block;
          object-fit: cover;
        }
        [data-testid="benefit-preview-modal"] .benefit-preview-placeholder {
          display: flex;
          align-items: center;
          justify-content: center;
          color: var(--muted-fg);
          background: linear-gradient(135deg, var(--surface-hover), var(--muted));
        }
        [data-testid="benefit-preview-modal"] .benefit-preview-discount {
          position: absolute;
          top: 12px;
          right: 12px;
          border: 1px solid rgba(21, 128, 61, 0.28);
          border-radius: 999px;
          background: rgba(220, 252, 231, 0.94);
          color: #15803d;
          font-size: 0.875rem;
          font-weight: 700;
          padding: 6px 10px;
        }
        [data-testid="benefit-preview-modal"] .benefit-preview-logo {
          position: absolute;
          left: 16px;
          bottom: -22px;
          display: flex;
          align-items: center;
          justify-content: center;
          width: 56px;
          height: 56px;
          overflow: hidden;
          padding: 0;
          border: none;
          border-radius: 14px;
          outline: none;
          background: transparent;
          box-shadow: none;
          z-index: 2;
        }
        [data-testid="benefit-preview-modal"] .benefit-preview-logo img {
          display: block;
          width: 100%;
          height: 100%;
          object-fit: cover;
          object-position: center;
        }
        [data-testid="benefit-preview-modal"] .benefit-preview-body {
          padding: 34px 18px 18px;
        }
        [data-testid="benefit-preview-modal"] .benefit-preview-description {
          color: var(--muted-fg);
          font-size: 0.875rem;
          line-height: 1.45;
        }
        [data-testid="benefit-preview-modal"] .benefit-preview-meta {
          color: var(--muted-fg);
          font-size: 0.75rem;
        }
      `}</style>
      <div className="benefit-preview-card" role="dialog" aria-modal="true" aria-labelledby="benefit-preview-title">
        <div className="benefit-preview-visual">
          {formData.image_url ? (
            <img src={formData.image_url} alt="" className="benefit-preview-banner" />
          ) : (
            <div className="benefit-preview-placeholder"><Gift className="h-10 w-10" /></div>
          )}
          {formData.discount && <span className="benefit-preview-discount">{formData.discount}</span>}
          <div className="benefit-preview-logo">
            {formData.business_logo ? <img src={formData.business_logo} alt="" /> : <Gift className="h-6 w-6 text-muted-foreground" />}
          </div>
        </div>
        <div className="benefit-preview-body">
          <h2 id="benefit-preview-title" className="font-heading font-bold text-xl">
            {formData.title || 'Título del beneficio'}
          </h2>
          <p className="mt-1 text-sm font-semibold">{formData.business_name || 'Nombre del comercio'}</p>
          {formData.description && <p className="benefit-preview-description mt-3">{formData.description}</p>}
          {formData.business_address && <p className="benefit-preview-meta mt-3">{formData.business_address}</p>}
          {paymentMethods.length > 0 && (
            <div className="flex flex-wrap gap-1.5 mt-3">
              {paymentMethods.map(method => (
                <span key={method} className="text-xs px-2 py-1 rounded-full border" style={{ borderColor: 'var(--border)', color: 'var(--muted-fg)' }}>
                  {method}
                </span>
              ))}
            </div>
          )}
          <p className="benefit-preview-meta mt-3"><strong>Disponible para:</strong> {targetHouses}</p>
          <button
            type="button"
            onClick={onClose}
            className="w-full mt-5 py-3 rounded-lg font-semibold"
            style={theme === 'dark'
              ? { backgroundColor: '#ffffff', color: '#111111', border: '1px solid #ffffff', boxShadow: 'none' }
              : { backgroundColor: '#111111', color: '#ffffff', border: '1px solid #111111', boxShadow: 'none' }}
          >
            Cerrar
          </button>
        </div>
      </div>
    </div>
  );
}

function NewBenefitModal({ onClose, onSubmit, houses }) {
  const { theme } = useTheme();
  const [formData, setFormData] = useState(_emptyBenefitForm);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const { cropImage, cropperNode } = useImageCropper();

  const handleImg = async (e, key, w, h) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!f) return;
    try {
      const aspect = key === 'business_logo' ? 1 : 4 / 3;
      const cropped = await cropImage(f, {
        aspect,
        title: key === 'business_logo' ? 'Encuadrar logo' : 'Encuadrar imagen',
      });
      if (!cropped) return;
      const compressed = await compressImage(cropped, w, h, 0.75);
      setFormData(p => ({ ...p, [key]: compressed }));
    } catch {
      setError('Error al procesar la imagen');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al crear beneficio');
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/70 z-50 modal-backdrop" data-testid="new-benefit-modal">
      {cropperNode}
      <style>{`
        [data-testid="new-benefit-modal"] [data-testid="submit-benefit-button"],
        [data-testid="new-benefit-modal"] [data-testid="submit-benefit-button"]:hover,
        [data-testid="new-benefit-modal"] [data-testid="submit-benefit-button"]:active {
          background-color: #111111 !important;
          color: #ffffff !important;
          border: 1px solid #111111 !important;
          box-shadow: none !important;
          transform: none !important;
        }
        :root[data-theme="dark"] [data-testid="new-benefit-modal"] [data-testid="submit-benefit-button"],
        :root[data-theme="dark"] [data-testid="new-benefit-modal"] [data-testid="submit-benefit-button"]:hover,
        :root[data-theme="dark"] [data-testid="new-benefit-modal"] [data-testid="submit-benefit-button"]:active {
          background-color: #ffffff !important;
          color: #111111 !important;
          border: 1px solid #ffffff !important;
        }
        [data-testid="new-benefit-modal"] .benefit-modal-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          box-shadow: none;
        }
        :root[data-theme="light"] [data-testid="new-benefit-modal"] .benefit-modal-card {
          background: #ffffff;
          box-shadow: 0 18px 50px rgba(24, 32, 43, 0.16);
        }
        [data-testid="new-benefit-modal"] .benefit-modal-title,
        [data-testid="new-benefit-modal"] .benefit-modal-section label {
          letter-spacing: 0.04em;
        }
        [data-testid="new-benefit-modal"] .benefit-modal-title {
          letter-spacing: normal;
        }
        [data-testid="new-benefit-modal"] .benefit-modal-fields {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        [data-testid="new-benefit-modal"] .benefit-modal-section {
          padding: 14px;
          background: var(--surface-hover);
          border: 1px solid var(--border);
          border-radius: 12px;
          box-shadow: none;
        }
        :root[data-theme="light"] [data-testid="new-benefit-modal"] .benefit-modal-section {
          background: #ffffff;
        }
        [data-testid="new-benefit-modal"] .benefit-modal-section > div + div {
          margin-top: 12px;
        }
        [data-testid="new-benefit-modal"] .benefit-modal-section .input-brutal {
          border: 1px solid var(--border);
          border-radius: 10px;
          background: var(--surface);
          box-shadow: none;
        }
        [data-testid="new-benefit-modal"] .benefit-modal-section .border-2 {
          border-width: 1px;
          border-color: var(--border);
        }
        [data-testid="new-benefit-modal"] .benefit-modal-footer {
          border-top: 1px solid var(--border);
          padding-top: 16px;
        }
      `}</style>
      <div
        className="bg-surface border rounded-xl w-full max-w-lg overflow-y-auto modal-card benefit-modal-card"
        style={theme === 'dark'
          ? {
              backgroundColor: 'var(--surface)',
              border: '1px solid var(--border)',
              boxShadow: 'none',
            }
          : { border: '1px solid var(--border)' }}
      >
        <div className="p-6">
          <h2 className="benefit-modal-title font-heading font-bold text-xl mb-6">Nuevo Beneficio</h2>
          <form onSubmit={handleSubmit} className="benefit-modal-form space-y-4">
            <div className="benefit-modal-fields">
              <BenefitFormFields
                formData={formData}
                setFormData={setFormData}
                houses={houses}
                onImage={(e) => handleImg(e, 'image_url', 800, 600)}
                onLogo={(e) => handleImg(e, 'business_logo', 256, 256)}
                error={error}
              />
            </div>
            <div className="benefit-modal-footer flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 rounded-lg font-semibold hover:bg-muted"
                style={theme === 'dark'
                  ? { backgroundColor: 'var(--surface)', color: 'var(--fg)', border: '1px solid var(--border)', boxShadow: 'none' }
                  : { backgroundColor: '#ffffff', color: '#111111', border: '1px solid var(--border)', boxShadow: 'none' }}
                data-testid="cancel-benefit-button"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                className="flex-1 py-3 rounded-lg font-semibold"
                style={{ backgroundColor: 'var(--surface)', color: 'var(--fg)', border: '1px solid var(--border)', boxShadow: 'none' }}
                data-testid="preview-new-benefit-button"
              >
                Vista previa
              </button>
              <button
                type="submit"
                disabled={loading}
                className="users-new-modal-submit btn-brutal flex-1 py-3 flex items-center justify-center gap-2 disabled:opacity-50"
                data-testid="submit-benefit-button"
              >
                {loading ? 'Creando...' : 'Crear'}
              </button>
            </div>
          </form>
        </div>
      </div>
      </div>
      {showPreview && <BenefitPreviewModal formData={formData} houses={houses} theme={theme} onClose={() => setShowPreview(false)} />}
    </>
  );
}



function AnalyticsSection() {
  const { theme } = useTheme();
  const [data, setData] = useState(null);
  const [occupancyData, setOccupancyData] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      const [analyticsResult, occupancyResult] = await Promise.allSettled([
        api.getAnalytics(),
        api.getOccupancy(),
      ]);

      if (analyticsResult.status === 'fulfilled') {
        setData(analyticsResult.value);
      } else {
        console.error(analyticsResult.reason);
      }

      if (occupancyResult.status === 'fulfilled' && Array.isArray(occupancyResult.value)) {
        setOccupancyData(occupancyResult.value);
      } else if (occupancyResult.status === 'rejected') {
        console.error(occupancyResult.reason);
      }

      setLoading(false);
    })();
  }, []);

  if (loading) return <LoadingIndicator label="Cargando analítica..." className="py-8" />;
  if (!data) return <div className="admin-analytics-card p-8 text-center text-muted-foreground">No se pudo cargar</div>;

  const CLAIM_TYPE_COLORS = {
    Convivencia: '#a78bfa',
    Limpieza: '#60a5fa',
    Otro: '#34d399',
    Reparación: '#facc15',
  };
  const FALLBACK_CLAIM_TYPE_COLORS = ['#fb7185', '#2dd4bf'];
  const claimTypeLabels = { repair: 'Reparación', cleaning: 'Limpieza', noise: 'Convivencia', other: 'Otro' };
  const claimTypeTotal = data.claim_type_distribution.reduce((total, item) => total + item.count, 0);
  const formatClaimTypePercentage = (value) => `${Number.isInteger(value) ? value : value.toFixed(1)}%`;
  const usedFallbackColors = new Set();
  const claimTypeData = data.claim_type_distribution.map(d => {
    const name = claimTypeLabels[d.type] || d.type;
    const color = CLAIM_TYPE_COLORS[name]
      || FALLBACK_CLAIM_TYPE_COLORS.find(fallbackColor => !usedFallbackColors.has(fallbackColor))
      || FALLBACK_CLAIM_TYPE_COLORS[0];

    if (!CLAIM_TYPE_COLORS[name]) usedFallbackColors.add(color);

    return {
      name,
      value: claimTypeTotal > 0 ? (d.count / claimTypeTotal) * 100 : 0,
      count: d.count,
      color,
    };
  });
  const renderClaimTypeLabel = ({ value, cx, cy, midAngle, innerRadius, outerRadius }) => {
    const radius = innerRadius + (outerRadius - innerRadius) * 0.55;
    const angle = -midAngle * (Math.PI / 180);
    const x = cx + radius * Math.cos(angle);
    const y = cy + radius * Math.sin(angle);

    return (
      <text
        x={x}
        y={y}
        fill="#111827"
        textAnchor="middle"
        dominantBaseline="middle"
        fontWeight={600}
        pointerEvents="none"
      >
        {formatClaimTypePercentage(value)}
      </text>
    );
  };
  const occupancyByHouse = new Map(occupancyData.map((house) => [house.house_id, house]));
  const occupancyChartData = data.occupancy.map((house) => {
    const occupancy = occupancyByHouse.get(house.house_id);
    return {
      ...house,
      occupied_now: occupancy?.occupied_now ?? 0,
      total_rooms: occupancy?.total_rooms ?? 0,
      occupancy_pct: occupancy?.occupancy_pct ?? 0,
    };
  });

  return (
    <div className="space-y-6 animate-enter" data-testid="admin-analytics-section">
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-2xl">Analítica</h1>
        {data.avg_resolution_hours != null && (
          <div className="admin-analytics-card px-4 py-2 text-sm">
            <span className="text-muted-foreground">Tiempo prom. resolución: </span>
            <span className="font-heading font-bold">{data.avg_resolution_hours}h</span>
          </div>
        )}
      </div>

      {}
      <div className="admin-analytics-card p-6">
        <h2 className="font-heading font-semibold mb-4">Ocupación por casa</h2>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart data={occupancyChartData} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
            <XAxis dataKey="house_name" stroke="#9ca3af" tick={{ fontSize: 12 }} />
            <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} />
            <Tooltip contentStyle={{ background: '#111', border: '2px solid #facc15', borderRadius: 8 }} />
            <Legend />
            <Bar dataKey="occupied_now" fill="#34d399" name="Habitaciones ocupadas" radius={[6, 6, 0, 0]} />
            <Bar dataKey="total_rooms" fill="#facc15" name="Habitaciones totales" radius={[6, 6, 0, 0]} />
          </BarChart>
        </ResponsiveContainer>
        <div className="grid md:grid-cols-3 gap-3 mt-4">
          {occupancyChartData.map(o => (
            <div key={o.house_id} className="p-3 bg-muted rounded-lg">
              <p className="font-medium text-sm">{o.house_name}</p>
              <p className="text-2xl font-heading font-bold">{o.occupancy_pct}%</p>
              <p className="text-xs text-muted-foreground">{o.occupied_now}/{o.total_rooms} habitaciones ocupadas</p>
            </div>
          ))}
        </div>
      </div>

      {}
      <div className="admin-analytics-card p-6">
        <h2 className="font-heading font-semibold mb-4">Tendencia últimos 6 meses</h2>
        <ResponsiveContainer width="100%" height={260}>
          <LineChart data={data.monthly_trend} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
            <XAxis dataKey="month_name" stroke="#9ca3af" tick={{ fontSize: 12 }} />
            <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} />
            <Tooltip contentStyle={{ background: '#111', border: '2px solid #facc15', borderRadius: 8 }} />
            <Legend />
            <Line type="monotone" dataKey="new_guests" stroke="#34d399" strokeWidth={3} name="Nuevos huéspedes" />
            <Line type="monotone" dataKey="new_claims" stroke="#fb7185" strokeWidth={3} name="Nuevos reclamos" />
          </LineChart>
        </ResponsiveContainer>
      </div>

      {}
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="admin-analytics-card p-6">
          <h2 className="font-heading font-semibold mb-4">Reclamos por tipo</h2>
          {claimTypeData.length === 0 ? (
            <p className="text-muted-foreground text-center py-8">Sin datos</p>
          ) : (
            <ResponsiveContainer width="100%" height={240}>
              <PieChart margin={{ top: 12, right: 24, bottom: 44, left: 24 }}>
                <Pie
                  data={claimTypeData}
                  dataKey="value"
                  nameKey="name"
                  cx="50%"
                  cy="60%"
                  outerRadius={80}
                  isAnimationActive={false}
                  label={renderClaimTypeLabel}
                  labelLine={false}
                >
                  {claimTypeData.map((entry, idx) => (
                    <Cell key={`c-${idx}`} fill={entry.color} />
                  ))}
                </Pie>
                <Tooltip
                  formatter={(_, name, item) => [item?.payload?.count ?? 0, name]}
                  contentStyle={{ background: '#111', border: '2px solid #facc15', borderRadius: 8 }}
                />
                <Legend verticalAlign="bottom" height={0} wrapperStyle={{ transform: 'translateY(18px)' }} />
              </PieChart>
            </ResponsiveContainer>
          )}
        </div>

        <div className="admin-analytics-card p-6">
          <h2 className="font-heading font-semibold mb-4">Reclamos por casa</h2>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={data.claims_by_house} margin={{ top: 8, right: 16, bottom: 0, left: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.08)" />
              <XAxis dataKey="house_name" stroke="#9ca3af" tick={{ fontSize: 11 }} />
              <YAxis stroke="#9ca3af" tick={{ fontSize: 12 }} />
              <Tooltip contentStyle={{ background: '#111', border: '2px solid #facc15', borderRadius: 8 }} />
              <Legend />
              <Bar dataKey="pending" stackId="a" fill="#fb923c" name="Pendiente" />
              <Bar dataKey="in_progress" stackId="a" fill="#facc15" name="En progreso" />
              <Bar dataKey="resolved" stackId="a" fill="#34d399" name="Resuelto" />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </div>
    </div>
  );
}


function EditHouseModal({ house, onClose, onSubmit }) {
  const [formData, setFormData] = useState({
    name: house.name,
    address: house.address,
    city: house.city,
    total_rooms: String(house.total_rooms),
    amenities: (house.amenities || []).join(', '),
    cover_photo: house.cover_photo || '',
    shared_room_capacity: house.shared_room_capacity || 2,
    visit_cost_per_night: house.visit_cost_per_night === null || house.visit_cost_per_night === undefined
      ? ''
      : String(house.visit_cost_per_night),
    visit_cost_currency: house.visit_cost_currency || 'ARS',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [globalVisitCost, setGlobalVisitCost] = useState(0);
  const [globalVisitCurrency, setGlobalVisitCurrency] = useState('ARS');
  const { cropImage, cropperNode } = useImageCropper();

  useEffect(() => {
    (async () => {
      try {
        const s = await api.getSettings();
        setGlobalVisitCost(s.visit_cost_per_night ?? 0);
        setGlobalVisitCurrency(s.visit_cost_currency || 'ARS');
      } catch (e) { /* usa defaults */ }
    })();
  }, []);

  const handleImage = async (e) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!f) return;
    try {
      const cropped = await cropImage(f, { aspect: 3 / 2, title: 'Encuadrar portada' });
      if (!cropped) return;
      const compressed = await compressImage(cropped, 1200, 800, 0.75);
      setFormData(p => ({ ...p, cover_photo: compressed }));
    } catch (err) {
      setError('Error al procesar la imagen');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = {
        name: formData.name,
        address: formData.address,
        city: formData.city,
        total_rooms: parseInt(formData.total_rooms),
        shared_room_capacity: parseInt(formData.shared_room_capacity) || 2,
        visit_cost_per_night: formData.visit_cost_per_night === '' ? null : parseInt(formData.visit_cost_per_night) || 0,
        visit_cost_currency: formData.visit_cost_currency || 'ARS',
        amenities: formData.amenities.split(',').map(a => a.trim()).filter(Boolean),
        cover_photo: formData.cover_photo,
      };
      // No reenviar la portada si sigue siendo la URL ya resuelta (pisaría la key de GCS)
      if (payload.cover_photo && payload.cover_photo.startsWith('http')) {
        delete payload.cover_photo;
      }
      await onSubmit(payload);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al actualizar');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 modal-backdrop" data-testid="edit-house-modal">
      {cropperNode}
      <style>{`
        [data-testid="edit-house-modal"] .house-modal-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          box-shadow: none;
        }
        :root[data-theme="light"] [data-testid="edit-house-modal"] .house-modal-card {
          background: #ffffff;
        }
        [data-testid="edit-house-modal"] .input-brutal,
        [data-testid="edit-house-modal"] .border-2 {
          border-width: 1px;
          border-color: var(--border);
          box-shadow: none;
        }
        [data-testid="edit-house-modal"] [data-testid="submit-edit-house"],
        [data-testid="edit-house-modal"] [data-testid="submit-edit-house"]:hover,
        [data-testid="edit-house-modal"] [data-testid="submit-edit-house"]:active {
          background-color: #111111 !important;
          color: #ffffff !important;
          border: 1px solid #111111 !important;
          box-shadow: none !important;
          transform: none !important;
        }
        :root[data-theme="dark"] [data-testid="edit-house-modal"] [data-testid="submit-edit-house"],
        :root[data-theme="dark"] [data-testid="edit-house-modal"] [data-testid="submit-edit-house"]:hover,
        :root[data-theme="dark"] [data-testid="edit-house-modal"] [data-testid="submit-edit-house"]:active {
          background-color: #ffffff !important;
          color: #111111 !important;
          border-color: var(--border) !important;
        }
        [data-testid="edit-house-modal"] .house-modal-cancel {
          background: #ffffff;
          color: #111111;
          border: 1px solid var(--border);
          box-shadow: none;
        }
        :root[data-theme="dark"] [data-testid="edit-house-modal"] .house-modal-cancel {
          background: var(--surface);
          color: var(--fg);
        }
      `}</style>
      <div className="bg-surface border rounded-xl w-full max-w-lg overflow-y-auto modal-card house-modal-card">
        <div className="p-6">
          <h2 className="font-heading font-bold text-xl mb-6">Editar Casa</h2>
          {error && <div className="mb-4 p-3 bg-destructive/20 border border-destructive rounded-lg text-sm text-destructive">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-4">
            <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-brutal w-full px-4 py-3" placeholder="Nombre" required data-testid="edit-house-name" />
            <input type="text" value={formData.address} onChange={(e) => setFormData({...formData, address: e.target.value})} className="input-brutal w-full px-4 py-3" placeholder="Dirección" required data-testid="edit-house-address" />
            <input type="text" value={formData.city} onChange={(e) => setFormData({...formData, city: e.target.value})} className="input-brutal w-full px-4 py-3" placeholder="Ciudad" required data-testid="edit-house-city" />
            <input type="number" min="1" value={formData.total_rooms} onChange={(e) => setFormData({...formData, total_rooms: e.target.value})} className="input-brutal w-full px-4 py-3" placeholder="Habitaciones" required data-testid="edit-house-rooms" />
            <div>
              <label className="block text-sm font-medium mb-2 text-foreground">Foto de portada</label>
              <div className="flex items-center gap-3">
                {formData.cover_photo && (
                  <img src={formData.cover_photo} alt="preview" className="w-24 h-16 rounded-lg border-2 border-foreground object-cover" data-testid="edit-house-cover-preview" />
                )}
                <label className="flex-1 cursor-pointer">
                  <input type="file" accept="image/*" onChange={handleImage} className="hidden" data-testid="edit-house-cover-input" />
                  <div className="px-4 py-3 border-2 border-foreground border-dashed rounded-lg text-center text-sm font-medium hover:bg-muted">
                    {formData.cover_photo ? 'Cambiar foto' : 'Subir foto (opcional)'}
                  </div>
                </label>
                {formData.cover_photo && (
                  <button type="button" onClick={() => setFormData(p => ({ ...p, cover_photo: '' }))} className="px-3 py-3 border-2 border-foreground rounded-lg text-sm hover:bg-muted" data-testid="edit-house-cover-remove">
                    Quitar
                  </button>
                )}
              </div>
            </div>
            <input type="text" value={formData.amenities} onChange={(e) => setFormData({...formData, amenities: e.target.value})} className="input-brutal w-full px-4 py-3" placeholder="Amenities (coma-separados)" data-testid="edit-house-amenities" />
            <div>
              <label className="block text-sm font-medium mb-1 text-foreground">Capacidad por habitación compartida *</label>
              <input
                type="number"
                min={1}
                max={10}
                value={formData.shared_room_capacity}
                onChange={(e) => setFormData({...formData, shared_room_capacity: e.target.value})}
                className="input-brutal w-full px-4 py-3"
                data-testid="edit-house-shared-capacity"
              />
              <p className="text-xs text-muted-foreground mt-1">Casas chicas hasta 2 personas; Córdoba / Nueva Córdoba hasta 3.</p>
            </div>
            <div>
              <label className="block text-sm font-medium mb-1 text-foreground">Precio de visita por noche</label>
              <div className="flex gap-2">
                <input
                  type="number"
                  min="0"
                  value={formData.visit_cost_per_night}
                  onChange={(e) => setFormData({...formData, visit_cost_per_night: e.target.value})}
                  className="input-brutal flex-1 px-4 py-3"
                  placeholder={`Heredado (${globalVisitCurrency} ${globalVisitCost})`}
                  data-testid="edit-house-visit-cost"
                />
                <select
                  value={formData.visit_cost_currency}
                  onChange={(e) => setFormData({...formData, visit_cost_currency: e.target.value})}
                  className="input-brutal px-3 py-3"
                  disabled={formData.visit_cost_per_night === ''}
                  data-testid="edit-house-visit-currency"
                >
                  {['ARS', 'USD', 'EUR'].map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
              <p className="text-xs text-muted-foreground mt-1">
                Vacío = hereda el precio global ({globalVisitCurrency} {globalVisitCost}).
              </p>
            </div>
            <div className="flex gap-3 pt-2">
              <button type="button" onClick={onClose} className="house-modal-cancel flex-1 py-3 rounded-lg hover:bg-muted">Cancelar</button>
              <button type="submit" disabled={loading} className="btn-brutal flex-1 py-3 disabled:opacity-50" data-testid="submit-edit-house">
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}


function EditAnnouncementModal({ announcement, onClose, onSubmit, houses }) {
  const [formData, setFormData] = useState({
    title: announcement.title,
    content: announcement.content,
    priority: announcement.priority,
    target_houses: announcement.target_houses || [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleHouse = (id) => {
    setFormData(previous => ({
      ...previous,
      target_houses: previous.target_houses.includes(id)
        ? previous.target_houses.filter(houseId => houseId !== id)
        : [...previous.target_houses, id],
    }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error');
      setLoading(false);
    }
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-3 backdrop-blur-[2px] sm:p-6"
      style={{ backgroundColor: 'rgba(0, 0, 0, 0.45)' }}
      data-testid="edit-announcement-modal"
    >
      <style>{`
        [data-testid="edit-announcement-modal"] [data-testid="submit-edit-announcement"],
        [data-testid="edit-announcement-modal"] [data-testid="submit-edit-announcement"]:hover,
        [data-testid="edit-announcement-modal"] [data-testid="submit-edit-announcement"]:active {
          background-color: #111111 !important;
          color: #ffffff !important;
          border-color: #111111 !important;
          box-shadow: none !important;
        }
        :root[data-theme="dark"] [data-testid="edit-announcement-modal"] [data-testid="submit-edit-announcement"],
        :root[data-theme="dark"] [data-testid="edit-announcement-modal"] [data-testid="submit-edit-announcement"]:hover,
        :root[data-theme="dark"] [data-testid="edit-announcement-modal"] [data-testid="submit-edit-announcement"]:active {
          background-color: #ffffff !important;
          color: #111111 !important;
          border-color: var(--border) !important;
        }
      `}</style>
      <div className="max-h-[calc(100vh-1.5rem)] w-full max-w-3xl overflow-y-auto overscroll-contain rounded-2xl border border-border bg-surface shadow-2xl sm:max-h-[calc(100vh-3rem)]">
        <div className="p-5 sm:p-8">
          <div className="mb-6 flex items-start justify-between gap-4">
            <div>
              <h2 className="font-heading text-xl font-bold text-foreground">Editar comunicado</h2>
              <p className="mt-1 text-sm text-muted-foreground">Actualizá el contenido y sus destinatarios.</p>
            </div>
            <button type="button" onClick={onClose} className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-muted-foreground transition hover:bg-muted hover:text-foreground" aria-label="Cerrar">
              <X className="h-5 w-5" />
            </button>
          </div>

          {error && <div className="mb-5 rounded-xl border border-destructive/30 bg-destructive/10 p-3 text-sm text-destructive">{error}</div>}

          <form onSubmit={handleSubmit} className="space-y-6">
            <div>
              <label className="mb-2 block text-sm font-semibold text-foreground">Título</label>
              <input
                type="text"
                value={formData.title}
                onChange={(event) => setFormData({...formData, title: event.target.value})}
                className="h-11 w-full rounded-xl border border-border bg-background px-4 text-sm text-foreground outline-none transition placeholder:text-muted-foreground focus:border-amber-400 focus:ring-4 focus:ring-amber-100/70 dark:focus:ring-amber-900/20"
                placeholder="Título del comunicado"
                required
                data-testid="edit-announcement-title"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-foreground">Contenido</label>
              <textarea
                value={formData.content}
                onChange={(event) => setFormData({...formData, content: event.target.value})}
                className="min-h-[150px] w-full resize-none rounded-xl border border-border bg-background px-4 py-3 text-sm leading-6 text-foreground outline-none transition placeholder:text-muted-foreground focus:border-amber-400 focus:ring-4 focus:ring-amber-100/70 dark:focus:ring-amber-900/20"
                placeholder="Contenido del comunicado"
                required
                data-testid="edit-announcement-content"
              />
            </div>

            <div>
              <label className="mb-2 block text-sm font-semibold text-foreground">Prioridad</label>
              <div className="flex flex-wrap gap-2">
                {Object.entries(announcementPriorityMeta).map(([value, meta]) => (
                  <button
                    key={value}
                    type="button"
                    onClick={() => setFormData({...formData, priority: value})}
                    className={`flex min-h-10 shrink-0 items-center justify-center gap-2 whitespace-nowrap rounded-xl border px-3 py-2 text-xs font-semibold transition ${
                      formData.priority === value
                        ? `${meta.badge} ring-2 ring-current/10`
                        : 'border-border bg-surface text-muted-foreground hover:bg-muted hover:text-foreground'
                    }`}
                  >
                    {(() => {
                      const PriorityIcon = announcementPriorityIcons[value] || Info;
                      return <PriorityIcon className={`h-4 w-4 shrink-0 ${announcementPriorityIconColors[value]}`} aria-hidden="true" />;
                    })()}
                    {value === 'multa' ? 'Multa' : meta.label}
                  </button>
                ))}
              </div>
            </div>

            {houses && houses.length > 0 && (
              <div>
                <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                  <label className="text-sm font-semibold text-foreground">Casas destino</label>
                  <span className="text-xs text-muted-foreground">Sin selección se envía a todas</span>
                </div>
                <div className="max-h-40 space-y-1 overflow-y-auto rounded-xl border border-border bg-background p-2">
                  {houses.map(house => (
                    <label key={house.id} className="flex cursor-pointer items-center gap-3 rounded-lg p-2 text-sm text-foreground transition hover:bg-muted">
                      <input type="checkbox" checked={formData.target_houses.includes(house.id)} onChange={() => toggleHouse(house.id)} />
                      <span>{house.name}</span>
                      {house.city && <span className="ml-auto text-xs text-muted-foreground">{house.city}</span>}
                    </label>
                  ))}
                </div>
              </div>
            )}

            <div className="flex flex-col-reverse gap-3 border-t border-border pt-5 sm:flex-row sm:justify-end">
              <button type="button" onClick={onClose} className="h-11 rounded-xl border border-border px-6 text-sm font-semibold text-foreground transition-colors hover:bg-muted sm:min-w-36">Cancelar</button>
              <button type="submit" disabled={loading} className="inline-flex h-11 items-center justify-center rounded-xl bg-slate-950 px-6 text-sm font-semibold text-white shadow-sm transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-50 dark:bg-primary dark:text-primary-foreground sm:min-w-44" data-testid="submit-edit-announcement">
                {loading ? 'Guardando...' : 'Guardar cambios'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}

function EditUserModal({ userData, houses, onClose, onSubmit }) {
  const isStaff = userData.role === 'admin' || userData.role === 'house_manager' || userData.role === 'maintenance';
  const [formData, setFormData] = useState({
    name: userData.name || '',
    email: userData.email || '',
    house_id: userData.house_id || '',
    room_number: userData.room_number || '',
    room_type: userData.room_type || 'shared',
    lease_start: (userData.lease_start || '').split('T')[0],
    lease_end: (userData.lease_end || '').split('T')[0],
    phone: userData.phone || '',
    assigned_house_ids: userData.assigned_house_ids || [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const toggleAssignedHouse = (id) => {
    setFormData((p) => {
      const set = new Set(p.assigned_house_ids);
      if (set.has(id)) set.delete(id); else set.add(id);
      return { ...p, assigned_house_ids: Array.from(set) };
    });
  };

  const roleLabel = userData.role === 'admin' ? 'Admin' :
                    userData.role === 'house_manager' ? 'House Manager' :
                    userData.role === 'maintenance' ? 'Mantenimiento' : 'Huésped';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (userData.role === 'house_manager' && formData.assigned_house_ids.length === 0) {
      setError('El house manager debe tener al menos una casa asignada');
      return;
    }
    setLoading(true);
    try {
      const payload = { name: formData.name };
      const newEmail = (formData.email || '').trim().toLowerCase();
      if (newEmail && newEmail !== (userData.email || '').toLowerCase()) {
        payload.email = newEmail;
      }
      if (isStaff) {
        payload.assigned_house_ids = formData.assigned_house_ids;
      } else {
        payload.house_id = formData.house_id;
        payload.room_number = formData.room_number;
        payload.room_type = formData.room_type;
        payload.lease_start = formData.lease_start;
        payload.lease_end = formData.lease_end;
        payload.phone = formData.phone.trim();
      }
      await onSubmit(payload);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error al actualizar');
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 modal-backdrop users-edit-modal" data-testid="edit-user-modal">
      <style>{`
        [data-testid="edit-user-modal"] .users-edit-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 16px;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.16);
        }
        :root[data-theme="dark"] [data-testid="edit-user-modal"] .users-edit-card {
          background: var(--surface);
          border-color: var(--border);
          box-shadow: none;
        }
        [data-testid="edit-user-modal"] .input-brutal {
          border: 1px solid var(--border) !important;
          border-radius: 10px;
          background: var(--surface);
          box-shadow: none !important;
          outline: none;
        }
        [data-testid="edit-user-modal"] .input-brutal:focus {
          border-color: var(--primary) !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.14) !important;
        }
        [data-testid="edit-user-modal"] .border-2 {
          border-width: 1px;
        }
        [data-testid="edit-user-modal"] .shadow-brutal-sm {
          box-shadow: none;
        }
        [data-testid="edit-user-modal"] .users-modal-cancel,
        [data-testid="edit-user-modal"] .users-modal-submit {
          border-radius: 10px;
          box-shadow: none !important;
          font-weight: 600;
        }
        [data-testid="edit-user-modal"] .users-modal-cancel {
          border: 1px solid var(--border);
          background: var(--surface);
          color: var(--fg);
        }
        [data-testid="edit-user-modal"] .users-modal-submit {
          border: 1px solid #111111 !important;
          background: #111111 !important;
          color: #ffffff !important;
        }
        :root[data-theme="dark"] [data-testid="edit-user-modal"] .users-modal-submit {
          border-color: #ffffff !important;
          background: #ffffff !important;
          color: #111111 !important;
        }
      `}</style>
      <div className="users-edit-card bg-surface border-2 border-foreground rounded-xl w-full max-w-lg overflow-y-auto modal-card">
        <div className="p-6">
          <div className="flex items-center justify-between mb-1">
            <h2 className="font-heading font-bold text-xl">Editar usuario</h2>
            <span className="text-xs px-2 py-1 bg-muted border border-border rounded-full font-semibold">{roleLabel}</span>
          </div>
          <p className="text-sm text-muted-foreground mb-6">{userData.email}</p>
          {error && <div className="mb-4 p-3 bg-destructive/20 border border-destructive rounded-lg text-sm">{error}</div>}
          {!isStaff && (
            <div className="mb-4 p-3 rounded-lg bg-muted/40 border border-border text-sm space-y-1" data-testid="user-profile-details">
              <p className="text-[10px] uppercase tracking-wide font-bold text-muted-foreground mb-1">Datos personales del huésped</p>
              <p><span className="text-muted-foreground">País:</span> <span className={`font-medium ${userData.country ? 'text-foreground' : 'text-muted-foreground italic'}`}>{userData.country || userData.nationality || 'Sin cargar'}</span></p>
              <p><span className="text-muted-foreground">Estudios:</span> <span className={`font-medium ${userData.studies ? 'text-foreground' : 'text-muted-foreground italic'}`}>{userData.studies || 'Sin cargar'}</span></p>
              <p><span className="text-muted-foreground">Universidad:</span> <span className={`font-medium ${userData.university ? 'text-foreground' : 'text-muted-foreground italic'}`}>{userData.university || 'Sin cargar'}</span></p>
              <p><span className="text-muted-foreground">Intereses:</span> <span className={`font-medium ${userData.interests ? 'text-foreground' : 'text-muted-foreground italic'}`}>{userData.interests || 'Sin cargar'}</span></p>
              {userData.instagram && <p><span className="text-muted-foreground">Instagram:</span> <span className="text-foreground font-medium">@{userData.instagram}</span></p>}
              <p className="text-[10px] text-muted-foreground pt-1 italic">Los datos los carga el huésped desde su perfil en la app.</p>
            </div>
          )}
          <form onSubmit={handleSubmit} className="space-y-4">
            <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="input-brutal w-full px-4 py-3" placeholder="Nombre" required data-testid="edit-user-name" />

            <div>
              <label className="block text-xs font-semibold mb-1 text-muted-foreground uppercase tracking-wide">Email</label>
              <input
                type="email"
                value={formData.email}
                onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                className="input-brutal w-full px-4 py-3"
                placeholder="usuario@dominio.com"
                data-testid="edit-user-email"
              />
              <p className="text-[10px] text-muted-foreground mt-1">Si lo cambiás, el usuario deberá usar este nuevo email para ingresar.</p>
            </div>

            {isStaff ? (
              <div>
                <label className="block text-sm font-medium mb-2">Casas asignadas {userData.role === 'admin' ? '(opcional · vacío = acceso global)' : '*'}</label>
                <div className="space-y-2 max-h-56 overflow-y-auto p-3 border-2 border-foreground rounded-lg bg-background" data-testid="edit-assigned-houses">
                  {houses.map((h) => (
                    <label key={h.id} className="flex items-center gap-3 p-2 rounded-lg hover:bg-muted cursor-pointer">
                      <input
                        type="checkbox"
                        checked={formData.assigned_house_ids.includes(h.id)}
                        onChange={() => toggleAssignedHouse(h.id)}
                        className="w-4 h-4"
                        data-testid={`edit-assigned-house-${h.id}`}
                      />
                      <div className="flex-1">
                        <p className="font-medium text-sm">{h.name}</p>
                        <p className="text-xs text-muted-foreground">{h.city || h.address}</p>
                      </div>
                    </label>
                  ))}
                </div>
              </div>
            ) : (
              <>
                <div className="grid grid-cols-2 gap-3">
                  <select value={formData.house_id} onChange={(e) => setFormData({...formData, house_id: e.target.value})} className="input-brutal w-full px-4 py-3" data-testid="edit-user-house">
                    <option value="">Sin casa</option>
                    {houses.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
                  </select>
                  <input type="text" value={formData.room_number} onChange={(e) => setFormData({...formData, room_number: e.target.value})} className="input-brutal w-full px-4 py-3" placeholder="Habitación" data-testid="edit-user-room" />
                </div>
                <div>
                  <label className="block text-sm font-medium mb-2">Tipo de habitación</label>
                  <div className="grid grid-cols-2 gap-2">
                    <button type="button" onClick={() => setFormData({...formData, room_type: 'shared'})}
                      className={`p-3 border-2 border-foreground rounded-lg text-sm font-medium ${formData.room_type === 'shared' ? 'bg-accent-yellow shadow-brutal-sm' : 'bg-surface hover:bg-muted'}`}>
                      Compartida
                    </button>
                    <button type="button" onClick={() => setFormData({...formData, room_type: 'exclusive'})}
                      className={`p-3 border-2 border-foreground rounded-lg text-sm font-medium ${formData.room_type === 'exclusive' ? 'bg-accent-mint shadow-brutal-sm' : 'bg-surface hover:bg-muted'}`}>
                      Privada
                    </button>
                  </div>
                </div>
                {formData.house_id && (
                  <HouseRoomsAvailability
                    houseId={formData.house_id}
                    currentRoomNumber={formData.room_number}
                    currentRoomType={formData.room_type}
                    excludeUserId={userData.id}
                    onSuggestRoom={(rn) => setFormData((p) => ({ ...p, room_number: rn }))}
                  />
                )}
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs mb-1 text-muted-foreground">Inicio contrato</label>
                    <input type="date" value={formData.lease_start} onChange={(e) => setFormData({...formData, lease_start: e.target.value})} className="input-brutal w-full px-4 py-3" data-testid="edit-user-lease-start" />
                  </div>
                  <div>
                    <label className="block text-xs mb-1 text-muted-foreground">Fin contrato</label>
                    <input type="date" value={formData.lease_end} onChange={(e) => setFormData({...formData, lease_end: e.target.value})} className="input-brutal w-full px-4 py-3" data-testid="edit-user-lease-end" />
                  </div>
                </div>
                <div>
                  <label className="block text-xs mb-1 text-muted-foreground">Celular (WhatsApp)</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({...formData, phone: e.target.value})}
                    className="input-brutal w-full px-4 py-3"
                    placeholder="+5491156789012"
                    data-testid="edit-user-phone"
                  />
                  <p className="text-[11px] text-muted-foreground mt-1">
                    Número completo con código de país (ej: +54, +1, +34).
                  </p>
                </div>
              </>
            )}

            {}
            {!isStaff && userData.contract_history && userData.contract_history.length > 0 && (
              <div className="mt-6 border-t-2 border-foreground pt-4" data-testid="edit-user-contract-history">
                <label className="block text-xs font-semibold mb-2 text-muted-foreground uppercase tracking-wide">
                  Historial de Contratos Previos
                </label>
                <div className="space-y-2 max-h-40 overflow-y-auto p-3 border border-border rounded-lg bg-muted/20">
                  {userData.contract_history.map((hist, idx) => {
                    const house = houses.find(h => h.id === hist.house_id);
                    return (
                      <div key={idx} className="text-xs p-2 border border-border bg-surface rounded flex flex-col gap-1">
                        <div className="flex justify-between font-semibold">
                          <span>{house ? house.name : 'Casa desconocida'}</span>
                          <span className="text-muted-foreground">Hab. {hist.room_number || '-'} ({hist.room_type === 'exclusive' ? 'Privada' : 'Compartida'})</span>
                        </div>
                        <div className="text-muted-foreground flex justify-between">
                          <span>{hist.lease_start ? formatDateAR(hist.lease_start, 'es-AR') : '-'} al {hist.lease_end ? formatDateAR(hist.lease_end, 'es-AR') : '-'}</span>
                          {hist.archived_at && <span className="text-[10px]">Archivado: {formatDateAR(hist.archived_at.split('T')[0], 'es-AR')}</span>}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}

            <div className="flex gap-3 pt-4">
              <button type="button" onClick={onClose} className="users-modal-cancel flex-1 py-3 hover:bg-muted">Cancelar</button>
              <button type="submit" disabled={loading} className="users-modal-submit btn-brutal flex-1 py-3 disabled:opacity-50" data-testid="submit-edit-user">
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
}


function EditBenefitModal({ benefit, houses, onClose, onSubmit }) {
  const { theme } = useTheme();
  const [formData, setFormData] = useState({
    title: benefit.title || '',
    description: benefit.description || '',
    discount: benefit.discount || '',
    business_name: benefit.business_name || '',
    business_address: benefit.business_address || '',
    target_houses: benefit.target_houses || [],
    valid_until: benefit.valid_until || '',
    image_url: benefit.image_url || '',
    category: benefit.category || 'other',
    business_logo: benefit.business_logo || '',
    branches: benefit.branches || '',
    valid_days: benefit.valid_days || '',
    uses_per_user: benefit.uses_per_user ?? null,
    how_it_works: benefit.how_it_works || '',
    more_info_url: benefit.more_info_url || '',
    payment_methods: benefit.payment_methods || [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showPreview, setShowPreview] = useState(false);
  const { cropImage, cropperNode } = useImageCropper();

  const handleImg = async (e, key, w, h) => {
    const f = e.target.files?.[0];
    if (e.target) e.target.value = '';
    if (!f) return;
    try {
      const aspect = key === 'business_logo' ? 1 : 4 / 3;
      const cropped = await cropImage(f, {
        aspect,
        title: key === 'business_logo' ? 'Encuadrar logo' : 'Encuadrar imagen',
      });
      if (!cropped) return;
      const compressed = await compressImage(cropped, w, h, 0.75);
      setFormData(p => ({ ...p, [key]: compressed }));
    } catch {
      setError('Error al procesar la imagen');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      const payload = { ...formData };
      // No reenviar imágenes si siguen siendo URLs ya resueltas (pisarían la key de GCS)
      if (payload.image_url && payload.image_url.startsWith('http')) delete payload.image_url;
      if (payload.business_logo && payload.business_logo.startsWith('http')) delete payload.business_logo;
      await onSubmit(payload);
    } catch (err) {
      setError(err.response?.data?.detail || 'Error');
      setLoading(false);
    }
  };

  return (
    <>
      <div className="fixed inset-0 bg-black/70 z-50 modal-backdrop" data-testid="edit-benefit-modal">
      {cropperNode}
      <style>{`
        [data-testid="edit-benefit-modal"] .benefit-modal-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 12px;
          box-shadow: none;
        }
        :root[data-theme="light"] [data-testid="edit-benefit-modal"] .benefit-modal-card {
          background: #ffffff;
          box-shadow: 0 18px 50px rgba(24, 32, 43, 0.16);
        }
        [data-testid="edit-benefit-modal"] .benefit-modal-title,
        [data-testid="edit-benefit-modal"] .benefit-modal-section label {
          letter-spacing: 0.04em;
        }
        [data-testid="edit-benefit-modal"] .benefit-modal-title {
          letter-spacing: normal;
        }
        [data-testid="edit-benefit-modal"] .benefit-modal-fields {
          display: flex;
          flex-direction: column;
          gap: 12px;
        }
        [data-testid="edit-benefit-modal"] .benefit-modal-section {
          padding: 14px;
          background: var(--surface-hover);
          border: 1px solid var(--border);
          border-radius: 12px;
          box-shadow: none;
        }
        :root[data-theme="light"] [data-testid="edit-benefit-modal"] .benefit-modal-section {
          background: #ffffff;
        }
        [data-testid="edit-benefit-modal"] .benefit-modal-section > div + div {
          margin-top: 12px;
        }
        [data-testid="edit-benefit-modal"] .benefit-modal-section .input-brutal {
          border: 1px solid var(--border);
          border-radius: 10px;
          background: var(--surface);
          box-shadow: none;
        }
        [data-testid="edit-benefit-modal"] .benefit-modal-section .border-2 {
          border-width: 1px;
          border-color: var(--border);
        }
        [data-testid="edit-benefit-modal"] .benefit-modal-footer {
          border-top: 1px solid var(--border);
          padding-top: 16px;
        }
      `}</style>
      <div
        className="bg-surface border rounded-xl w-full max-w-lg overflow-y-auto modal-card benefit-modal-card"
        style={theme === 'dark'
          ? {
              backgroundColor: 'var(--surface)',
              border: '1px solid var(--border)',
              boxShadow: 'none',
            }
          : { border: '1px solid var(--border)' }}
      >
        <div className="p-6">
          <h2 className="benefit-modal-title font-heading font-bold text-xl mb-6">Editar Beneficio</h2>
          <form onSubmit={handleSubmit} className="benefit-modal-form space-y-4">
            <div className="benefit-modal-fields">
              <BenefitFormFields
                formData={formData}
                setFormData={setFormData}
                houses={houses}
                onImage={(e) => handleImg(e, 'image_url', 800, 600)}
                onLogo={(e) => handleImg(e, 'business_logo', 256, 256)}
                error={error}
              />
            </div>
            <div className="benefit-modal-footer flex gap-3">
              <button
                type="button"
                onClick={onClose}
                className="flex-1 py-3 rounded-lg font-semibold hover:bg-muted"
                style={theme === 'dark'
                  ? { backgroundColor: 'var(--surface)', color: 'var(--fg)', border: '1px solid var(--border)', boxShadow: 'none' }
                  : { backgroundColor: '#ffffff', color: '#111111', border: '1px solid var(--border)', boxShadow: 'none' }}
              >Cancelar</button>
              <button
                type="button"
                onClick={() => setShowPreview(true)}
                className="flex-1 py-3 rounded-lg font-semibold"
                style={{ backgroundColor: 'var(--surface)', color: 'var(--fg)', border: '1px solid var(--border)', boxShadow: 'none' }}
                data-testid="preview-edit-benefit-button"
              >
                Vista previa
              </button>
              <button
                type="submit"
                disabled={loading}
                className="flex-1 py-3 rounded-lg font-semibold disabled:opacity-50"
                style={theme === 'dark'
                  ? { backgroundColor: '#ffffff', color: '#111111', border: '1px solid #ffffff', boxShadow: 'none' }
                  : { backgroundColor: '#111111', color: '#ffffff', border: '1px solid #111111', boxShadow: 'none' }}
                data-testid="submit-edit-benefit"
              >
                {loading ? 'Guardando...' : 'Guardar'}
              </button>
            </div>
          </form>
        </div>
      </div>
      </div>
      {showPreview && <BenefitPreviewModal formData={formData} houses={houses} theme={theme} onClose={() => setShowPreview(false)} />}
    </>
  );
}


function SpacesSection({ houses, emptyStateIcon: EmptyStateIcon = Calendar }) {
  const [spaces, setSpaces] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showNew, setShowNew] = useState(false);
  const [editing, setEditing] = useState(null);  

  const load = async () => {
    try {
      const s = await api.getSpaces();
      setSpaces(s);
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const handleCreate = async (data) => {
    try {
      await api.createSpace(data);
      setShowNew(false);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const handleUpdate = async (id, data) => {
    try {
      await api.updateSpace(id, data);
      setEditing(null);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('¿Desactivar este espacio?')) return;
    try {
      await api.deleteSpace(id);
      load();
    } catch (e) {
      alert(e.response?.data?.detail || 'Error');
    }
  };

  return (
    <div className="space-y-6 animate-enter" data-testid="admin-spaces-section">
      <style>{`
        [data-testid="admin-spaces-section"] .space-new-button,
        [data-testid="admin-spaces-section"] .space-action {
          box-shadow: none !important;
          transform: none;
        }
        [data-testid="admin-spaces-section"] .space-new-button {
          border: none !important;
        }
        :root[data-theme="light"] [data-testid="admin-spaces-section"] .space-new-button,
        :root:not([data-theme="dark"]) [data-testid="admin-spaces-section"] .space-new-button {
          background-color: #111111 !important;
          color: #ffffff !important;
        }
        :root[data-theme="dark"] [data-testid="admin-spaces-section"] .space-new-button {
          background-color: #ffffff !important;
          color: #111111 !important;
        }
        :root[data-theme="dark"] [data-testid="admin-spaces-section"] .space-card {
          border: 1px solid var(--border);
          box-shadow: none;
        }
        :root[data-theme="dark"] [data-testid="admin-spaces-section"] .space-card:hover,
        :root[data-theme="dark"] [data-testid="admin-spaces-section"] .space-card:active {
          border: 1px solid var(--border);
          box-shadow: none;
          transform: none;
        }
        [data-testid="admin-spaces-section"] .space-icon {
          display: flex;
          align-items: center;
          justify-content: center;
        }
        [data-testid="admin-spaces-section"] .space-icon[data-space-type="gym"] {
          background-color: #FEF3C7 !important;
          color: #B45309 !important;
          border: 1px solid rgba(245, 158, 11, 0.35) !important;
        }
        [data-testid="admin-spaces-section"] .space-icon[data-space-type="coworking"],
        [data-testid="admin-spaces-section"] .space-icon[data-space-type="tv"] {
          background-color: #DBEAFE !important;
          color: #2563EB !important;
          border: 1px solid rgba(37, 99, 235, 0.3) !important;
        }
        [data-testid="admin-spaces-section"] .space-icon[data-space-type="bbq"] {
          background-color: #FEE2E2 !important;
          color: #C2410C !important;
          border: 1px solid rgba(225, 29, 72, 0.28) !important;
        }
        [data-testid="admin-spaces-section"] .space-icon[data-space-type="laundry"] {
          background-color: #CFFAFE !important;
          color: #0891B2 !important;
          border: 1px solid rgba(8, 145, 178, 0.3) !important;
        }
        [data-testid="admin-spaces-section"] .space-icon[data-space-type="living"] {
          background-color: #EDE9FE !important;
          color: #7C3AED !important;
          border: 1px solid rgba(124, 58, 237, 0.3) !important;
        }
        [data-testid="admin-spaces-section"] .space-icon[data-space-type="study"] {
          background-color: #E0E7FF !important;
          color: #4F46E5 !important;
          border: 1px solid rgba(79, 70, 229, 0.3) !important;
        }
        [data-testid="admin-spaces-section"] .space-icon[data-space-type="other"] {
          background-color: #DCFCE7 !important;
          color: #15803D !important;
          border: 1px solid rgba(21, 128, 61, 0.28) !important;
        }
        :root[data-theme="dark"] [data-testid="admin-spaces-section"] .space-icon[data-space-type="gym"] {
          background-color: rgba(186, 133, 31, 0.32) !important;
          color: #edc86f !important;
          border: 1px solid rgba(237, 190, 88, 0.26) !important;
        }
        :root[data-theme="dark"] [data-testid="admin-spaces-section"] .space-icon[data-space-type="coworking"] {
          background-color: rgba(54, 116, 185, 0.32) !important;
          color: #9CC7F7 !important;
          border: 1px solid rgba(104, 169, 241, 0.42) !important;
        }
        :root[data-theme="dark"] [data-testid="admin-spaces-section"] .space-icon[data-space-type="bbq"] {
          background-color: rgba(190, 24, 93, 0.24) !important;
          color: #FDA4AF !important;
          border: 1px solid rgba(225, 29, 72, 0.36) !important;
        }
        :root[data-theme="dark"] [data-testid="admin-spaces-section"] .space-icon[data-space-type="laundry"] {
          background-color: rgba(13, 148, 136, 0.28) !important;
          color: #5EEAD4 !important;
          border: 1px solid rgba(45, 212, 191, 0.4) !important;
        }
        :root[data-theme="dark"] [data-testid="admin-spaces-section"] .space-icon[data-space-type="study"] {
          background-color: rgba(109, 40, 217, 0.3) !important;
          color: #C4B5FD !important;
          border: 1px solid rgba(167, 139, 250, 0.38) !important;
        }
        :root[data-theme="dark"] [data-testid="admin-spaces-section"] .space-icon[data-space-type="tv"] {
          background-color: rgba(54, 116, 185, 0.32) !important;
          color: #9CC7F7 !important;
          border: 1px solid rgba(104, 169, 241, 0.42) !important;
        }
        :root[data-theme="dark"] [data-testid="admin-spaces-section"] .space-icon[data-space-type="living"] {
          background-color: rgba(109, 40, 217, 0.3) !important;
          color: #C4B5FD !important;
          border: 1px solid rgba(167, 139, 250, 0.38) !important;
        }
        :root[data-theme="dark"] [data-testid="admin-spaces-section"] .space-icon[data-space-type="other"] {
          background-color: rgba(52, 158, 94, 0.28) !important;
          color: #75D49A !important;
          border: 1px solid rgba(89, 210, 137, 0.28) !important;
        }
        [data-testid="admin-spaces-section"] .space-action {
          border: 1px solid var(--border) !important;
          border-radius: 0.5rem;
        }
        [data-testid="admin-spaces-section"] .space-action-edit {
          background-color: var(--surface) !important;
          color: var(--fg) !important;
        }
        [data-testid="admin-spaces-section"] .space-action-delete {
          border-color: rgba(225, 29, 72, 0.45) !important;
        }
      `}</style>
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-2xl">Espacios comunes</h1>
        <button onClick={() => setShowNew(true)} className="space-new-button btn-brutal px-4 py-2 flex items-center gap-2" data-testid="new-space-button">
          <Plus className="w-4 h-4" />
          Nuevo Espacio
        </button>
      </div>

      {loading ? (
        <LoadingIndicator className="py-8" />
      ) : spaces.length === 0 ? (
        <div className="admin-surface-card p-8 text-center">
          <EmptyStateIcon className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No hay espacios configurados</p>
        </div>
      ) : (
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
          {spaces.map(s => {
            const house = houses.find(h => h.id === s.house_id);
            const SpaceIcon = spaceTypeVisualMap[s.space_type]?.icon || House;
            return (
              <div key={s.id} className="space-card admin-surface-card p-4 flex flex-col" data-testid={`admin-space-${s.id}`}>
                <div className="flex-1">
                <div className="flex items-start justify-between mb-2">
                  <div
                    className="space-icon w-12 h-12 rounded-lg"
                    data-space-type={s.space_type}
                  >
                    <SpaceIcon className="w-6 h-6" />
                  </div>
                  <span className="text-xs px-2 py-1 bg-muted rounded-full">{s.type_label}</span>
                </div>
                <h3 className="font-heading font-semibold">{s.name}</h3>
                <p className="text-xs text-muted-foreground mb-2">{house?.name || '-'}</p>
                <p className="text-sm mb-2">{s.description}</p>
                <p className="text-xs text-muted-foreground">
                  {String(s.open_hour).padStart(2, '0')}:00 – {String(s.close_hour).padStart(2, '0')}:00
                  {s.capacity ? ` · Cap. ${s.capacity}` : ''} · Slots {s.slot_minutes}min
                </p>
                </div>
                <div className="mt-3 flex gap-2">
                  <button
                    onClick={() => setEditing(s)}
                    className="space-action space-action-edit flex-1 text-xs px-3 py-1.5 hover:bg-muted flex items-center justify-center gap-1"
                    data-testid={`edit-space-${s.id}`}
                  >
                    <Edit2 className="w-3 h-3" /> Editar
                  </button>
                  <button
                    onClick={() => handleDelete(s.id)}
                    className="space-action space-action-delete flex-1 text-xs px-3 py-1.5 bg-destructive text-white flex items-center justify-center gap-1"
                    data-testid={`delete-space-${s.id}`}
                  >
                    <Trash2 className="w-3 h-3" /> Desactivar
                  </button>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {showNew && (
        <SpaceModal houses={houses} onClose={() => setShowNew(false)} onSubmit={handleCreate} />
      )}
      {editing && (
        <SpaceModal
          houses={houses}
          initial={editing}
          onClose={() => setEditing(null)}
          onSubmit={(data) => handleUpdate(editing.id, data)}
        />
      )}
    </div>
  );
}

function SpaceModal({ houses, initial, onClose, onSubmit }) {
  
  
  const isEdit = !!initial;
  const [formData, setFormData] = useState({
    house_id: initial?.house_id || houses[0]?.id || '',
    name: initial?.name || '',
    space_type: initial?.space_type || 'gym',
    description: initial?.description || '',
    capacity: initial?.capacity ?? 4,
    open_hour: initial?.open_hour ?? 8,
    close_hour: initial?.close_hour ?? 22,
    slot_minutes: initial?.slot_minutes ?? 60,
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [typeMenuOpen, setTypeMenuOpen] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await onSubmit({
        ...formData,
        capacity: parseInt(formData.capacity),
        open_hour: parseInt(formData.open_hour),
        close_hour: parseInt(formData.close_hour),
        slot_minutes: parseInt(formData.slot_minutes),
      });
    } catch (err) {
      setError(err.response?.data?.detail || 'Error');
      setLoading(false);
    }
  };

  const SPACE_TYPES = [
    { value: 'gym', label: 'Gimnasio' },
    { value: 'coworking', label: 'Coworking' },
    { value: 'bbq', label: 'Parrilla' },
    { value: 'laundry', label: 'Lavadero' },
    { value: 'living', label: 'Living' },
    { value: 'study', label: 'Sala de estudio' },
    { value: 'tv', label: 'TV' },
    { value: 'other', label: 'Otro' },
  ];
  const selectedSpaceType = SPACE_TYPES.find((type) => type.value === formData.space_type) || SPACE_TYPES[0];
  const SelectedSpaceIcon = spaceTypeVisualMap[formData.space_type]?.icon || House;

  const handleTypeMenuKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      setTypeMenuOpen(true);
    }
    if (event.key === 'Escape') setTypeMenuOpen(false);
  };

  return createPortal(
    <div className="fixed inset-0 bg-black/70 z-[100] modal-backdrop" data-testid={isEdit ? 'edit-space-modal' : 'new-space-modal'}>
      <style>{`
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-modal-card {
          background: var(--surface);
          border: 1px solid var(--border);
          border-radius: 16px;
          box-shadow: 0 18px 48px rgba(15, 23, 42, 0.16);
        }
        :root[data-theme="dark"] [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-modal-card {
          background: var(--surface);
          border-color: var(--border);
          box-shadow: none;
        }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-modal-input {
          border: 1px solid var(--border) !important;
          border-radius: 10px;
          background: var(--surface);
          box-shadow: none !important;
          outline: none;
        }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-modal-input:focus {
          border-color: var(--primary) !important;
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.14) !important;
        }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-trigger {
          min-height: 48px;
          border: 1px solid var(--border);
          border-radius: 10px;
          background: var(--surface);
          color: var(--fg);
        }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-trigger:focus-visible {
          outline: none;
          border-color: var(--primary);
          box-shadow: 0 0 0 3px rgba(59, 130, 246, 0.14);
        }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-menu {
          border: 1px solid var(--border);
          border-radius: 12px;
          background: var(--surface);
          box-shadow: 0 14px 32px rgba(15, 23, 42, 0.16);
        }
        :root[data-theme="dark"] [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-menu {
          box-shadow: 0 14px 32px rgba(0, 0, 0, 0.34);
        }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-option:hover,
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-option[aria-selected="true"] {
          background: var(--muted);
        }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="gym"] { color: #B45309; }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="coworking"],
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="tv"] { color: #2563EB; }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="bbq"] { color: #C2410C; }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="laundry"] { color: #0891B2; }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="living"] { color: #7C3AED; }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="study"] { color: #4F46E5; }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="other"] { color: #15803D; }
        :root[data-theme="dark"] [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="gym"] { color: #edc86f; }
        :root[data-theme="dark"] [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="coworking"],
        :root[data-theme="dark"] [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="tv"] { color: #9CC7F7; }
        :root[data-theme="dark"] [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="bbq"] { color: #FDA4AF; }
        :root[data-theme="dark"] [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="laundry"] { color: #5EEAD4; }
        :root[data-theme="dark"] [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="living"],
        :root[data-theme="dark"] [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="study"] { color: #C4B5FD; }
        :root[data-theme="dark"] [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-type-symbol[data-space-type="other"] { color: #75D49A; }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-modal-cancel,
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-modal-submit {
          border-radius: 10px;
          box-shadow: none !important;
          font-weight: 600;
        }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-modal-cancel {
          border: 1px solid var(--border);
          background: var(--surface);
          color: var(--fg);
        }
        [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-modal-submit {
          border: 1px solid #111111 !important;
          background: #111111 !important;
          color: #ffffff !important;
        }
        :root[data-theme="dark"] [data-testid="${isEdit ? 'edit-space-modal' : 'new-space-modal'}"] .space-modal-submit {
          border-color: #ffffff !important;
          background: #ffffff !important;
          color: #111111 !important;
        }
      `}</style>
      <div className="space-modal-card w-full max-w-lg overflow-y-auto modal-card">
        <div className="p-6">
          <h2 className="font-heading font-bold text-xl mb-6">{isEdit ? 'Editar Espacio' : 'Nuevo Espacio'}</h2>
          {error && <div className="mb-4 p-3 bg-destructive/20 border border-destructive rounded-lg text-sm">{error}</div>}
          <form onSubmit={handleSubmit} className="space-y-3">
            <select value={formData.house_id} onChange={(e) => setFormData({...formData, house_id: e.target.value})} required className="space-modal-input input-brutal w-full px-4 py-3" data-testid="space-house-select">
              <option value="">Elegir casa...</option>
              {houses.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
            </select>
            <input type="text" value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} placeholder="Nombre (ej: Gimnasio 1er piso)" required className="space-modal-input input-brutal w-full px-4 py-3" data-testid="space-name-input" />
            <div className="relative">
              <button
                type="button"
                role="combobox"
                aria-expanded={typeMenuOpen}
                aria-controls="space-type-options"
                onClick={() => setTypeMenuOpen((open) => !open)}
                onKeyDown={handleTypeMenuKeyDown}
                className="space-type-trigger flex w-full items-center gap-3 px-4 py-3 text-left"
                data-space-type={formData.space_type}
                data-testid="space-type-select"
              >
                <SelectedSpaceIcon className="space-type-symbol h-4 w-4 flex-shrink-0" data-space-type={formData.space_type} />
                <span className="flex-1">{selectedSpaceType.label}</span>
                <ChevronDown className={`h-4 w-4 transition-transform ${typeMenuOpen ? 'rotate-180' : ''}`} />
              </button>
              {typeMenuOpen && (
                <div id="space-type-options" role="listbox" className="space-type-menu absolute left-0 right-0 top-[calc(100%+6px)] z-10 max-h-56 overflow-y-auto p-1">
                  {SPACE_TYPES.map((type) => {
                    const TypeIcon = spaceTypeVisualMap[type.value]?.icon || House;
                    return (
                      <button
                        key={type.value}
                        type="button"
                        role="option"
                        aria-selected={formData.space_type === type.value}
                        data-space-type={type.value}
                        onClick={() => {
                          setFormData({ ...formData, space_type: type.value });
                          setTypeMenuOpen(false);
                        }}
                        className="space-type-option flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm"
                      >
                        <TypeIcon className="space-type-symbol h-4 w-4 flex-shrink-0" data-space-type={type.value} />
                        <span>{type.label}</span>
                      </button>
                    );
                  })}
                </div>
              )}
            </div>
            <textarea value={formData.description} onChange={(e) => setFormData({...formData, description: e.target.value})} rows={2} placeholder="Descripción" className="space-modal-input input-brutal w-full px-4 py-3 resize-none" />
            <div className="grid grid-cols-4 gap-2">
              <div>
                <label className="text-xs text-muted-foreground">Desde</label>
                <input type="number" min="0" max="23" value={formData.open_hour} onChange={(e) => setFormData({...formData, open_hour: e.target.value})} className="space-modal-input input-brutal w-full px-2 py-2" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Hasta</label>
                <input type="number" min="1" max="24" value={formData.close_hour} onChange={(e) => setFormData({...formData, close_hour: e.target.value})} className="space-modal-input input-brutal w-full px-2 py-2" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Cap.</label>
                <input type="number" min="1" value={formData.capacity} onChange={(e) => setFormData({...formData, capacity: e.target.value})} className="space-modal-input input-brutal w-full px-2 py-2" />
              </div>
              <div>
                <label className="text-xs text-muted-foreground">Slot (min)</label>
                <input type="number" min="15" step="15" value={formData.slot_minutes} onChange={(e) => setFormData({...formData, slot_minutes: e.target.value})} className="space-modal-input input-brutal w-full px-2 py-2" />
              </div>
            </div>
            <div className="flex gap-2 pt-3">
              <button type="button" onClick={onClose} className="space-modal-cancel flex-1 py-3 hover:bg-muted">Cancelar</button>
              <button type="submit" disabled={loading} className="space-modal-submit flex-1 py-3 disabled:opacity-50" data-testid="submit-space">
                {loading ? '...' : (isEdit ? 'Guardar' : 'Crear')}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>,
    document.body
  );
}



function OccupancySection({ houses }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [expandedId, setExpandedId] = useState(null);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const d = await api.getOccupancy();
      setData(d);
    } catch (e) { console.error(e); }
    if (!silent) setLoading(false);
  };

  useEffect(() => { load(); }, []);

  
  useAutoRefresh(() => load({ silent: true }), 120000);

  if (loading) return <LoadingIndicator className="py-8" />;
  if (!data) return <div className="admin-occupancy-card p-8 text-center text-muted-foreground">Sin datos</div>;

  const totals = data.reduce((a, h) => ({
    total: a.total + h.total_rooms,
    occupied: a.occupied + h.occupied_now,
    pending_in: a.pending_in + h.pending_inbound_transfers.length,
    pending_out: a.pending_out + h.pending_outbound_transfers.length,
  }), { total: 0, occupied: 0, pending_in: 0, pending_out: 0 });

  return (
    <div className="space-y-6 animate-enter" data-testid="admin-occupancy-section">
      <style>{`
        :root[data-theme="dark"] [data-testid="admin-occupancy-section"] [data-testid="occupancy-stats-grid"] > .admin-occupancy-card,
        :root[data-theme="dark"] [data-testid="admin-occupancy-section"] [data-testid^="occupancy-house-"] {
          border: 1px solid var(--border);
          box-shadow: none;
        }
        :root[data-theme="dark"] [data-testid="admin-occupancy-section"] [data-testid="occupancy-stats-grid"] > .admin-occupancy-card:hover,
        :root[data-theme="dark"] [data-testid="admin-occupancy-section"] [data-testid="occupancy-stats-grid"] > .admin-occupancy-card:active,
        :root[data-theme="dark"] [data-testid="admin-occupancy-section"] [data-testid^="occupancy-house-"]:hover,
        :root[data-theme="dark"] [data-testid="admin-occupancy-section"] [data-testid^="occupancy-house-"]:active {
          box-shadow: none;
          transform: none;
        }
        :root[data-theme="dark"] [data-testid="occupancy-stats-grid"] > .admin-occupancy-card:nth-child(1) > div:first-child {
          background-color: rgba(186, 133, 31, 0.32) !important;
          color: #edc86f !important;
          border-color: rgba(237, 190, 88, 0.26) !important;
        }
        :root[data-theme="dark"] [data-testid="occupancy-stats-grid"] > .admin-occupancy-card:nth-child(2) > div:first-child {
          background-color: rgba(52, 158, 94, 0.32) !important;
          color: #75d49a !important;
          border-color: rgba(89, 210, 137, 0.25) !important;
        }
        :root[data-theme="dark"] [data-testid="occupancy-stats-grid"] > .admin-occupancy-card:nth-child(3) > div:first-child {
          background-color: rgba(190, 24, 93, 0.24) !important;
          color: #FDA4AF !important;
          border-color: rgba(225, 29, 72, 0.36) !important;
        }
        :root[data-theme="dark"] [data-testid="occupancy-stats-grid"] > .admin-occupancy-card:nth-child(4) > div:first-child {
          background-color: rgba(109, 40, 217, 0.36) !important;
          color: #C4B5FD !important;
          border-color: rgba(167, 139, 250, 0.42) !important;
        }
        :root[data-theme="dark"] [data-testid="occupancy-stats-grid"] > .admin-occupancy-card > div:first-child svg {
          color: currentColor !important;
          stroke: currentColor !important;
        }
        [data-testid="admin-occupancy-section"] .occupancy-refresh-button {
          border: none !important;
          box-shadow: none !important;
        }
        :root[data-theme="light"] [data-testid="admin-occupancy-section"] .occupancy-refresh-button,
        :root:not([data-theme="dark"]) [data-testid="admin-occupancy-section"] .occupancy-refresh-button {
          background-color: #111111 !important;
          color: #ffffff !important;
        }
        :root[data-theme="dark"] [data-testid="admin-occupancy-section"] .occupancy-refresh-button {
          background-color: #ffffff !important;
          color: #111111 !important;
        }
        [data-testid="admin-occupancy-section"] .occupancy-guest-room-type[data-room-type="exclusive"] {
          background-color: rgba(225, 29, 72, 0.12) !important;
          border: 1px solid rgba(225, 29, 72, 0.28);
          color: #be123c !important;
        }
        [data-testid="admin-occupancy-section"] .occupancy-guest-room-type[data-room-type="shared"] {
          background-color: rgba(52, 158, 94, 0.12) !important;
          border: 1px solid rgba(52, 158, 94, 0.28);
          color: #15803d !important;
        }
        :root[data-theme="dark"] [data-testid="admin-occupancy-section"] .occupancy-guest-room-type[data-room-type="exclusive"] {
          background-color: rgba(190, 24, 93, 0.24) !important;
          border-color: rgba(225, 29, 72, 0.36);
          color: #FDA4AF !important;
        }
        :root[data-theme="dark"] [data-testid="admin-occupancy-section"] .occupancy-guest-room-type[data-room-type="shared"] {
          background-color: rgba(52, 158, 94, 0.28) !important;
          border-color: rgba(89, 210, 137, 0.28);
          color: #75D49A !important;
        }
        [data-testid="admin-occupancy-section"] .occupancy-bar-track {
          background-color: rgba(148, 163, 184, 0.18) !important;
          border: 1px solid rgba(148, 163, 184, 0.3);
          overflow: hidden;
        }
        [data-testid="admin-occupancy-section"] .occupancy-bar-fill {
          background-color: #2fbd70 !important;
        }
      `}</style>
      <div className="flex items-center justify-between">
        <h1 className="font-heading font-bold text-2xl">Ocupación</h1>
        <button onClick={load} className="occupancy-refresh-button text-xs px-3 py-1.5 border-2 border-foreground rounded-lg hover:bg-muted">
          Actualizar
        </button>
      </div>

      {}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="occupancy-stats-grid">
        <StatCard label="Total habitaciones" value={totals.total} icon={Building2} color="bg-accent-yellow" surfaceClassName="admin-occupancy-card" />
        <StatCard label="Ocupadas" value={totals.occupied} icon={UsersRound} color="bg-accent-mint" surfaceClassName="admin-occupancy-card" />
        <StatCard label="Disponibles" value={totals.total - totals.occupied} icon={Home} color="bg-accent-peach" surfaceClassName="admin-occupancy-card" />
        <StatCard label="Cambios pendientes" value={totals.pending_in + totals.pending_out} icon={ArrowLeftRight} color="bg-accent-lavender" surfaceClassName="admin-occupancy-card" />
      </div>

      {}
      <div className="space-y-3">
        {data.map(h => {
          const expanded = expandedId === h.house_id;
          const netBalance = h.approved_incoming_count - h.approved_outgoing_count;
          return (
            <div key={h.house_id} className="admin-occupancy-card p-4" data-testid={`occupancy-house-${h.house_id}`}>
              <button
                onClick={() => setExpandedId(expanded ? null : h.house_id)}
                className="w-full flex items-center justify-between text-left"
              >
                <div>
                  <h3 className="font-heading font-semibold text-lg">{h.house_name}</h3>
                  <p className="text-xs text-muted-foreground">{h.city}</p>
                </div>
                <div className="flex items-center gap-6">
                  <div className="text-right">
                    <p className="text-2xl font-heading font-bold">{Math.min(100, Math.round(h.occupancy_pct))}%</p>
                    <p className="text-xs text-muted-foreground">ocupado</p>
                  </div>
                  <ChevronRight className={`w-5 h-5 transition-transform ${expanded ? 'rotate-90' : ''}`} />
                </div>
              </button>

              {}
              <div className="occupancy-bar-track w-full rounded-full h-2 mt-3">
                <div
                  className="occupancy-bar-fill h-full rounded-full transition-all"
                  style={{ width: `${Math.min(100, h.occupancy_pct || 0)}%` }}
                />
              </div>

              <div className="flex flex-wrap gap-2 mt-3 text-xs">
                <span className="px-2 py-1 bg-accent-mint/50 rounded-full">
                  Disponibles ahora: <strong>{h.available_now}</strong>
                </span>
                {h.approved_incoming_count > 0 && (
                  <span className="px-2 py-1 bg-accent-yellow rounded-full">
                    +{h.approved_incoming_count} entrando
                  </span>
                )}
                {h.approved_outgoing_count > 0 && (
                  <span className="px-2 py-1 bg-accent-peach rounded-full">
                    -{h.approved_outgoing_count} saliendo
                  </span>
                )}
                {netBalance !== 0 && (
                  <span className="px-2 py-1 bg-muted rounded-full">
                    Proyección: <strong>{h.projected_occupied}/{h.total_rooms}</strong> ({h.projected_available} libres)
                  </span>
                )}
                {h.pending_inbound_transfers.length > 0 && (
                  <span className="px-2 py-1 bg-accent-lavender rounded-full">
                    {h.pending_inbound_transfers.length} pendiente(s) por aprobar
                  </span>
                )}
              </div>

              {expanded && (
                <div className="mt-4 pt-4 border-t border-border space-y-4">
                  {}
                  <div>
                    <h4 className="font-heading font-semibold text-sm mb-2">Huéspedes actuales ({h.rooms.length})</h4>
                    {h.rooms.length === 0 ? (
                      <p className="text-xs text-muted-foreground">Casa vacía</p>
                    ) : (
                      <div className="overflow-x-auto">
                        <table className="w-full text-xs border-separate border-spacing-0">
                          <thead>
                            <tr className="text-left text-muted-foreground border-b border-border/60">
                              <th className="pb-2 pr-2 font-medium">Hab.</th>
                              <th className="pb-2 pr-2 font-medium">Huésped</th>
                              <th className="pb-2 pr-2 font-medium">Tipo</th>
                              <th className="pb-2 pr-2 font-medium">Contrato</th>
                            </tr>
                          </thead>
                          <tbody>
                            {h.rooms.map(r => (
                              <tr key={r.user_id} className="border-b border-border/50 transition-colors hover:bg-muted/20">
                                <td className="py-2.5 pr-2 font-semibold">{r.room_number || '-'}</td>
                                <td className="py-2.5 pr-2">{r.user_name}</td>
                                <td className="py-2.5 pr-2">
                                  <span data-room-type={r.room_type === 'exclusive' ? 'exclusive' : 'shared'} className="occupancy-guest-room-type inline-block rounded-full px-1.5 py-0.5 text-[10px]">
                                    {r.room_type === 'exclusive' ? 'Privada' : 'Compartida'}
                                  </span>
                                </td>
                                <td className="py-2.5 pr-2 text-muted-foreground">
                                  {r.lease_start?.slice(0,10) || '—'} → {r.lease_end?.slice(0,10) || '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                    )}
                  </div>

                  {}
                  {h.pending_inbound_transfers.length > 0 && (
                    <div>
                      <h4 className="font-heading font-semibold text-sm mb-2 text-accent-mint-foreground">
                        Quieren entrar ({h.pending_inbound_transfers.length})
                      </h4>
                      <ul className="space-y-1 text-xs">
                        {h.pending_inbound_transfers.map(t => (
                          <li key={t.id} className="p-2 bg-accent-mint/20 rounded border border-accent-mint">
                            <strong>{t.user_name}</strong> · del {t.start_date} al {t.end_date}
                            {t.reason && <span className="text-muted-foreground"> · &ldquo;{t.reason}&rdquo;</span>}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {}
                  {h.pending_outbound_transfers.length > 0 && (
                    <div>
                      <h4 className="font-heading font-semibold text-sm mb-2">
                        Quieren irse ({h.pending_outbound_transfers.length})
                      </h4>
                      <ul className="space-y-1 text-xs">
                        {h.pending_outbound_transfers.map(t => (
                          <li key={t.id} className="p-2 bg-accent-peach/20 rounded border border-accent-peach">
                            <strong>{t.user_name}</strong> → {t.target_house_name} · del {t.start_date} al {t.end_date}
                          </li>
                        ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}


function AgendaSection({ houses }) {
  const [spaces, setSpaces] = useState([]);
  const [reservations, setReservations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [houseFilter, setHouseFilter] = useState('all');
  const [selectedDate, setSelectedDate] = useState(new Date().toISOString().split('T')[0]);

  const load = async ({ silent = false } = {}) => {
    if (!silent) setLoading(true);
    try {
      const [sp, ...daysPromises] = await Promise.all([
        api.getSpaces(),
        ...getDateRange(selectedDate).map(d =>
          api.getReservations({ date: d }).catch(() => [])
        ),
      ]);
      setSpaces(sp);
      setReservations(daysPromises.flat());
    } catch (e) { console.error(e); }
    if (!silent) setLoading(false);
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [selectedDate]);

  
  useAutoRefresh(() => load({ silent: true }), 120000);

  
  function getDateRange(from) {
    const start = new Date(from);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start);
      d.setDate(start.getDate() + i);
      return d.toISOString().split('T')[0];
    });
  }

  const filteredSpaces = houseFilter === 'all' ? spaces : spaces.filter(s => s.house_id === houseFilter);
  const days = getDateRange(selectedDate);
  const dayNames = ['Dom', 'Lun', 'Mar', 'Mié', 'Jue', 'Vie', 'Sáb'];

  const moveWeek = (direction) => {
    const d = new Date(selectedDate);
    d.setDate(d.getDate() + direction * 7);
    setSelectedDate(d.toISOString().split('T')[0]);
  };

  const reservationsForSpaceDay = (spaceId, date) =>
    reservations.filter(r => r.space_id === spaceId && r.date === date);

  return (
    <div className="space-y-6 animate-enter" data-testid="admin-agenda-section">
      <style>{`
        [data-testid="admin-agenda-section"] .agenda-control {
          min-height: 40px;
          border: 1px solid var(--border);
          border-radius: 10px;
          background: var(--surface);
          color: var(--fg);
          box-shadow: none;
        }
        [data-testid="admin-agenda-section"] .agenda-control:hover {
          background: var(--muted);
        }
        [data-testid="admin-agenda-section"] .agenda-date-control {
          color-scheme: light;
        }
        :root[data-theme="dark"] [data-testid="admin-agenda-section"] .agenda-date-control {
          color-scheme: dark;
        }
        [data-testid="admin-agenda-section"] .agenda-grid-shell {
          border: 1px solid var(--border);
          border-radius: 14px;
          box-shadow: none;
        }
        :root[data-theme="dark"] [data-testid="admin-agenda-section"] .agenda-grid-shell {
          border-color: var(--border);
          box-shadow: none;
        }
        [data-testid="admin-agenda-section"] .agenda-header-row {
          border-bottom: 1px solid var(--border);
        }
        [data-testid="admin-agenda-section"] .agenda-header-cell {
          background: var(--surface);
          border-right: 1px solid var(--border);
        }
        [data-testid="admin-agenda-section"] .agenda-header-cell:last-child {
          border-right: 0;
        }
        [data-testid="admin-agenda-section"] .agenda-header-cell.agenda-today {
          background: #111111;
          color: #FFFFFF;
        }
        [data-testid="admin-agenda-section"] .agenda-header-cell.agenda-today .agenda-header-date {
          color: rgba(255, 255, 255, 0.82);
        }
        :root[data-theme="dark"] [data-testid="admin-agenda-section"] .agenda-header-cell.agenda-today {
          background: #FFFFFF;
          color: #111111;
        }
        :root[data-theme="dark"] [data-testid="admin-agenda-section"] .agenda-header-cell.agenda-today .agenda-header-date {
          color: rgba(17, 17, 17, 0.72);
        }
        [data-testid="admin-agenda-section"] .agenda-header-date {
          margin-top: 3px;
          font-size: 10px;
          line-height: 1.2;
          color: var(--muted-foreground);
        }
        [data-testid="admin-agenda-section"] .agenda-row {
          border-bottom: 1px solid var(--border);
        }
        [data-testid="admin-agenda-section"] .agenda-row:last-child {
          border-bottom: 0;
        }
        [data-testid="admin-agenda-section"] .agenda-space-cell {
          background: var(--surface);
          border-right: 1px solid var(--border);
        }
        [data-testid="admin-agenda-section"] .agenda-space-icon {
          display: flex;
          align-items: center;
          justify-content: center;
          width: 32px;
          height: 32px;
          flex-shrink: 0;
          border-radius: 9px;
          border: 1px solid transparent;
        }
        [data-testid="admin-agenda-section"] .agenda-space-icon[data-space-semantic="amber"] {
          background: #FEF3C7;
          color: #B45309;
          border-color: rgba(245, 158, 11, 0.35);
        }
        [data-testid="admin-agenda-section"] .agenda-space-icon[data-space-semantic="blue"] {
          background: #DBEAFE;
          color: #2563EB;
          border-color: rgba(37, 99, 235, 0.3);
        }
        [data-testid="admin-agenda-section"] .agenda-space-icon[data-space-semantic="rose"] {
          background: #FEE2E2;
          color: #C2410C;
          border-color: rgba(225, 29, 72, 0.28);
        }
        [data-testid="admin-agenda-section"] .agenda-space-icon[data-space-semantic="cyan"] {
          background: #CFFAFE;
          color: #0891B2;
          border-color: rgba(8, 145, 178, 0.3);
        }
        [data-testid="admin-agenda-section"] .agenda-space-icon[data-space-semantic="violet"],
        [data-testid="admin-agenda-section"] .agenda-space-icon[data-space-semantic="indigo"] {
          background: #EDE9FE;
          color: #5B21B6;
          border-color: rgba(124, 58, 237, 0.3);
        }
        [data-testid="admin-agenda-section"] .agenda-space-icon[data-space-semantic="green"] {
          background: #DCFCE7;
          color: #15803D;
          border-color: rgba(21, 128, 61, 0.28);
        }
        :root[data-theme="dark"] [data-testid="admin-agenda-section"] .agenda-space-icon[data-space-semantic="amber"] {
          background: rgba(186, 133, 31, 0.32);
          color: #edc86f;
          border-color: rgba(237, 190, 88, 0.26);
        }
        :root[data-theme="dark"] [data-testid="admin-agenda-section"] .agenda-space-icon[data-space-semantic="blue"] {
          background: rgba(54, 116, 185, 0.32);
          color: #9CC7F7;
          border-color: rgba(104, 169, 241, 0.42);
        }
        :root[data-theme="dark"] [data-testid="admin-agenda-section"] .agenda-space-icon[data-space-semantic="rose"] {
          background: rgba(190, 24, 93, 0.24);
          color: #FDA4AF;
          border-color: rgba(225, 29, 72, 0.36);
        }
        :root[data-theme="dark"] [data-testid="admin-agenda-section"] .agenda-space-icon[data-space-semantic="cyan"] {
          background: rgba(13, 148, 136, 0.28);
          color: #5EEAD4;
          border-color: rgba(45, 212, 191, 0.4);
        }
        :root[data-theme="dark"] [data-testid="admin-agenda-section"] .agenda-space-icon[data-space-semantic="violet"],
        :root[data-theme="dark"] [data-testid="admin-agenda-section"] .agenda-space-icon[data-space-semantic="indigo"] {
          background: rgba(109, 40, 217, 0.3);
          color: #C4B5FD;
          border-color: rgba(167, 139, 250, 0.38);
        }
        :root[data-theme="dark"] [data-testid="admin-agenda-section"] .agenda-space-icon[data-space-semantic="green"] {
          background: rgba(52, 158, 94, 0.28);
          color: #75D49A;
          border-color: rgba(89, 210, 137, 0.28);
        }
        [data-testid="admin-agenda-section"] .agenda-empty-cell {
          height: 64px;
          border: 1px solid var(--border);
          border-radius: 8px;
          background: color-mix(in srgb, var(--muted) 28%, transparent);
        }
        [data-testid="admin-agenda-section"] .agenda-reservation {
          border: 1px solid rgba(21, 128, 61, 0.22);
          border-radius: 8px;
          background: var(--pill-green-bg);
        }
        :root[data-theme="dark"] [data-testid="admin-agenda-section"] .agenda-reservation {
          border-color: rgba(89, 210, 137, 0.25);
          background: rgba(52, 158, 94, 0.25);
        }
      `}</style>
      <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-3">
        <h1 className="font-heading font-bold text-2xl">Agenda de reservas</h1>
        <div className="flex items-center gap-2 flex-wrap">
          <select
            value={houseFilter}
            onChange={(e) => setHouseFilter(e.target.value)}
            className="input-brutal px-3 py-2 text-sm"
            data-testid="agenda-house-filter"
          >
            <option value="all">Todas las casas</option>
            {houses.map(h => <option key={h.id} value={h.id}>{h.name}</option>)}
          </select>
          <button onClick={() => moveWeek(-1)} className="agenda-control px-3 py-2 text-[0px]" data-testid="agenda-prev-week" aria-label="Semana anterior">
            <ChevronLeft className="w-4 h-4" aria-hidden="true" />
            ‹
          </button>
          <input
            type="date"
            value={selectedDate}
            onChange={(e) => setSelectedDate(e.target.value)}
            className="agenda-control agenda-date-control px-3 py-2 text-sm"
            data-testid="agenda-date-input"
          />
          <button onClick={() => moveWeek(1)} className="agenda-control px-3 py-2 text-[0px]" data-testid="agenda-next-week" aria-label="Semana siguiente">
            <ChevronRight className="w-4 h-4" aria-hidden="true" />
            ›
          </button>
        </div>
      </div>

      {loading ? (
        <LoadingIndicator className="py-8" />
      ) : filteredSpaces.length === 0 ? (
        <div className="admin-agenda-grid-shell p-8 text-center">
          <Calendar className="w-12 h-12 mx-auto mb-4 text-muted-foreground" />
          <p className="text-muted-foreground">No hay espacios configurados para esta casa</p>
        </div>
      ) : (
      <div className="admin-agenda-grid-shell agenda-grid-shell p-3 overflow-x-auto">
          <table className="min-w-full text-xs">
            <thead>
              <tr className="agenda-header-row">
                <th className="agenda-header-cell text-left p-3 sticky left-0" style={{ minWidth: 180 }}>Espacio</th>
                {days.map(d => {
                  const dateObj = new Date(d + 'T00:00');
                  const isToday = d === new Date().toISOString().split('T')[0];
                  return (
                    <th key={d} className={`agenda-header-cell p-3 text-center ${isToday ? 'agenda-today' : ''}`} style={{ minWidth: 110 }}>
                      <div className="font-semibold">{dayNames[dateObj.getDay()]}</div>
                      <div className="agenda-header-date">{dateObj.getDate()}/{dateObj.getMonth() + 1}</div>
                    </th>
                  );
                })}
              </tr>
            </thead>
            <tbody>
              {filteredSpaces.map(space => {
                const house = houses.find(h => h.id === space.house_id);
                const spaceVisual = spaceTypeVisualMap[space.space_type] || spaceTypeVisualMap.other;
                const SpaceIcon = spaceVisual.icon;
                return (
                  <tr key={space.id} className="agenda-row">
                    <td className="agenda-space-cell p-3 sticky left-0">
                      <div className="flex items-center gap-2.5">
                        <span className="agenda-space-icon" data-space-semantic={spaceVisual.semantic} data-space-type={space.space_type}>
                          <SpaceIcon className="w-4 h-4" aria-hidden="true" />
                        </span>
                        <div>
                          <div className="font-semibold">{space.name}</div>
                          <div className="text-[10px] text-muted-foreground">{house?.name}</div>
                        </div>
                      </div>
                    </td>
                    {days.map(d => {
                      const dayRes = reservationsForSpaceDay(space.id, d);
                      const pct = space.capacity ? (dayRes.length / space.capacity) * 100 : 0;
                      return (
                        <td key={d} className="p-2 align-top" data-testid={`agenda-cell-${space.id}-${d}`}>
                          {dayRes.length === 0 ? (
                            <div className="agenda-empty-cell" />
                          ) : (
                            <div className="space-y-1">
                              {dayRes.slice(0, 3).map(r => (
                                <div
                                  key={r.id}
                                  className="agenda-reservation p-2 text-[10px] leading-tight"
                                  title={`${r.user_name} (${r.start_time}-${r.end_time})${r.notes ? ' - ' + r.notes : ''}`}
                                >
                                  <div className="font-semibold truncate">{r.user_name}</div>
                                  <div className="text-muted-foreground">{r.start_time}-{r.end_time}</div>
                                </div>
                              ))}
                              {dayRes.length > 3 && (
                                <div className="text-[10px] text-center text-muted-foreground">+{dayRes.length - 3} más</div>
                              )}
                              {space.capacity && (
                                <div className="text-[9px] text-center text-muted-foreground">
                                  {dayRes.length}/{space.capacity} ({Math.round(pct)}%)
                                </div>
                              )}
                            </div>
                          )}
                        </td>
                      );
                    })}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
